import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Alert,
  AlertTitle,
  Autocomplete,
  Checkbox,
  InputLabel,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Menu,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  KeyboardArrowUp,
  KeyboardArrowDown,
  CheckCircle,
  Cancel,
  Edit,
  Save,
  Close,
  Add,
  Delete,
  CloudUpload,
  AutoFixHigh,
  AutoAwesome,
  Article,
  Quiz as QuizIcon,
  Settings as SettingsIcon,
  PhotoLibrary as PhotoLibraryIcon,
  ExpandMore,
  ExpandLess,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  WarningAmber as WarningAmberIcon,
  DragIndicator,
  MenuBook as MenuBookIcon,
  UploadFile as UploadFileIcon,
  LibraryBooks as LibraryBooksIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  TravelExplore as TravelExploreIcon,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { formatDistanceToNow } from "date-fns";
import api from "../../api";
import { QUESTION_TYPES } from "./components/QuestionTypeSelector";
import TopicTreeSelect from "./components/TopicTreeSelect";
import GenerateSimilarPaperDialog from "./components/GenerateSimilarPaperDialog";
import SimilarityReportDialog from "./components/SimilarityReportDialog";
import { BlankAnswerEditor } from "./components/FillInBlankEditor";
import TableFillEditor, { DEFAULT_GRID } from "./components/TableFillEditor";
import ImageOverlayFillEditor from "./components/ImageOverlayFillEditor";
import FillTypeMarksControls from "./components/FillTypeMarksControls";
import { inferBlankLabelPosition } from "../../utils/fillQuestionUtils";
import ShortAnswerEditor from "./components/ShortAnswerEditor";
import MatchingEditor from "./components/MatchingEditor";
import OrderingEditor from "./components/OrderingEditor";
import InteractiveDiagramEditor from "./components/InteractiveDiagramEditor";
import EssayAnswer from "./components/EssayAnswer";
import McqOptions from "./components/McqOptions";
import { validateQuestion } from "./utils/questionValidators";
import { SubjectChip } from "./components/QuestionBankSubjectChip";
import HtmlWithLatex from "./components/HtmlWithLatex";
import QuestionAnswerDisplay, { AnswerSourceBadge } from "./components/QuestionAnswerDisplay";
import QuestionPaperLayout from "./components/QuestionPaperLayout";
import {
  isErrorFindingStandalone,
  isErrorFindingContext,
  isClozeWordChoice,
} from "./utils/englishPaperDetection";
import QualityIndicator, { QualityBadge } from "./components/QualityIndicator";
import PdfComparisonView, { PdfToggleButton } from "./components/PdfComparisonView";
import PaperPageView, { PaperViewToggleButton } from "./components/PaperPageView.jsx";
import FormatInstructions from "../../components/FormatInstructions";
import ImportMarkdownViewerDialog from "./components/ImportMarkdownViewerDialog";
import PassageWithLineNumbers, { parseLineRefs } from "./components/PassageWithLineNumbers";
import { normalizeExtractedDisplayText } from "./utils/normalizeExtractedDisplayText";
import { parseReReadLineRange, extractPassageLines, computePassageWrapWidth, convertMarkdownLineNumbersToMarkers, insertLineNumberMarkers, stripLineNumberMarkers } from "./utils/passageLineExtract";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  QuillR2Editor,
  quillImageHandler,
  QUILL_MODULES as EXPL_QUILL_MODULES,
  QUILL_FORMATS as EXPL_QUILL_FORMATS,
  PASSAGE_QUILL_MODULES,
  PASSAGE_QUILL_FORMATS,
} from "../../utils/quillQuestionEditorConfig";

const INSTRUCTIONS_QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ size: ["small", false, "large"] }],
    ["clean"],
  ],
};
const INSTRUCTIONS_QUILL_FORMATS = ["bold", "italic", "underline", "list", "bullet", "indent", "size"];
const QUESTION_TEXT_QUILL_MODULES = {
  toolbar: {
    container: [
      [{ header: [false, 2, 3] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      ["image"],
      ["clean"],
    ],
    handlers: { image: quillImageHandler },
  },
};
const QUESTION_TEXT_QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "script",
  "image",
];

const normalizeQuillValue = (html) => {
  if (!html) return "";
  const compact = String(html).trim();
  if (compact === "<p><br></p>" || compact === "<p></p>") return "";
  return compact;
};

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const formatExplanationStepwise = (raw) => {
  const clean = String(raw || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\r/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean) return "";

  const explicitLines = String(raw || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const hasStepMarkers = explicitLines.some((l) => /^(\d+[).]|step\s*\d+[:.)-]?)/i.test(l));

  let steps = [];
  if (hasStepMarkers) {
    steps = explicitLines.map((l) =>
      l.replace(/^(\d+[).]\s*|step\s*\d+[:.)-]?\s*)/i, "").trim()
    );
  } else {
    steps = clean
      .split(/(?:\.\s+|;\s+|,\s+(?=(?:then|next|after that|finally|so)\b))/i)
      .map((s) => (s == null ? "" : String(s)).trim())
      .filter((s) => s.length > 0 && !/^(then|next|after that|finally|so)$/i.test(s));
  }

  if (steps.length === 0) steps = [clean];
  if (steps.length === 1 && steps[0].length > 140) {
    steps = steps[0]
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  const items = steps
    .map((s) => escapeHtml(s.replace(/\s+/g, " ").trim()))
    .filter(Boolean)
    .map((s) => `<li>${s}</li>`)
    .join("");

  return items ? `<ol>${items}</ol>` : "";
};

const VALID_QUESTION_TYPES = [
  "multiple-choice", "multiple-select", "essay", "fill-in-the-blank",
  "multi-fill", "matching", "ordering", "true-false", "short-answer", "grouped", "table", "image-fill", "drawing",
];
const QUESTION_TYPE_ALIASES = {
  standard: "short-answer",
  "open-ended": "essay",
  mcq: "multiple-choice",
  "multi-choice": "multiple-choice",
};
function normalizeQuestionType(type) {
  if (!type) return "multiple-choice";
  if (VALID_QUESTION_TYPES.includes(type)) return type;
  if (QUESTION_TYPE_ALIASES[type]) return QUESTION_TYPE_ALIASES[type];
  return "short-answer";
}
function parsePaperNumber(val) {
  if (val == null || val === "") return null;
  const n = parseInt(val, 10);
  return isNaN(n) ? null : n;
}

function duplicatePaperNumberKeys(qs) {
  const counts = new Map();
  for (const q of qs || []) {
    const pn = q?.paper_number;
    if (pn == null || String(pn).trim() === '') continue;
    const key = `${q?.section_title ?? ''}|${String(pn).trim()}`;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return new Set([...counts.entries()].filter(([, c]) => c > 1).map(([k]) => k));
}

function questionRowHasDuplicateKey(q, dupKeySet) {
  if (!dupKeySet || dupKeySet.size === 0) return false;
  const pn = q?.paper_number;
  if (pn == null || String(pn).trim() === '') return false;
  const key = `${q?.section_title ?? ''}|${String(pn).trim()}`;
  return dupKeySet.has(key);
}

function isMarkdownParityFailed(parity) {
  return Boolean(parity?.has_gap && (parity?.missing_count ?? 0) > 0);
}

/**
 * Root id of a paper shelf in the admin paper category tree: "practice" → Practice Papers,
 * "past" → Past Papers. An exact root name wins over a nested look-alike ("Revision > Past
 * Paper Revision"); the old fuzzy walk is the fallback for past papers.
 */
function findPaperShelfCategoryId(categories, shelf) {
  const want = shelf === "practice" ? "practice papers" : "past papers";
  const norm = (n) => String(n?.name || "").trim().toLowerCase().replace(/\s+/g, " ");
  const root = (categories || []).find((n) => norm(n) === want);
  if (root) return root.id;
  return shelf === "practice" ? null : findPastPapersCategoryId(categories);
}

/** Walk admin paper category tree and return id matching "past ... paper" (case-insensitive). */
function findPastPapersCategoryId(categories) {
  const walk = (nodes) => {
    for (const n of nodes || []) {
      const normalized = String(n.name || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
      if (normalized.includes("past") && normalized.includes("paper")) {
        return n.id;
      }
      const nested = n.all_children || n.children || [];
      const found = walk(nested);
      if (found != null) return found;
    }
    return null;
  };
  return walk(categories);
}

function flattenMockExamCategoriesForLineage(cats) {
  const out = [];
  const walk = (nodes) => {
    for (const cat of nodes || []) {
      out.push({ id: cat.id, name: cat.name, parent_id: cat.parent_id ?? null });
      walk(cat.all_children || cat.children || []);
    }
  };
  walk(cats);
  return out;
}

const PAST_PAPERS_LINEAGE_RE = /(\bpast[\s-]?papers?\b)/i;
const PRACTICE_PAPERS_LINEAGE_RE = /(\bpractice[\s-]?papers?\b)/i;

/** "practice" | "past" when the category sits under a paper shelf, else null (mock exam etc.). */
function paperShelfForMockCategoryLineage(flatCats, categoryId) {
  if (!categoryId || !flatCats.length) return null;
  const byId = new Map(flatCats.map((c) => [String(c.id), c]));
  const visited = new Set();
  let id = String(categoryId);
  while (id && !visited.has(id)) {
    visited.add(id);
    const node = byId.get(id);
    if (!node) break;
    if (node.name && PRACTICE_PAPERS_LINEAGE_RE.test(String(node.name))) return "practice";
    if (node.name && PAST_PAPERS_LINEAGE_RE.test(String(node.name))) return "past";
    const pid = node.parent_id != null && node.parent_id !== "" ? String(node.parent_id) : "";
    if (!pid) break;
    id = pid;
  }
  return null;
}

/** Strip HTML tags for list previews; LaTeX `$...$` / `$$...$$` stays intact for HtmlWithLatex. */
const stripTagsKeepLatex = (html) => (html || "").replace(/<[^>]*>/g, "");

const sectionBlockKey = (q) => `${q?.section_title ?? ""}\u0000${q?.section_instructions ?? ""}`;

const mergeSectionFieldsOntoQuestion = (q, st, si) => {
  const payload = { section_title: st, section_instructions: si };
  if (q.type === "grouped" && q.sub_questions?.length > 0) {
    return {
      ...q,
      ...payload,
      sub_questions: q.sub_questions.map((sq) => ({ ...sq, ...payload })),
    };
  }
  return { ...q, ...payload };
};

// Removes duplicate question text (AI sometimes outputs the question body twice,
// either in separate paragraphs or inline without a newline separator).
// For table questions, detects when the same table block appears more than once.
const dedupeQuestionText = (text) => {
  if (!text || text.length < 20) return text;

  // Check if text contains a markdown table
  const hasMarkdownTable = /\|[^\n]+\|/.test(text) && /\|[\s\-:]+\|/.test(text);

  if (hasMarkdownTable) {
    // Find all contiguous markdown table blocks (sequences of lines starting with |)
    const lines = text.split('\n');
    const tableBlocks = [];
    let inTable = false;
    let tableStart = 0;
    let tableLinesBuf = [];

    lines.forEach((line, i) => {
      const isTableLine = /^\s*\|/.test(line);
      if (isTableLine && !inTable) {
        inTable = true;
        tableStart = i;
        tableLinesBuf = [line];
      } else if (isTableLine && inTable) {
        tableLinesBuf.push(line);
      } else if (inTable) {
        inTable = false;
        tableBlocks.push({ start: tableStart, content: tableLinesBuf.join('\n') });
        tableLinesBuf = [];
      }
    });
    if (inTable && tableLinesBuf.length > 0) {
      tableBlocks.push({ start: tableStart, content: tableLinesBuf.join('\n') });
    }

    // If the same table block appears more than once, cut before the second occurrence.
    // This catches: "table\n\nquestion\n\ntable\n\nquestion[+answer blanks]"
    if (tableBlocks.length >= 2) {
      const firstTableNorm = tableBlocks[0].content.replace(/\s+/g, ' ').trim();
      for (let b = 1; b < tableBlocks.length; b++) {
        const nthTableNorm = tableBlocks[b].content.replace(/\s+/g, ' ').trim();
        if (firstTableNorm === nthTableNorm) {
          const cutLines = lines.slice(0, tableBlocks[b].start);
          while (cutLines.length > 0 && cutLines[cutLines.length - 1].trim() === '') {
            cutLines.pop();
          }
          return cutLines.join('\n');
        }
      }
    }

    // Paragraph-level half-duplicate check (exact equality)
    const paras = text.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
    if (paras.length >= 2) {
      const firstPara = paras[0];
      if (paras.every(p => p === firstPara)) return firstPara;
      const mid = Math.floor(paras.length / 2);
      const firstHalf = paras.slice(0, mid).join('\n\n');
      const secondHalf = paras.slice(mid).join('\n\n');
      if (firstHalf === secondHalf && firstHalf.length > 20) return firstHalf;
    }

    // No clear duplication — preserve as-is (table + question together)
    return text;
  }

  const toPlain = (s) => s.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const stripQNum = (s) => s.replace(/^Q?\d+[).:\s]+/i, "").trim();
  const normalizeBody = (s) => stripQNum(toPlain(s)).toLowerCase();

  // Case 1: paragraph-separated duplicates (split by \n\n)
  const paras = text.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
  if (paras.length >= 2) {
    const norm0 = normalizeBody(paras[0]);
    if (paras.slice(1).some((p) => normalizeBody(p) === norm0)) {
      return paras[0];
    }
  }

  // Case 2: inline duplicate — plain body is "X X" (text repeated without newline)
  const fullNorm = normalizeBody(text);
  const len = fullNorm.length;
  for (let i = Math.floor(len * 0.3); i <= Math.floor(len * 0.65); i++) {
    if (fullNorm.substring(0, i).trim() === fullNorm.substring(i).trim()) {
      const qMatch = text.match(/^(Q?\d+[).:\s]*)/i);
      const qPrefix = qMatch ? qMatch[1] : "";
      const rawBody = text.slice(qPrefix.length);
      // Walk rawBody counting non-tag chars until we have `i` of them
      let rIdx = 0, nCount = 0;
      while (rIdx < rawBody.length && nCount < i) {
        if (rawBody[rIdx] === "<") {
          const end = rawBody.indexOf(">", rIdx);
          rIdx = (end > rIdx ? end : rIdx) + 1;
        } else {
          nCount++;
          rIdx++;
        }
      }
      // Cut at last sentence boundary before rIdx
      const chunk = rawBody.substring(0, rIdx);
      const cutAt = Math.max(chunk.lastIndexOf("?"), chunk.lastIndexOf("."));
      if (cutAt > rIdx * 0.4) {
        return (qPrefix + rawBody.substring(0, cutAt + 1)).trim();
      }
      return (qPrefix + chunk).trim();
    }
  }

  // Case 3: trailing N-line block is a repeat of the N lines immediately before it.
  // Handles multi-fill questions where the AI lists sub-items (a)/b)) twice:
  //   "Intro text\na) item one\nb) item two\na) item one\nb) item two"
  // → "Intro text\na) item one\nb) item two"
  {
    const rawLines = text.split('\n');
    // Only consider lines with substantial content (avoids matching blank/short separator lines)
    const significant = rawLines
      .map((t, i) => ({ t: t.trim(), i }))
      .filter(({ t }) => t.length >= 5);
    if (significant.length >= 4) {
      const normLine = (l) => l.replace(/\s+/g, ' ').toLowerCase();
      for (let n = 1; n <= Math.floor(significant.length / 2); n++) {
        const suffix   = significant.slice(-n).map(({ t }) => normLine(t));
        const preceding = significant.slice(-2 * n, -n).map(({ t }) => normLine(t));
        if (suffix.every((l, i) => l === preceding[i])) {
          // Cut the original lines just before the repeated block begins
          const cutAt  = significant[significant.length - n].i;
          const trimmed = rawLines.slice(0, cutAt).join('\n').trimEnd();
          if (trimmed.length >= 15) return trimmed;
          break; // Guard: don't strip if remaining content is too short
        }
      }
    }
  }

  return text;
};

const buildPreviewQuestionRef = (parentIndex, subIndex = null) =>
  subIndex == null ? `preview:p_${parentIndex}` : `preview:p_${parentIndex}_sq_${subIndex}`;

const buildDbQuestionRef = (id) => (id != null ? `db:${id}` : null);

const ensureQuestionRef = (question, parentIndex, subIndex = null) => {
  if (!question || typeof question !== "object") return question;
  const explicitRef = question.question_ref || question.import_question_ref;
  const fallbackRef =
    question.id != null && String(question.id).match(/^\d+$/)
      ? buildDbQuestionRef(question.id)
      : buildPreviewQuestionRef(parentIndex, subIndex);

  return {
    ...question,
    import_question_ref: explicitRef || fallbackRef,
  };
};

const paperNumberSortValue = (q) => {
  const raw = String(q?.paper_number ?? q?.question_number ?? "").replace(
    /\D/g,
    ""
  );
  return raw === "" ? Number.MAX_SAFE_INTEGER : parseInt(raw, 10);
};

const normalizeMarksValue = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const m = Math.floor(n);
  return m >= 1 ? m : null;
};

const normalizeQuestionsForReview = (rawQuestions = [], { skipSort = false } = {}) => {
  // Defensive sort by paper_number so the reviewer sees questions in PDF order
  // even if the backend persisted them in extraction/chunk order. Matches the
  // backend `sortByPaperNumber` so ref ids stay stable on re-normalize.
  // skipSort=true is used for manual inserts so the question stays at the splice position.
  const sorted = skipSort
    ? [...(rawQuestions || [])]
    : [...(rawQuestions || [])].sort((a, b) => {
        const pa = paperNumberSortValue(a);
        const pb = paperNumberSortValue(b);
        return pa - pb;
      });

  return sorted.map((q, qIdx) => {
    const normalizedQuestion = ensureQuestionRef(
      {
        ...q,
        is_sample_question: !!q.is_sample_question,
        question_text: dedupeQuestionText(q.question_text),
        sub_questions: (q.sub_questions || []).map((sq, sqIdx) =>
          ensureQuestionRef(
            { ...sq, question_text: dedupeQuestionText(sq.question_text) },
            qIdx,
            sqIdx
          )
        ),
      },
      qIdx
    );

    return normalizedQuestion;
  });
};

const collectNumericPaperNumbersFromQuestions = (qs) => {
  const nums = [];
  for (const q of qs || []) {
    const pn = parsePaperNumber(q?.paper_number);
    if (pn != null) nums.push(pn);
    for (const sq of q?.sub_questions || []) {
      const sn = parsePaperNumber(sq?.paper_number);
      if (sn != null) nums.push(sn);
    }
  }
  return nums;
};

const suggestNextPaperNumber = (questions, markdownParity) => {
  const missing = markdownParity?.missing_question_numbers;
  if (Array.isArray(missing) && missing.length > 0) {
    const sorted = [...missing].sort((a, b) => a - b);
    return sorted[0];
  }
  const nums = collectNumericPaperNumbersFromQuestions(questions);
  if (nums.length === 0) return 1;
  return Math.max(...nums) + 1;
};

const buildManualRootQuestionSkeleton = (questions, markdownParity) => {
  const n = suggestNextPaperNumber(questions, markdownParity);
  return {
    type: "multiple-choice",
    difficulty: "Medium",
    default_marks: 1,
    topic: null,
    question_text: "(Edit question text)",
    paper_number: n,
    section_title: null,
    section_instructions: null,
    question_subtype: null,
    image_url: null,
    image_urls: null,
    image_description: null,
    explanation: null,
    options: [
      { text: "Option A", is_correct: true },
      { text: "Option B", is_correct: false },
    ],
    answer_data: { correct_options: [0] },
    sub_questions: [],
    is_sample_question: false,
  };
};

const buildComprehensionPassageSkeleton = (questions, markdownParity) => {
  const n = suggestNextPaperNumber(questions, markdownParity);
  return {
    type: "grouped",
    passage_type: "comprehension",
    difficulty: "Medium",
    default_marks: 2,
    topic: null,
    question_text: "",
    paper_number: n,
    section_title: null,
    section_instructions: null,
    question_subtype: null,
    image_url: null,
    image_urls: null,
    image_description: null,
    explanation: null,
    options: [],
    answer_data: {},
    sub_questions: [],
    is_sample_question: false,
  };
};

const remapIndexSetForInsert = (set, insertAt) => {
  const next = new Set();
  set.forEach((j) => {
    if (j < insertAt) next.add(j);
    else next.add(j + 1);
  });
  return next;
};

const remapIndexSetForDelete = (set, delIdx) => {
  const next = new Set();
  set.forEach((j) => {
    if (j === delIdx) return;
    if (j < delIdx) next.add(j);
    else next.add(j - 1);
  });
  return next;
};

const remapSetAfterSwap = (set, i, j) => {
  const next = new Set();
  set.forEach((k) => {
    if (k === i) next.add(j);
    else if (k === j) next.add(i);
    else next.add(k);
  });
  return next;
};

const remapIndexSetForMoveRange = (set, fromStart, fromEnd, insertAtBeforeRemoval) => {
  const movedLen = fromEnd - fromStart + 1;
  const indexed = [...set];
  const remapped = new Set();

  const insertAtAfterRemoval =
    insertAtBeforeRemoval > fromEnd
      ? insertAtBeforeRemoval - movedLen
      : insertAtBeforeRemoval;

  indexed.forEach((k) => {
    // Inside moved block
    if (k >= fromStart && k <= fromEnd) {
      const offset = k - fromStart;
      remapped.add(insertAtAfterRemoval + offset);
      return;
    }

    // Shift due to removal of moved block
    let shifted = k;
    if (k > fromEnd) shifted = k - movedLen;

    // Shift due to insertion of moved block
    if (shifted >= insertAtAfterRemoval) shifted += movedLen;
    remapped.add(shifted);
  });

  return remapped;
};

const parseHeaderPaperInputValue = (raw) => {
  const t = String(raw ?? "").trim();
  if (t === "") return null;
  if (/^\d+$/.test(t)) {
    const n = parseInt(t, 10);
    return Number.isNaN(n) ? t : n;
  }
  return t;
};

// Strip trailing exam-booklet content that bleeds into a comprehension passage stem.
// Happens when the PDF extraction merges the reading-passage page and the answer-booklet
// cover page into one markdown block. We cut at the first mid-body ## heading that looks
// like an exam-section banner so the passage stops at the end of the story, not the instructions.
const stripPassageTail = (text) => {
  if (!text) return text;
  const m = text.match(/\n{2,}(?:!\[[^\]]*\]\([^)]*\)\s*\n+)?#{1,3}\s+\*\*[A-Z ]+(?:ENTRANCE|EXAMINATION|SECTION|SCHOLARSHIP|WRITING|READING)[^]*/i);
  if (!m) return text;
  return text.slice(0, m.index).trimEnd();
};

// Strips trailing sub-question prompts (a) ..., b) ...) from a grouped question's stem.
// The AI includes sub-part questions in the group_title/question_text even though they are
// already shown as separate labelled sub-question cards below the stem. This function
// removes that duplicated content so the stem shows only the shared passage/context.
const stripSubQsFromStem = (stemText, subQuestions = []) => {
  if (!stemText) return stemText;
  if (!Array.isArray(subQuestions) || subQuestions.length === 0) return stemText;
  // Match the first occurrence of a sub-question label: " a) " / " (a) " / " i) " / " (i) "
  const match = stemText.match(
    /^([\s\S]*?)\s+(?:[a-z]\)|\([a-z]\)|[ivx]+\)|\([ivx]+\))\s+/
  );
  if (!match) return stemText;
  const before = match[1].trim();
  // Guard: only strip when there is substantial content before the label (avoid false positives)
  if (before.length < 80) return stemText;
  const firstSub = String(subQuestions[0]?.question_text || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  const after = String(stemText.slice(match[0].length))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (firstSub.length >= 12 && !after.includes(firstSub.slice(0, 12))) {
    return stemText;
  }
  return before;
};

const getGroupedStemText = (q) => {
  if (!q || typeof q !== "object") return "";
  const direct = String(q.question_text || "").trim();
  if (direct) return direct;
  const legacy = String(q.group_title || "").trim();
  if (legacy) return legacy;
  return "";
};

const isEnglishSubjectLabel = (subject) =>
  /english/i.test(String(subject || ""));

// Detect comprehension passage for review, but only for English imports.
const isComprehensionPassageForReview = (q, examSubject) =>
  isEnglishSubjectLabel(examSubject) &&
  q?.type === "grouped" &&
  (q?.passage_type === "comprehension" || (q?.question_text?.length ?? 0) > 400);

// Detect a standalone (non-grouped) comprehension passage that was extracted incorrectly.
// This happens when the PDF passage page and questions page are separated by a cover page,
// causing the AI to extract the passage as an essay/short-answer question instead of
// grouping it with its comprehension Q&A sub-questions.
const isOrphanedPassage = (q, subject) => {
  if (!q) return false;
  const type = q.type || "";
  if (type === "grouped" || !["essay", "short-answer", ""].includes(type)) return false;
  if ((q.options || []).length > 0) return false;
  // Maths papers never have reading passages
  if (subject && /math/i.test(subject)) return false;
  const plain = (q.question_text || "").replace(/<[^>]*>/g, "");
  if (plain.length < 400) return false;
  // Questions containing a markdown table are data questions, not passages
  if (/\|[-|: ]+\|/.test(q.question_text || "")) return false;
  // Contains line-number markers (only added to comprehension passages)
  if (/\{L\d+\}/.test(q.question_text || "")) return true;
  // passage_type explicitly set
  if (q.passage_type === "comprehension") return true;
  // Long text with no question mark at the end (reading passage, not a question)
  if (plain.length >= 500 && !plain.trimEnd().endsWith("?")) return true;
  return false;
};

// Human-friendly labels for VR/NVR structural issue types (used in badge display)
const VR_NVR_ISSUE_LABELS = {
  nvr_option_images_missing: "NVR: Missing Option Images",
  nvr_odd_one_out_option_count: "NVR: Wrong Option Count",
  nvr_set_membership_option_count: "NVR: Set Membership Count",
  nvr_odd_one_out_stem_image_present: "NVR: Stem on Odd-One-Out",
  nvr_stem_image_missing: "NVR: Stem Image Missing",
  nvr_practice_leaked: "NVR: Practice Q Leaked",
  vr_practice_leaked: "VR: Practice Q Leaked",
  vr_cipher_key_leaked: "VR: Cipher Key Leaked",
  vr_table_row_mismatch: "VR: Missing Table Rows",
};

// NVR question_subtype display labels and colors
const NVR_SUBTYPE_LABELS = {
  standard_nvr: { label: "NVR: Analogy/Sequence", color: "#e65100", bg: "#fff3e0" },
  odd_one_out: { label: "NVR: Odd One Out", color: "#1565c0", bg: "#e3f2fd" },
  set_membership: { label: "NVR: Set Membership", color: "#1b5e20", bg: "#e8f5e9" },
  applying_operations: { label: "NVR: Applying Operations", color: "#4a148c", bg: "#f3e5f5" },
};

// Returns true when a question already has its answer data populated
const questionHasAnswer = (q) => {
  if (!q) return false;
  const type = q.type || '';
  if (['multiple-choice', 'multiple-select', 'true-false'].includes(type)) {
    return (q.options || []).some((o) => o.is_correct);
  }
  if (['essay', 'short-answer'].includes(type)) {
    return !!(q.answer_data?.correct_answer_text);
  }
  if (['fill-in-the-blank', 'multi-fill', 'table', 'image-fill'].includes(type)) {
    const blanks = q.answer_data?.correct_blanks || q.blanks || [];
    if (blanks.some((b) => Array.isArray(b) && b.some((a) => String(a ?? '').trim()))) return true;
    if (type === 'image-fill') {
      return !!(q.answer_data?.overlay_layout?.image_url?.trim());
    }
    return blanks.length > 0;
  }
  if (type === 'matching') {
    return (q.options || []).some((o) => o.match_text);
  }
  if (type === 'ordering') {
    return (q.options || []).length > 0;
  }
  if (type === 'drawing') {
    // Deterministic (interactive geometry) or AI-vision (answer diagram / description) both count.
    return !!(q.answer_data?.solution_geometry?.interactive
      || q.answer_data?.correct_solution_image_url
      || q.answer_data?.correct_answer_text);
  }
  return false;
};

/**
 * Toggle buttons shown inside the answer box when both pdf_answer_data and
 * ai_answer_data are available, allowing admin to switch the active answer.
 */
const AnswerSourceSelector = ({ question, onSwitch, onEditManual }) => {
  const hasPdf = !!question?.pdf_answer_data;
  const hasAi = !!question?.ai_answer_data;
  // Show selector only when at least two sources exist
  if (!hasPdf && !hasAi) return null;

  const activeSource = question?.answer_data?.answer_source;
  const currentValue =
    activeSource === "answer_sheet" ? "pdf" :
    (activeSource === "ai" || activeSource === "programmatic" || activeSource === "cross_validated") ? "ai" :
    "manual";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
        Source:
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={currentValue}
        onChange={(_, val) => { if (val && val !== "manual") onSwitch(val); }}
        sx={{ "& .MuiToggleButton-root": { py: 0.25, px: 1, fontSize: "0.7rem", textTransform: "none" } }}
      >
        <ToggleButton value="pdf" disabled={!hasPdf}>
          PDF Key
        </ToggleButton>
        <ToggleButton value="ai" disabled={!hasAi}>
          AI
        </ToggleButton>
      </ToggleButtonGroup>
      <Button
        size="small"
        variant="outlined"
        startIcon={<Edit sx={{ fontSize: "0.8rem !important" }} />}
        onClick={onEditManual}
        sx={{ py: 0.25, px: 1, fontSize: "0.7rem", textTransform: "none", borderRadius: 1 }}
      >
        Edit
      </Button>
    </Box>
  );
};

const QuestionBankImportReview = () => {
  const { id: importId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [importData, setImportData] = useState(null);
  // Plagiarism: per-question duplicates from GET /import/{id}/similarity
  const [similarity, setSimilarity] = useState(null);
  const [similarityDialogOpen, setSimilarityDialogOpen] = useState(false);
  const [examTitle, setExamTitle] = useState("");
  const [examInstructions, setExamInstructions] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [schoolLogo, setSchoolLogo] = useState(null);
  const [examSubject, setExamSubject] = useState("");
  const [examDuration, setExamDuration] = useState("");
  const [examSection, setExamSection] = useState("");
  const [importTopicCatalog, setImportTopicCatalog] = useState([]);
  const [importTopicYearId, setImportTopicYearId] = useState("");
  const [approvedSet, setApprovedSet] = useState(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  // editTarget: null | { type: 'standalone' } | { type: 'stem', parentIndex } | { type: 'subquestion', parentIndex, subIndex }
  const [editTarget, setEditTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [originalQuestions, setOriginalQuestions] = useState([]);
  const [modifiedQuestions, setModifiedQuestions] = useState(new Set());
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkEditValues, setBulkEditValues] = useState({
    difficulty: "",
    default_marks: "",
    section_title: "",
    section_instructions: "",
  });
  const imageInputRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const draftDebounceTimerRef = useRef(null);
  const lastSavedSnapshotRef = useRef(null);
  const baseUpdatedAtRef = useRef(null);
  const extractionBaselineRef = useRef(new Map());

  const captureExtractionBaseline = useCallback((qs) => {
    const clone = JSON.parse(JSON.stringify(qs || []));
    setOriginalQuestions(clone);
    const map = new Map();
    for (const q of clone) {
      if (q?.import_question_ref) {
        map.set(q.import_question_ref, JSON.parse(JSON.stringify(q)));
      }
      for (const sq of q?.sub_questions || []) {
        if (sq?.import_question_ref) {
          map.set(sq.import_question_ref, JSON.parse(JSON.stringify(sq)));
        }
      }
    }
    extractionBaselineRef.current = map;
  }, []);

  const lookupBaselineQuestion = useCallback((ref, fallback) => {
    if (ref && extractionBaselineRef.current.has(ref)) {
      return JSON.parse(JSON.stringify(extractionBaselineRef.current.get(ref)));
    }
    if (fallback != null) {
      return JSON.parse(JSON.stringify(fallback));
    }
    return null;
  }, []);

  const [headerPaperDraft, setHeaderPaperDraft] = useState("");
  const [editLivePreview, setEditLivePreview] = useState(true);
  const [markdownDialogOpen, setMarkdownDialogOpen] = useState(false);
  const [parityPanelExpanded, setParityPanelExpanded] = useState(false);

  // AI Improve state
  const [improving, setImproving] = useState(false);
  const [improvingAll, setImprovingAll] = useState(false);
  const [generatingExplanation, setGeneratingExplanation] = useState(false);
  const [generatingAllExplanations, setGeneratingAllExplanations] = useState(false);
  const [explainAllProgress, setExplainAllProgress] = useState({ done: 0, total: 0 });
  const [aiImproveOpen, setAiImproveOpen] = useState(false);
  const [aiOriginal, setAiOriginal] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiImprovePath, setAiImprovePath] = useState(null);

  // AI Generate Diagram state
  const [generatingDiagram, setGeneratingDiagram] = useState(false);
  const [diagramDialogOpen, setDiagramDialogOpen] = useState(false);

  // Source-link deep research (import review dialogs)
  const [importFindingLinks, setImportFindingLinks]       = useState(false);
  const [importLinkCandidates, setImportLinkCandidates]   = useState([]);
  const [importLinkAnchorEl, setImportLinkAnchorEl]       = useState(null);
  const [importLinkTarget, setImportLinkTarget]           = useState(null); // 'exam' | 'pastpaper'

  // Quality & Answer Generation state
  const [qualityReport, setQualityReport] = useState(null);
  const [validationReport, setValidationReport] = useState(null);
  const [accuracyMetrics, setAccuracyMetrics] = useState(null);
  const [generatingAnswers, setGeneratingAnswers] = useState(false);
  const [validating, setValidating] = useState(false);
  const [agentFixing, setAgentFixing] = useState(false);
  const [fixingMarkdown, setFixingMarkdown] = useState(false);
  const [addingLineNumbers, setAddingLineNumbers] = useState(false);
  const [manualLineNum, setManualLineNum] = useState("");
  const passageQuillRef = useRef(null);
  const savedPassageSelectionRef = useRef(null);
  const [activeSubQIdx, setActiveSubQIdx] = useState(null);
  const [showPdf, setShowPdf] = useState(false);
  const [showPaperView, setShowPaperView] = useState(false);
  const [supplementaryBookletUrl, setSupplementaryBookletUrl] = useState(null);
  const [showSupplementary, setShowSupplementary] = useState(false);
  const [uploadingSupplementary, setUploadingSupplementary] = useState(false);
  const [generatedDiagram, setGeneratedDiagram] = useState(null);
  const [diagramTargetPath, setDiagramTargetPath] = useState(null);

  const dragSrcIdx = useRef(null);

  // Generate Similar Paper — the dialog owns generation, polling, and the hand-off
  // to the Similar Paper review page, where the questions are approved and saved.
  const [generateOpen, setGenerateOpen] = useState(false);

  // Rescan + Apply Updates state
  const [rescanning, setRescanning] = useState(false);
  const [applyingUpdates, setApplyingUpdates] = useState(false);
  const [applyUpdatesResult, setApplyUpdatesResult] = useState(null);

  // Passage expand/collapse for long comprehension passages
  const [passageExpanded, setPassageExpanded] = useState(false);

  // Extracted Images panel state
  const [imagesOpen, setImagesOpen] = useState(false);
  const [extractedImages, setExtractedImages] = useState([]);
  const [imagesPanelQuestions, setImagesPanelQuestions] = useState([]);
  const qLabelMap = useMemo(() => {
    const map = {};
    let parentNum = 0;
    const parentNums = {};
    imagesPanelQuestions.forEach((q) => {
      if (!q.parent_question_id) {
        parentNum++;
        parentNums[String(q.id)] = parentNum;
        if (q.question_ref) parentNums[String(q.question_ref)] = parentNum;
        map[String(q.id)] = `Q${parentNum}`;
        if (q.question_ref) map[String(q.question_ref)] = `Q${parentNum}`;
      }
    });
    const subCounters = {};
    imagesPanelQuestions.forEach((q) => {
      if (q.parent_question_id) {
        const pk = String(q.parent_question_id);
        subCounters[pk] = (subCounters[pk] || 0) + 1;
        const label = `Q${parentNums[pk] ?? '?'}.${subCounters[pk]}`;
        map[String(q.id)] = label;
        if (q.question_ref) map[String(q.question_ref)] = label;
      }
    });
    return map;
  }, [imagesPanelQuestions]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [reassigningImage, setReassigningImage] = useState(null);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignTargetImage, setReassignTargetImage] = useState(null);
  const [reassignSelectedQuestion, setReassignSelectedQuestion] = useState("");
  const [removingExtractedImage, setRemovingExtractedImage] = useState(null);
  const [unassigningImage, setUnassigningImage] = useState(null);
  const [dragImageIdx, setDragImageIdx] = useState(null);
  const [dragOverImageIdx, setDragOverImageIdx] = useState(null);
  const draggedExtractedImageRef = useRef(null);
  const [isDropOverEditImages, setIsDropOverEditImages] = useState(false);

  // Create Exam from Import state
  const [createExamOpen, setCreateExamOpen] = useState(false);
  const [creatingExam, setCreatingExam] = useState(false);
  const [examCategories, setExamCategories] = useState([]);
  const [examFormats, setExamFormats] = useState([]);
  const [examSubjects, setExamSubjects] = useState([]);
  const [examSchoolYears, setExamSchoolYears] = useState([]);
  const [examSchools, setExamSchools] = useState([]);
  const [loadingExamMeta, setLoadingExamMeta] = useState(false);
  const [examForm, setExamForm] = useState({
    name: "",
    description: "",
    original_paper_link: "",
    category_id: "",
    format_id: "",
    subject_ids: [],
    school_year_id: "",
    price: "0",
    currency: "£",
    duration_minutes: "",
    school_id: "",
    target_school_ids: [],
    // Advanced settings (aligned with MockExam create form)
    max_attempts: 2,
    marks_per_correct_answer: 1,
    negative_marks_penalty: 0,
    instructions: "",
  });
  const [examFormErrors, setExamFormErrors] = useState({});

  // Create Past Paper (catalog Paper → PaperExtract) state
  const [createPastPaperOpen, setCreatePastPaperOpen] = useState(false);
  const [creatingPastPaper, setCreatingPastPaper] = useState(false);
  const [loadingPastPaperMeta, setLoadingPastPaperMeta] = useState(false);
  const [paperCategories, setPaperCategories] = useState([]);
  const [pastPaperForm, setPastPaperForm] = useState({
    name: "",
    description: "",
    original_paper_link: "",
    category_id: "",
    format_id: "",
    subject_ids: [],
    school_year_id: "",
    target_school_ids: [],
    price: "0",
    currency: "£",
    duration_minutes: "",
    school_id: "",
    is_free_paper: false,
  });
  const [pastPaperFormErrors, setPastPaperFormErrors] = useState({});

  // Edit Exam Metadata state
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [editExamMetaOpen, setEditExamMetaOpen] = useState(false);
  const [autoNumberInstructions, setAutoNumberInstructions] = useState(false);
  const [autoNumberQuestions, setAutoNumberQuestions] = useState(false);
  const [editExamMetaValues, setEditExamMetaValues] = useState({
    school_name: "",
    school_logo: null,
    exam_subject: "",
    exam_duration: "",
    exam_section: "",
    exam_title: "",
    exam_instructions: "",
    auto_number_instructions: false,
    auto_number_questions: false,
  });
  const [savingExamMeta, setSavingExamMeta] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [listEditPaperNum, setListEditPaperNum] = useState(null);
  const [listEditSection, setListEditSection] = useState(null);
  const [imageDropActive, setImageDropActive] = useState(false);

  const parseExamHeaderFromHtml = (html) => {
    if (!html) return {};
    
    const result = {
      schoolLogo: null,
      schoolName: "",
      examTitle: "",
      examSubject: "",
      examSection: "",
      examDuration: "",
      instructionsText: html
    };

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const img =
      tempDiv.querySelector('img[alt*="crest"]') ||
      tempDiv.querySelector('img[alt*="logo"]') ||
      tempDiv.querySelector('img[alt*="school"]') ||
      tempDiv.querySelector('img[alt*="badge"]') ||
      tempDiv.querySelector('img:first-of-type');
    if (img && img.src) {
      result.schoolLogo = img.src;
      img.remove();
    }

    const h1 = tempDiv.querySelector('h1');
    if (h1) {
      result.examTitle = h1.textContent.trim();
      h1.remove();
    }

    const h2Elements = tempDiv.querySelectorAll('h2');
    h2Elements.forEach((h2) => {
      const text = h2.textContent.trim().toUpperCase();
      if (text.includes('SECTION')) {
        result.examSection = text;
      } else if (['MATHEMATICS', 'ENGLISH', 'SCIENCE', 'CHEMISTRY', 'PHYSICS', 'BIOLOGY', 'HISTORY', 'GEOGRAPHY'].some(s => text.includes(s))) {
        result.examSubject = text;
      }
      h2.remove();
    });

    const pElements = tempDiv.querySelectorAll('p');
    let durationParagraph = null;
    
    pElements.forEach((p) => {
      const text = p.textContent.trim();
      if (/^\d+\s*(minutes?|hours?|mins?|hrs?)$/i.test(text)) {
        result.examDuration = text;
        durationParagraph = p;
      } else if (text.includes('ST PAUL') || text.includes("GIRLS' SCHOOL") || text.includes("BOYS' SCHOOL") || text.includes('SCHOOL')) {
        const cleanName = text.replace(/<[^>]*>/g, '').trim();
        if (cleanName && !result.schoolName) {
          result.schoolName = cleanName;
        }
      }
    });

    if (durationParagraph) durationParagraph.remove();

    tempDiv.querySelectorAll('img').forEach(img => img.remove());
    tempDiv.querySelectorAll('div').forEach(div => div.remove());
    tempDiv.querySelectorAll('b, br, i').forEach(el => {
      if (el.tagName === 'BR') {
        el.replaceWith('\n');
      } else {
        el.replaceWith(el.textContent);
      }
    });

    const remainingPs = tempDiv.querySelectorAll('p');
    const instructionLines = [];
    remainingPs.forEach((p) => {
      const text = p.textContent.trim();
      if (text && !text.includes('Name:') && !text.includes('Age:') && !text.includes('Group Number:') && !text.includes('PLEASE READ')) {
        instructionLines.push(text);
      }
    });

    result.instructionsText = instructionLines.join('\n');
    
    return result;
  };

  // Fetch preview data
  useEffect(() => {
    const fetchPreview = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/question-bank/import/${importId}/preview`);
        const data = res.data?.data;
        setImportData(data);

        const examInstructionsHtml = data?.exam_instructions || "";
        const parsedFromHtml = parseExamHeaderFromHtml(examInstructionsHtml);

        const apiSchoolName = data?.school_name || "";
        const apiExamSubject = data?.exam_subject || "";
        const apiExamDuration = data?.exam_duration || "";
        const apiExamSection = data?.exam_section || "";

        const finalSchoolName = apiSchoolName || parsedFromHtml.schoolName;
        const finalSchoolLogo = data?.school_logo ||
          (parsedFromHtml.schoolLogo && parsedFromHtml.schoolLogo !== 'null' ? parsedFromHtml.schoolLogo : null);
        const finalExamTitle = data?.exam_title || parsedFromHtml.examTitle;
        const finalExamSubject = apiExamSubject || parsedFromHtml.examSubject;
        const finalExamDuration = apiExamDuration || parsedFromHtml.examDuration;
        const finalExamSection = apiExamSection || parsedFromHtml.examSection;
        const finalInstructions = parsedFromHtml.instructionsText || examInstructionsHtml;

        setSchoolName(finalSchoolName);
        setSchoolLogo(finalSchoolLogo);
        setExamTitle(finalExamTitle);
        setExamSubject(finalExamSubject);
        setExamDuration(finalExamDuration);
        setExamSection(finalExamSection);
        setExamInstructions(finalInstructions);
        
        const parsedQuestions = normalizeQuestionsForReview(data?.parsed_questions || []);
        setQuestions(parsedQuestions);
        lastSavedSnapshotRef.current = JSON.stringify(parsedQuestions);
        baseUpdatedAtRef.current = data?.updated_at ?? null;
        captureExtractionBaseline(parsedQuestions);
        setApprovedSet(new Set());
        const questionIndexParam = searchParams.get("question");
        const questionIndex =
          questionIndexParam != null && questionIndexParam !== ""
            ? Math.min(Math.max(0, parseInt(questionIndexParam, 10)), Math.max(0, parsedQuestions.length - 1))
            : 0;
        setCurrentIndex(Number.isNaN(questionIndex) ? 0 : questionIndex);

        // Quality and validation data
        if (data?.quality_report) setQualityReport(data.quality_report);
        if (data?.validation_report) setValidationReport(data.validation_report);
        if (data?.accuracy_metrics) setAccuracyMetrics(data.accuracy_metrics);

        // Supplementary booklet (auto-detected or manually uploaded)
        setSupplementaryBookletUrl(data?.supplementary_booklet_url || null);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load import preview");
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [importId, searchParams, captureExtractionBaseline]);

  useEffect(() => {
    if (!importData?.target_category_id || examCategories.length > 0) return;
    let cancelled = false;
    api
      .get("admin/mock-exam/category-tree")
      .then((res) => {
        if (!cancelled) setExamCategories(res.data?.data || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [importData?.target_category_id, examCategories.length]);

  useEffect(() => {
    if (!importData?.subject_id) {
      setImportTopicCatalog([]);
      return;
    }
    let cancelled = false;
    api
      .get("/admin/question-bank/topics-by-subject", { params: { subject_id: importData.subject_id, include_counts: 1 } })
      .then((res) => {
        if (!cancelled) setImportTopicCatalog(res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setImportTopicCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [importData?.subject_id]);

  const flatMockCategoriesLineage = useMemo(
    () => flattenMockExamCategoriesForLineage(examCategories),
    [examCategories]
  );
  // Practice Papers and Past Papers imports both become catalogue Papers; the shelf picks
  // the dialog's default category.
  const importPaperShelf = useMemo(
    () => paperShelfForMockCategoryLineage(flatMockCategoriesLineage, importData?.target_category_id),
    [flatMockCategoriesLineage, importData?.target_category_id]
  );
  const isPastPaperCategory = importPaperShelf !== null;
  // Which shelf the Create Paper dialog's chosen category is (null: some other root picked).
  const pastPaperShelf = useMemo(() => {
    const id = String(pastPaperForm.category_id || "");
    if (!id) return null;
    if (String(findPaperShelfCategoryId(paperCategories, "practice") ?? "") === id) return "practice";
    if (String(findPaperShelfCategoryId(paperCategories, "past") ?? "") === id) return "past";
    return null;
  }, [paperCategories, pastPaperForm.category_id]);

  // Fetch canonical subjects for the Edit Header subject dropdown
  useEffect(() => {
    api.get("/admin/question-bank/exam-subjects")
      .then((res) => setAvailableSubjects(res.data?.data || []))
      .catch(() => {});
  }, []);

  const currentQuestion = useMemo(() => questions[currentIndex], [questions, currentIndex]);

  useEffect(() => {
    if (!importId) return;
    api
      .get(`/admin/question-bank/import/${importId}/similarity`)
      .then((res) => setSimilarity(res.data?.data || null))
      .catch(() => {});
  }, [importId]);

  // Questions can be reordered while reviewing, so match on paper_number first, index second.
  const currentSimilarityMatches = useMemo(() => {
    const matches = similarity?.report?.questions?.matches || [];
    if (!currentQuestion || !matches.length) return [];
    const pn = currentQuestion.paper_number != null ? String(currentQuestion.paper_number) : null;
    return matches.filter((m) =>
      pn != null && m.paper_number != null ? String(m.paper_number) === pn : m.index === currentIndex
    );
  }, [similarity, currentQuestion, currentIndex]);

  const duplicatePaperNumbers = useMemo(
    () => duplicatePaperNumberKeys(questions),
    [questions]
  );
  const duplicatePaperNumbersSorted = useMemo(
    () => [...duplicatePaperNumbers].map((k) => k.split('|')[1]).sort(),
    [duplicatePaperNumbers]
  );

  useEffect(() => {
    if (
      editMode &&
      editDraft &&
      (editTarget?.type === "stem" ||
        editTarget?.type === "standalone" ||
        editTarget?.type === "subquestion")
    ) {
      const pn = editDraft.paper_number;
      setHeaderPaperDraft(pn != null && pn !== "" ? String(pn) : "");
      return;
    }
    const pn = questions[currentIndex]?.paper_number;
    setHeaderPaperDraft(pn != null && pn !== "" ? String(pn) : "");
  }, [currentIndex, questions, editMode, editDraft, editTarget?.type]);

  // Resolves the ref to use when assigning images to the current question.
  // For confirmed imports, parsed_questions never carries a DB id, so we look up
  // the matching DB question in imagesPanelQuestions by paper_number/position.
  const currentQuestionImagesRef = useMemo(() => {
    if (!currentQuestion) return null;
    const isConfirmed = importData?.has_imported_questions === true;
    if (isConfirmed) {
      const rootPanelQs = imagesPanelQuestions.filter((q) => !q.parent_question_id);
      // Prefer exact paper_number match
      const pn = currentQuestion.paper_number;
      if (pn != null) {
        const panelQ = rootPanelQs.find((q) => String(q.paper_number) === String(pn));
        if (panelQ) return panelQ.question_ref || buildDbQuestionRef(panelQ.id);
      }
      // Fallback: positional match (root questions are ordered same as parsed_questions)
      const rootIdx = questions
        .slice(0, currentIndex)
        .filter((q) => !q.parent_question_id).length;
      if (rootPanelQs[rootIdx]) {
        const pq = rootPanelQs[rootIdx];
        return pq.question_ref || buildDbQuestionRef(pq.id);
      }
      return null;
    }
    // Preview: use the normalised import_question_ref
    return currentQuestion.import_question_ref || null;
  }, [currentQuestion, currentIndex, importData, imagesPanelQuestions, questions]);
  const approvedCount = approvedSet.size;
  const totalCount = questions.length;
  const markdownParity = importData?.markdown_parity || null;
  const markdownParityBlocking = Boolean(markdownParity?.has_gap && (markdownParity?.missing_count || 0) > 0);
  const markdownParityRanges = (markdownParity?.missing_ranges || []).join(", ");

  useEffect(() => {
    if (markdownParityBlocking) setParityPanelExpanded(true);
  }, [markdownParityBlocking]);

  useEffect(() => {
    setPassageExpanded(false);
  }, [currentIndex]);

  // ── Source-link deep research for import review dialogs ─────────────────

  const handleFindImportSourceLinks = async (target, anchorEl) => {
    setImportLinkTarget(target);
    setImportLinkAnchorEl(anchorEl);
    setImportLinkCandidates([]);
    setImportFindingLinks(true);
    try {
      const res = await api.post(`/admin/question-bank/import/${importId}/find-source-links`);
      setImportLinkCandidates(res.data?.data?.candidates || []);
    } catch {
      toast.error("Failed to fetch source link candidates");
    } finally {
      setImportFindingLinks(false);
    }
  };

  const handleSelectImportLink = (url) => {
    if (importLinkTarget === "exam") {
      setExamForm((p) => ({ ...p, original_paper_link: url }));
    } else {
      setPastPaperForm((p) => ({ ...p, original_paper_link: url }));
    }
    setImportLinkAnchorEl(null);
  };

  // ─────────────────────────────────────────────────────────────────────────

  const toggleApproval = useCallback((index) => {
    setApprovedSet((prev) => {
      const updated = new Set(prev);
      if (updated.has(index)) {
        updated.delete(index);
      } else {
        updated.add(index);
      }
      return updated;
    });
  }, []);

  const approveAll = useCallback(async () => {
    setApprovedSet(new Set(questions.map((_, i) => i)));
    try {
      const res = await api.post(`/admin/question-bank/import/${importId}/issues/dismiss-all`);
      const data = res.data?.data;
      if (data && (data.dismissed_count ?? 0) > 0) {
        setImportData((prev) => {
          if (!prev) return prev;
          const vr = prev.validation_report ? { ...prev.validation_report } : {};
          if (vr.structural_verification) {
            vr.structural_verification = {
              ...vr.structural_verification,
              unresolved_count: 0,
              status: 'healed',
              issues: (vr.structural_verification.issues || []).map((issue) => {
                const healed = issue.healed;
                const status = issue.status;
                if (!healed && status !== 'dismissed' && status !== 'verified_by_agent') {
                  return { ...issue, status: 'dismissed' };
                }
                return issue;
              }),
            };
          }
          return { ...prev, validation_report: vr };
        });
        setValidationReport((prev) => {
          if (!prev?.structural_verification) return prev;
          return {
            ...prev,
            structural_verification: {
              ...prev.structural_verification,
              unresolved_count: 0,
              status: 'healed',
              issues: (prev.structural_verification.issues || []).map((issue) => {
                if (!issue.healed && issue.status !== 'dismissed' && issue.status !== 'verified_by_agent') {
                  return { ...issue, status: 'dismissed' };
                }
                return issue;
              }),
            },
          };
        });
      }
    } catch {
      // dismiss-all is best-effort; approval still takes effect
    }
  }, [questions, importId]);

  const handlePrev = () => {
    setEditMode(false);
    setEditTarget(null);
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setEditMode(false);
    setEditTarget(null);
    setCurrentIndex((prev) => Math.min(prev + 1, questions.length - 1));
  };

  const normalizeDifficulty = (d) => {
    const map = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };
    return map[(d || '').toLowerCase()] || 'Medium';
  };

  // Start editing a standalone question
  const startEdit = () => {
    setValidationErrors({});
    setEditDraft({
      ...currentQuestion,
      default_marks: currentQuestion.default_marks || 1,
      difficulty: normalizeDifficulty(currentQuestion.difficulty),
    });
    setEditTarget({ type: "standalone" });
    setEditMode(true);
    setEditLivePreview(true);
  };

  // Start editing the stem of a grouped question
  const startEditStem = (parentIndex) => {
    setValidationErrors({});
    const p = questions[parentIndex];
    setEditDraft({
      question_text: p.question_text,
      paper_number: p.paper_number ?? null,
      section_title: p.section_title ?? null,
      section_instructions: p.section_instructions ?? null,
      image_url: p.image_url ?? null,
      image_urls: p.image_urls ?? null,
      image_description: p.image_description ?? null,
      passage_type: p.passage_type ?? null,
    });
    setEditTarget({ type: "stem", parentIndex });
    setEditMode(true);
    setEditLivePreview(true);
  };

  // Start editing a sub-question
  const startEditSubQuestion = (parentIndex, subIndex) => {
    setValidationErrors({});
    const sq = questions[parentIndex].sub_questions[subIndex];
    const draft = JSON.parse(JSON.stringify({ ...sq, default_marks: sq.default_marks || 1 }));
    draft.difficulty = normalizeDifficulty(draft.difficulty);
    setEditDraft(draft);
    setEditTarget({ type: "subquestion", parentIndex, subIndex });
    setEditMode(true);
    setEditLivePreview(true);
  };

  const addSubQuestionAt = (parentIndex) => {
    const newSub = {
      question_text: "(Enter sub-question text)",
      type: "short-answer",
      difficulty: "Medium",
      default_marks: 1,
      topic: null,
      answer_data: { correct_answer_text: "" },
      paper_number: null,
      section_title: null,
      section_instructions: null,
      question_subtype: null,
      image_url: null,
      image_urls: null,
      image_description: null,
      explanation: null,
      options: [],
    };
    const newSubIndex = (questions[parentIndex].sub_questions || []).length;
    setQuestions((prev) => {
      const next = [...prev];
      next[parentIndex] = {
        ...next[parentIndex],
        sub_questions: [...(next[parentIndex].sub_questions || []), newSub],
      };
      return next;
    });
    setModifiedQuestions((prev) => new Set([...prev, parentIndex]));
    setEditDraft(JSON.parse(JSON.stringify(newSub)));
    setEditTarget({ type: "subquestion", parentIndex, subIndex: newSubIndex });
    setEditMode(true);
    setEditLivePreview(true);
  };

  const addSubQuestionToCurrentQuestion = () => {
    const q = questions[currentIndex];
    if (q.type === "grouped") {
      addSubQuestionAt(currentIndex);
      return;
    }
    const newSub = {
      question_text: "(Enter sub-question text)",
      type: "short-answer",
      difficulty: q.difficulty || "Medium",
      default_marks: q.default_marks || 1,
      topic: null,
      answer_data: { correct_answer_text: "" },
      paper_number: null,
      section_title: null,
      section_instructions: null,
      question_subtype: null,
      image_url: null,
      image_urls: null,
      image_description: null,
      explanation: null,
      options: [],
    };
    setQuestions((prev) => {
      const next = [...prev];
      next[currentIndex] = {
        ...next[currentIndex],
        type: "grouped",
        sub_questions: [newSub],
      };
      return next;
    });
    setModifiedQuestions((prev) => new Set([...prev, currentIndex]));
    setEditDraft(JSON.parse(JSON.stringify(newSub)));
    setEditTarget({ type: "subquestion", parentIndex: currentIndex, subIndex: 0 });
    setEditMode(true);
    setEditLivePreview(true);
  };

  const deleteSubQuestion = (parentIndex, subIndex) => {
    if (!window.confirm("Remove this sub-question?")) return;
    setQuestions((prev) => {
      const next = [...prev];
      const subs = [...(next[parentIndex].sub_questions || [])];
      subs.splice(subIndex, 1);
      next[parentIndex] = { ...next[parentIndex], sub_questions: subs };
      return next;
    });
    setModifiedQuestions((prev) => new Set([...prev, parentIndex]));
    if (editTarget?.type === "subquestion" && editTarget.parentIndex === parentIndex) {
      cancelEdit();
    }
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditDraft(null);
    setEditTarget(null);
    setValidationErrors({});
    setEditLivePreview(true);
  };

  const findQbIdForEdit = useCallback((target, parentIdx, subIdx) => {
    const rootPanelQs = imagesPanelQuestions.filter((q) => !q.parent_question_id);

    const findRootQbId = (questionIndex) => {
      const q = questions[questionIndex];
      if (!q) return null;
      // Match by paper_number first
      if (q.paper_number != null) {
        const match = rootPanelQs.find((p) => String(p.paper_number) === String(q.paper_number));
        if (match) return match.id;
      }
      // Positional fallback
      const rootIdx = questions.slice(0, questionIndex).filter((x) => !x.parent_question_id).length;
      return rootPanelQs[rootIdx]?.id ?? null;
    };

    if (target?.type === "subquestion") {
      const parentQbId = findRootQbId(parentIdx);
      if (parentQbId == null) return null;
      const childPanelQs = imagesPanelQuestions.filter(
        (p) => p.parent_question_id === parentQbId
      );
      return childPanelQs[subIdx]?.id ?? null;
    }
    if (target?.type === "stem") {
      return findRootQbId(parentIdx);
    }
    return findRootQbId(parentIdx ?? 0);
  }, [imagesPanelQuestions, questions]);

  const saveEdit = async () => {
    // For stem edits, skip full validation
    if (editTarget?.type !== "stem") {
      const validation = validateQuestion(editDraft);
      if (!validation.valid) {
        setValidationErrors(validation.errors);
        toast.error("Please fix validation errors before saving");
        return;
      }
    }
    setValidationErrors({});

    // For choice-based types, sync answer_data.correct_options from options[].is_correct
    // so that the stored answer record always matches what the user sees in the UI.
    let draftToSave = { ...editDraft };
    if (["fill-in-the-blank", "multi-fill"].includes(draftToSave.type)) {
      const blanksLen = draftToSave.answer_data?.correct_blanks?.length
        ?? (Array.isArray(draftToSave.blanks) ? draftToSave.blanks.length : 0);
      draftToSave.type = blanksLen >= 2 ? "multi-fill" : "fill-in-the-blank";
    }
    if (
      ["multiple-choice", "multiple-select", "true-false"].includes(draftToSave.type) &&
      Array.isArray(draftToSave.options)
    ) {
      const correctIndices = draftToSave.options
        .map((opt, i) => (opt.is_correct ? i : -1))
        .filter((i) => i >= 0);
      draftToSave = {
        ...draftToSave,
        answer_data: {
          ...(draftToSave.answer_data || {}),
          correct_options: correctIndices,
        },
      };
    }

    const updated = [...questions];

    if (editTarget?.type === "subquestion") {
      const { parentIndex, subIndex } = editTarget;
      const subQuestions = [...updated[parentIndex].sub_questions];
      subQuestions[subIndex] = { ...draftToSave };
      updated[parentIndex] = { ...updated[parentIndex], sub_questions: subQuestions };
      setModifiedQuestions((prev) => new Set([...prev, parentIndex]));
    } else if (editTarget?.type === "stem") {
      const { parentIndex } = editTarget;
      const inferredPassageType = draftToSave.passage_type
        ?? (isEnglishSubjectLabel(examSubject) && (draftToSave.question_text || "").replace(/<[^>]*>/g, "").trim().length > 200
          ? "comprehension"
          : null);
      updated[parentIndex] = {
        ...updated[parentIndex],
        question_text: draftToSave.question_text,
        paper_number: draftToSave.paper_number ?? null,
        section_title: draftToSave.section_title ?? null,
        section_instructions: draftToSave.section_instructions ?? null,
        image_url: draftToSave.image_url ?? null,
        image_urls: draftToSave.image_urls ?? null,
        image_description: draftToSave.image_description ?? null,
        passage_type: inferredPassageType,
      };
      setModifiedQuestions((prev) => new Set([...prev, parentIndex]));
    } else {
      // standalone
      const isModified = JSON.stringify(draftToSave) !== JSON.stringify(originalQuestions[currentIndex]);
      if (isModified) {
        setModifiedQuestions((prev) => new Set([...prev, currentIndex]));
      }
      updated[currentIndex] = { ...draftToSave };
    }

    setQuestions(updated);
    setEditMode(false);
    setEditDraft(null);
    setEditTarget(null);
    toast.success("Question updated");

    // If import is already confirmed, also push the change to the question bank
    if (importData?.has_imported_questions) {
      const parentIdx = editTarget?.type === "subquestion" || editTarget?.type === "stem"
        ? editTarget.parentIndex
        : currentIndex;
      const subIdx = editTarget?.type === "subquestion" ? editTarget.subIndex : undefined;
      const qbId = findQbIdForEdit(editTarget, parentIdx, subIdx);

      if (qbId != null) {
        // For stem edits send just the updated parent question; for subquestion send the sub draft;
        // for standalone send the full draft.
        const payload = editTarget?.type === "stem"
          ? {
              question_text: draftToSave.question_text,
              paper_number: draftToSave.paper_number,
              section_title: draftToSave.section_title,
              section_instructions: draftToSave.section_instructions,
              image_url: draftToSave.image_url,
              image_urls: draftToSave.image_urls,
              image_description: draftToSave.image_description,
            }
          : draftToSave;

        try {
          await api.patch(`/admin/question-bank/import/${importId}/question/${qbId}`, payload);
          toast.success("Question bank updated");
        } catch (err) {
          toast.error(err.response?.data?.message || "Failed to update question bank");
        }
      }
    }

    await persistDraftAndSyncPreview(updated);
  };

  const undoEdit = useCallback(() => {
    const applyDraft = (q) => {
      const d = JSON.parse(JSON.stringify(q));
      d.difficulty = normalizeDifficulty(d.difficulty);
      setEditDraft(d);
    };
    if (editTarget?.type === "subquestion") {
      const { parentIndex, subIndex } = editTarget;
      applyDraft(questions[parentIndex].sub_questions[subIndex]);
    } else {
      applyDraft(questions[currentIndex]);
    }
    setValidationErrors({});
    toast.info("Changes reverted");
  }, [currentIndex, editTarget, questions]);

  const autoSaveDraft = useCallback(async () => {
    // Without this guard an idle tab republishes its load-time copy every 30s,
    // silently reverting edits made anywhere else (other tabs, server-side fixes).
    const snapshot = JSON.stringify(questions);
    if (snapshot === lastSavedSnapshotRef.current) return;
    try {
      setAutoSaving(true);
      const res = await api.patch(`/admin/question-bank/import/${importId}/draft`, {
        parsed_questions: questions,
        base_updated_at: baseUpdatedAtRef.current,
      });
      lastSavedSnapshotRef.current = snapshot;
      baseUpdatedAtRef.current = res.data?.data?.updated_at ?? baseUpdatedAtRef.current;
      setLastSaved(new Date());
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error(
          error.response.data?.message ||
            "This import changed elsewhere. Reload before saving."
        );
      } else {
        console.error("Auto-save failed:", error);
      }
    } finally {
      setAutoSaving(false);
    }
  }, [importId, questions]);

  const persistDraftAndSyncPreview = useCallback(async (parsedSnapshot) => {
    try {
      setAutoSaving(true);
      const draftRes = await api.patch(`/admin/question-bank/import/${importId}/draft`, {
        parsed_questions: parsedSnapshot,
        base_updated_at: baseUpdatedAtRef.current,
      });
      lastSavedSnapshotRef.current = JSON.stringify(parsedSnapshot);
      baseUpdatedAtRef.current = draftRes.data?.data?.updated_at ?? baseUpdatedAtRef.current;
      setLastSaved(new Date());
      const res = await api.get(`/admin/question-bank/import/${importId}/preview`);
      const data = res.data?.data;
      if (data) {
        setImportData((prev) => ({
          ...(prev || {}),
          markdown_parity: data.markdown_parity,
          validation_report: data.validation_report,
          quality_report: data.quality_report ?? prev?.quality_report,
          accuracy_metrics: data.accuracy_metrics ?? prev?.accuracy_metrics,
        }));
        if (data.validation_report != null) setValidationReport(data.validation_report);
        if (data.quality_report != null) setQualityReport(data.quality_report);
        if (data.accuracy_metrics != null) setAccuracyMetrics(data.accuracy_metrics);
      }
      return data ?? null;
    } catch (error) {
      console.error("Persist draft / preview sync failed:", error);
      return null;
    } finally {
      setAutoSaving(false);
    }
  }, [importId]);

  const commitHeaderPaperNumber = useCallback(() => {
    const value = parseHeaderPaperInputValue(headerPaperDraft);
    setHeaderPaperDraft(value != null && value !== "" ? String(value) : "");

    if (editMode && editTarget?.type === "subquestion") {
      const { parentIndex, subIndex } = editTarget;
      const next = questions.map((q, pi) => {
        if (pi !== parentIndex) return q;
        const subs = [...(q.sub_questions || [])];
        if (!subs[subIndex]) return q;
        subs[subIndex] = { ...subs[subIndex], paper_number: value };
        return { ...q, sub_questions: subs };
      });
      setEditDraft((d) => (d ? { ...d, paper_number: value } : d));
      setQuestions(next);
      setModifiedQuestions((prev) => new Set([...prev, parentIndex]));
      void persistDraftAndSyncPreview(next);
      return;
    }

    const next = questions.map((q, idx) =>
      idx === currentIndex ? { ...q, paper_number: value } : q
    );
    setQuestions(next);
    if (editMode && (editTarget?.type === "stem" || editTarget?.type === "standalone")) {
      setEditDraft((d) => (d ? { ...d, paper_number: value } : d));
    }
    setModifiedQuestions((prev) => new Set([...prev, currentIndex]));
    void persistDraftAndSyncPreview(next);
  }, [headerPaperDraft, questions, currentIndex, persistDraftAndSyncPreview, editMode, editTarget]);

  const insertManualQuestionAfter = useCallback(
    (afterIdx) => {
      const insertAt = afterIdx + 1;
      const skeleton = buildManualRootQuestionSkeleton(questions, importData?.markdown_parity);
      const merged = [...questions.slice(0, insertAt), skeleton, ...questions.slice(insertAt)];
      const norm = normalizeQuestionsForReview(merged, { skipSort: true });
      setQuestions(norm);
      setApprovedSet((s) => remapIndexSetForInsert(s, insertAt));
      setSelectedQuestions((s) => remapIndexSetForInsert(s, insertAt));
      setModifiedQuestions((s) => {
        const next = remapIndexSetForInsert(s, insertAt);
        next.add(insertAt);
        return next;
      });
      const inserted = norm[insertAt];
      if (inserted?.import_question_ref) {
        extractionBaselineRef.current.set(
          inserted.import_question_ref,
          JSON.parse(JSON.stringify(inserted))
        );
      }
      setOriginalQuestions((o) => {
        const row = JSON.parse(JSON.stringify(inserted));
        return [...o.slice(0, insertAt), row, ...o.slice(insertAt)];
      });
      setCurrentIndex(insertAt);
      setEditMode(false);
      setEditDraft(null);
      setEditTarget(null);
      toast.success("Question added — edit and approve when ready");
      void persistDraftAndSyncPreview(norm);
    },
    [questions, importData?.markdown_parity, persistDraftAndSyncPreview]
  );

  const insertManualQuestionBefore = useCallback(
    (beforeIdx) => {
      const skeleton = buildManualRootQuestionSkeleton(questions, importData?.markdown_parity);
      const merged = [...questions.slice(0, beforeIdx), skeleton, ...questions.slice(beforeIdx)];
      const norm = normalizeQuestionsForReview(merged, { skipSort: true });
      setQuestions(norm);
      setApprovedSet((s) => remapIndexSetForInsert(s, beforeIdx));
      setSelectedQuestions((s) => remapIndexSetForInsert(s, beforeIdx));
      setModifiedQuestions((s) => {
        const next = remapIndexSetForInsert(s, beforeIdx);
        next.add(beforeIdx);
        return next;
      });
      const inserted = norm[beforeIdx];
      if (inserted?.import_question_ref) {
        extractionBaselineRef.current.set(
          inserted.import_question_ref,
          JSON.parse(JSON.stringify(inserted))
        );
      }
      setOriginalQuestions((o) => {
        const row = JSON.parse(JSON.stringify(inserted));
        return [...o.slice(0, beforeIdx), row, ...o.slice(beforeIdx)];
      });
      setCurrentIndex(beforeIdx);
      setEditMode(false);
      setEditDraft(null);
      setEditTarget(null);
      toast.success("Question added — edit and approve when ready");
      void persistDraftAndSyncPreview(norm);
    },
    [questions, importData?.markdown_parity, persistDraftAndSyncPreview]
  );

  const insertComprehensionPassageAfter = useCallback(
    (afterIdx) => {
      const insertAt = afterIdx + 1;
      const skeleton = buildComprehensionPassageSkeleton(questions, importData?.markdown_parity);
      const merged = [...questions.slice(0, insertAt), skeleton, ...questions.slice(insertAt)];
      const norm = normalizeQuestionsForReview(merged, { skipSort: true });
      setQuestions(norm);
      setApprovedSet((s) => remapIndexSetForInsert(s, insertAt));
      setSelectedQuestions((s) => remapIndexSetForInsert(s, insertAt));
      setModifiedQuestions((s) => {
        const next = remapIndexSetForInsert(s, insertAt);
        next.add(insertAt);
        return next;
      });
      const inserted = norm[insertAt];
      if (inserted?.import_question_ref) {
        extractionBaselineRef.current.set(
          inserted.import_question_ref,
          JSON.parse(JSON.stringify(inserted))
        );
      }
      setOriginalQuestions((o) => {
        const row = JSON.parse(JSON.stringify(inserted));
        return [...o.slice(0, insertAt), row, ...o.slice(insertAt)];
      });
      setCurrentIndex(insertAt);
      // Immediately open the stem (passage) editor so the admin can paste the text
      setValidationErrors({});
      setEditDraft({
        question_text: "",
        paper_number: skeleton.paper_number ?? null,
        section_title: null,
        section_instructions: null,
        image_url: null,
        image_urls: null,
        image_description: null,
        passage_type: "comprehension",
      });
      setEditTarget({ type: "stem", parentIndex: insertAt });
      setEditMode(true);
      setEditLivePreview(true);
      void persistDraftAndSyncPreview(norm);
    },
    [questions, importData?.markdown_parity, persistDraftAndSyncPreview]
  );

  const renumberQuestionsInSection = useCallback(() => {
    if (!questions.length) return;
    const currentSection = sectionBlockKey(questions[currentIndex]);
    let counter = 1;
    const next = questions.map((q) => {
      if (sectionBlockKey(q) === currentSection && currentSection !== " ") {
        const updated = { ...q, paper_number: counter++ };
        if (q.type === "grouped" && q.sub_questions?.length > 0) {
          return { ...updated, sub_questions: q.sub_questions };
        }
        return updated;
      }
      return q;
    });
    setQuestions(next);
    setModifiedQuestions((s) => {
      const ns = new Set(s);
      next.forEach((_, i) => {
        if (sectionBlockKey(next[i]) === currentSection && currentSection !== " ") ns.add(i);
      });
      return ns;
    });
    void persistDraftAndSyncPreview(next);
    toast.success("Renumbered questions in this section");
  }, [questions, currentIndex, persistDraftAndSyncPreview]);

  const renumberAllQuestions = useCallback(() => {
    if (!questions.length) return;
    const next = questions.map((q, i) => ({ ...q, paper_number: i + 1 }));
    setQuestions(next);
    setModifiedQuestions((s) => {
      const ns = new Set(s);
      next.forEach((_, i) => ns.add(i));
      return ns;
    });
    void persistDraftAndSyncPreview(next);
    toast.success("Renumbered all questions 1…" + next.length);
  }, [questions, persistDraftAndSyncPreview]);

  const applyCurrentSectionToFollowing = useCallback(() => {
    if (!editDraft) return;
    const st = editDraft.section_title ?? null;
    const si = editDraft.section_instructions ?? null;
    const next = questions.map((q, i) => {
      if (i < currentIndex) return q;
      return mergeSectionFieldsOntoQuestion(q, st, si);
    });
    setQuestions(next);
    setModifiedQuestions((s) => {
      const ns = new Set(s);
      for (let i = currentIndex; i < next.length; i++) ns.add(i);
      return ns;
    });
    void persistDraftAndSyncPreview(next);
    toast.success(`Section applied to questions ${currentIndex + 1}–${next.length}`);
  }, [editDraft, questions, currentIndex, persistDraftAndSyncPreview]);

  const deleteRootQuestionAt = useCallback(
    (idx) => {
      if (questions.length <= 1) {
        toast.error("Cannot delete the only question");
        return;
      }
      if (!window.confirm("Delete this question from the import draft?")) return;
      const next = questions.filter((_, i) => i !== idx);
      const norm = normalizeQuestionsForReview(next);
      setQuestions(norm);
      setApprovedSet((s) => remapIndexSetForDelete(s, idx));
      setSelectedQuestions((s) => remapIndexSetForDelete(s, idx));
      setModifiedQuestions((s) => remapIndexSetForDelete(s, idx));
      setOriginalQuestions((o) => o.filter((_, i) => i !== idx));
      const removedRef = questions[idx]?.import_question_ref;
      if (removedRef) extractionBaselineRef.current.delete(removedRef);
      setCurrentIndex((ci) => {
        if (ci === idx) return Math.min(idx, norm.length - 1);
        if (ci > idx) return ci - 1;
        return ci;
      });
      setEditMode(false);
      setEditDraft(null);
      setEditTarget(null);
      toast.success("Question removed");
      void persistDraftAndSyncPreview(norm);
    },
    [questions, persistDraftAndSyncPreview]
  );

  const resetQuestion = useCallback((path = null) => {
    const target =
      path ||
      (editTarget?.type === "subquestion"
        ? editTarget
        : editTarget?.type === "stem"
          ? editTarget
          : { type: "standalone", index: currentIndex });

    const parentIdx =
      target.type === "subquestion" || target.type === "stem"
        ? target.parentIndex
        : target.index ?? currentIndex;

    const parent = questions[parentIdx];
    if (!parent) return;

    const isSubReset = target.type === "subquestion";
    const isStemReset = target.type === "stem";
    const confirmMsg = isSubReset
      ? "Reset this sub-question to its original parsed state? All edits will be lost."
      : isStemReset
        ? "Reset this passage/stem to its original parsed state? All edits will be lost."
        : "Reset this question to its original parsed state? All edits will be lost.";
    if (!window.confirm(confirmMsg)) return;

    const updated = structuredClone(questions);

    if (isSubReset) {
      const { subIndex } = target;
      const sq = parent.sub_questions?.[subIndex];
      const original = lookupBaselineQuestion(
        sq?.import_question_ref,
        originalQuestions[parentIdx]?.sub_questions?.[subIndex]
      );
      if (!original) {
        toast.error("No original found for this sub-question");
        return;
      }
      updated[parentIdx].sub_questions[subIndex] = original;
      setModifiedQuestions((prev) => new Set([...prev, parentIdx]));
    } else if (isStemReset) {
      const original = lookupBaselineQuestion(
        parent.import_question_ref,
        originalQuestions[parentIdx]
      );
      if (!original) {
        toast.error("No original found for this passage/stem");
        return;
      }
      updated[parentIdx] = {
        ...updated[parentIdx],
        question_text: original.question_text,
        paper_number: original.paper_number,
        section_title: original.section_title,
        section_instructions: original.section_instructions,
        image_url: original.image_url,
        image_urls: original.image_urls,
        image_description: original.image_description,
      };
      setModifiedQuestions((prev) => new Set([...prev, parentIdx]));
    } else {
      const original = lookupBaselineQuestion(
        parent.import_question_ref,
        originalQuestions[parentIdx]
      );
      if (!original) {
        toast.error("No original found for this question");
        return;
      }
      updated[parentIdx] = original;
      setModifiedQuestions((prev) => {
        const next = new Set(prev);
        next.delete(parentIdx);
        return next;
      });
    }

    setQuestions(updated);
    setEditMode(false);
    setEditDraft(null);
    setEditTarget(null);
    toast.success(isSubReset ? "Sub-question reset to original" : "Question reset to original");
    void persistDraftAndSyncPreview(updated);
  }, [
    currentIndex,
    editTarget,
    lookupBaselineQuestion,
    originalQuestions,
    questions,
    persistDraftAndSyncPreview,
  ]);

  const clearSectionOnCurrent = useCallback(() => {
    const next = questions.map((q, i) =>
      i === currentIndex ? mergeSectionFieldsOntoQuestion(q, null, null) : q
    );
    setQuestions(next);
    setModifiedQuestions((prev) => new Set([...prev, currentIndex]));
    if (editMode && editDraft) {
      setEditDraft((d) => d ? { ...d, section_title: null, section_instructions: null } : d);
    }
    toast.success("Section removed from this question");
    void persistDraftAndSyncPreview(next);
  }, [currentIndex, questions, editMode, editDraft, persistDraftAndSyncPreview]);

  const resetAllToOriginal = useCallback(() => {
    if (!window.confirm("Reset ALL questions to their original parsed state? All manual edits will be lost.")) return;
    const next = originalQuestions.map((q) => JSON.parse(JSON.stringify(q)));
    const norm = normalizeQuestionsForReview(next);
    setQuestions(norm);
    setModifiedQuestions(new Set());
    setApprovedSet(new Set());
    setEditMode(false);
    setEditDraft(null);
    setEditTarget(null);
    toast.success("All questions reset to original");
    void persistDraftAndSyncPreview(norm);
  }, [originalQuestions, persistDraftAndSyncPreview]);

  const clearAllSections = useCallback(() => {
    if (!window.confirm("Remove section title and instructions from ALL questions?")) return;
    const next = questions.map((q) => mergeSectionFieldsOntoQuestion(q, null, null));
    setQuestions(next);
    setModifiedQuestions((prev) => {
      const ns = new Set(prev);
      next.forEach((_, i) => ns.add(i));
      return ns;
    });
    toast.success("All sections cleared");
    void persistDraftAndSyncPreview(next);
  }, [questions, persistDraftAndSyncPreview]);

  const handleListDragStart = useCallback((e, idx) => {
    dragSrcIdx.current = idx;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  }, []);

  const handleListDragOver = useCallback((e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIdx(idx);
  }, []);

  const handleListDrop = useCallback((e, toIdx) => {
    e.preventDefault();
    setDragOverIdx(null);
    const fromIdx = dragSrcIdx.current;
    if (fromIdx == null || fromIdx === toIdx) return;
    dragSrcIdx.current = null;

    const sourceSection = sectionBlockKey(questions[fromIdx]);
    const sourceIsSection = sourceSection !== "\u0000";

    let moveStart = fromIdx;
    let moveEnd = fromIdx;

    if (sourceIsSection) {
      while (moveStart > 0 && sectionBlockKey(questions[moveStart - 1]) === sourceSection) moveStart--;
      while (moveEnd < questions.length - 1 && sectionBlockKey(questions[moveEnd + 1]) === sourceSection) moveEnd++;
    }

    if (toIdx >= moveStart && toIdx <= moveEnd) return;

    let insertAt = toIdx;
    if (sourceIsSection) {
      const targetSection = sectionBlockKey(questions[toIdx]);
      if (targetSection !== "\u0000") {
        let targetSectionStart = toIdx;
        while (
          targetSectionStart > 0 &&
          sectionBlockKey(questions[targetSectionStart - 1]) === targetSection
        ) {
          targetSectionStart--;
        }
        insertAt = targetSectionStart;
      }
    }

    const next = [...questions];
    const moved = next.splice(moveStart, moveEnd - moveStart + 1);
    const insertAfterRemoval = insertAt > moveEnd ? insertAt - moved.length : insertAt;
    next.splice(insertAfterRemoval, 0, ...moved);

    const shouldRenumberAll =
      next.length > 0 &&
      next.every((q) => {
        const pn = q?.paper_number;
        return pn == null || String(pn).trim() === "" || /^\d+$/.test(String(pn).trim());
      });

    const reordered = shouldRenumberAll
      ? next.map((q, idx) => ({ ...q, paper_number: idx + 1 }))
      : next;

    const norm = normalizeQuestionsForReview(reordered, { skipSort: true });
    setQuestions(norm);
    setCurrentIndex(insertAfterRemoval);
    setApprovedSet((prev) => remapIndexSetForMoveRange(prev, moveStart, moveEnd, insertAt));
    setSelectedQuestions((prev) => remapIndexSetForMoveRange(prev, moveStart, moveEnd, insertAt));
    setModifiedQuestions((prev) => {
      const ns = remapIndexSetForMoveRange(prev, moveStart, moveEnd, insertAt);
      for (let i = 0; i < moved.length; i++) ns.add(insertAfterRemoval + i);
      return ns;
    });
    void persistDraftAndSyncPreview(norm);
  }, [questions, persistDraftAndSyncPreview]);

  const handleListDragEnd = useCallback(() => {
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  }, []);

  const commitListPaperNum = useCallback((idx, raw) => {
    setListEditPaperNum(null);
    const t = String(raw ?? "").trim();
    const value = t === "" ? null : (/^\d+$/.test(t) ? parseInt(t, 10) : t);
    const next = questions.map((q, i) => i === idx ? { ...q, paper_number: value } : q);
    setQuestions(next);
    setModifiedQuestions((prev) => new Set([...prev, idx]));
    void persistDraftAndSyncPreview(next);
  }, [questions, persistDraftAndSyncPreview]);

  const commitListSectionEdit = useCallback((sectionKey, newTitle, newInstructions) => {
    setListEditSection(null);
    const st = newTitle?.trim() || null;
    const si = newInstructions?.trim() || null;
    const next = questions.map((q) =>
      sectionBlockKey(q) === sectionKey ? mergeSectionFieldsOntoQuestion(q, st, si) : q
    );
    setQuestions(next);
    setModifiedQuestions((prev) => {
      const ns = new Set(prev);
      next.forEach((_, i) => { if (sectionBlockKey(questions[i]) === sectionKey) ns.add(i); });
      return ns;
    });
    toast.success("Section updated");
    void persistDraftAndSyncPreview(next);
  }, [questions, persistDraftAndSyncPreview]);

  const handleLogoUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await api.post("/admin/question-bank/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.url || res.data?.data?.url;
      if (url) {
        setEditExamMetaValues((prev) => ({ ...prev, school_logo: url }));
        toast.success("Logo uploaded");
      } else {
        toast.error("Upload succeeded but no URL returned");
      }
    } catch {
      toast.error("Logo upload failed");
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  }, []);

  const openEditExamMeta = useCallback(() => {
    setEditExamMetaValues({
      school_name: schoolName,
      school_logo: schoolLogo,
      exam_subject: examSubject,
      exam_duration: examDuration,
      exam_section: examSection,
      exam_title: examTitle,
      exam_instructions: examInstructions,
      auto_number_instructions: autoNumberInstructions,
      auto_number_questions: autoNumberQuestions,
    });
    setEditExamMetaOpen(true);
  }, [schoolName, schoolLogo, examSubject, examDuration, examSection, examTitle, examInstructions, autoNumberInstructions, autoNumberQuestions]);

  const saveExamMeta = useCallback(async () => {
    setSavingExamMeta(true);
    try {
      await api.patch(`/admin/question-bank/import/${importId}/exam-metadata`, editExamMetaValues);
      setSchoolName(editExamMetaValues.school_name);
      setSchoolLogo(editExamMetaValues.school_logo);
      setExamSubject(editExamMetaValues.exam_subject);
      setExamDuration(editExamMetaValues.exam_duration);
      setExamSection(editExamMetaValues.exam_section);
      setExamTitle(editExamMetaValues.exam_title);
      setExamInstructions(editExamMetaValues.exam_instructions);
      setAutoNumberInstructions(editExamMetaValues.auto_number_instructions);
      setAutoNumberQuestions(editExamMetaValues.auto_number_questions);
      setEditExamMetaOpen(false);
      toast.success("Exam metadata updated");
    } catch (error) {
      console.error("Failed to save exam metadata:", error);
      toast.error("Failed to save exam metadata");
    } finally {
      setSavingExamMeta(false);
    }
  }, [importId, editExamMetaValues]);

  // Reload questions from the /preview endpoint (used after Apply Updates or Rescan completes)
  const reloadPreview = useCallback(async ({ refreshBaseline = false } = {}) => {
    try {
      const res = await api.get(`/admin/question-bank/import/${importId}/preview`);
      const data = res.data?.data;
      if (data?.parsed_questions) {
        const normalizedQuestions = normalizeQuestionsForReview(data.parsed_questions);
        setQuestions(normalizedQuestions);
        if (refreshBaseline) {
          captureExtractionBaseline(normalizedQuestions);
        }
      }
      if (data?.supplementary_booklet_url !== undefined) {
        setSupplementaryBookletUrl(data.supplementary_booklet_url || null);
      }
    } catch {
      // silent — the update happened, just the reload failed
    }
  }, [importId, captureExtractionBaseline]);

  // ── Verification / agent-fix: bounded polling (replaces fixed setTimeout refreshes) ──
  const POLL_INTERVAL_MS = 2500;
  const POLL_MAX_MS_DEFAULT = 120000;
  const STABLE_ROUNDS = 3;

  const importFlowAbortRef = useRef(null);

  const startImportFlow = useCallback(() => {
    importFlowAbortRef.current?.abort();
    const ac = new AbortController();
    importFlowAbortRef.current = ac;
    return ac;
  }, []);

  useEffect(
    () => () => {
      importFlowAbortRef.current?.abort();
    },
    []
  );

  /** Apply a /preview payload to review UI state (used by poll loops). */
  const applyPreviewToReviewState = useCallback((data, { refreshBaseline = false } = {}) => {
    if (!data) return;
    if (data.parsed_questions) {
      const normalized = normalizeQuestionsForReview(data.parsed_questions);
      setQuestions(normalized);
      if (refreshBaseline) {
        captureExtractionBaseline(normalized);
      }
    }
    if (data.validation_report !== undefined) setValidationReport(data.validation_report);
    if (data.quality_report !== undefined) setQualityReport(data.quality_report);
    if (data.accuracy_metrics !== undefined) setAccuracyMetrics(data.accuracy_metrics);
    if (data.supplementary_booklet_url !== undefined) {
      setSupplementaryBookletUrl(data.supplementary_booklet_url || null);
    }
    setImportData((prev) => (prev && data ? { ...prev, ...data } : data ?? prev));
  }, [captureExtractionBaseline]);

  /**
   * Poll GET /preview until structural_verification stabilizes after a baseline snapshot,
   * or unresolved_count hits 0, or maxMs elapses.
   */
  const pollUntilStructuralReportSettles = useCallback(
    async (signal, baselineStructuralVerification, options = {}) => {
      const maxMs = options.maxMs ?? POLL_MAX_MS_DEFAULT;
      const baselineStr = JSON.stringify(baselineStructuralVerification ?? null);
      const t0 = Date.now();
      let lastSvStr = null;
      let stableCount = 0;
      let seenDifferentFromBaseline = false;
      let lastPreviewData = null;

      while (Date.now() - t0 < maxMs) {
        if (signal?.aborted) {
          return { ok: false, reason: "aborted", lastPreviewData };
        }
        await new Promise((resolve) => {
          setTimeout(resolve, POLL_INTERVAL_MS);
        });
        if (signal?.aborted) {
          return { ok: false, reason: "aborted", lastPreviewData };
        }
        try {
          const res = await api.get(`/admin/question-bank/import/${importId}/preview`);
          const data = res.data?.data;
          if (!data) continue;
          lastPreviewData = data;
          applyPreviewToReviewState(data);
          const sv = data.validation_report?.structural_verification;
          const u = sv?.unresolved_count;
          if (typeof u === "number" && u === 0) {
            return { ok: true, reason: "zero_unresolved", lastPreviewData };
          }
          const svStr = JSON.stringify(sv ?? null);
          if (svStr !== baselineStr) {
            seenDifferentFromBaseline = true;
          }
          if (seenDifferentFromBaseline) {
            if (svStr === lastSvStr) {
              stableCount += 1;
            } else {
              stableCount = 0;
            }
            lastSvStr = svStr;
            if (stableCount >= STABLE_ROUNDS) {
              return { ok: true, reason: "stable", lastPreviewData };
            }
          }
        } catch {
          /* keep polling */
        }
      }
      return { ok: false, reason: "timeout", lastPreviewData };
    },
    [importId, applyPreviewToReviewState]
  );

  /** Agent fix then optional verify pass if issues remain — shared by button and auto-clean. */
  const runAgentFixWithFollowUp = useCallback(
    async (signal, baselineBeforeAgent) => {
      const res = await api.post(`/admin/question-bank/import/${importId}/agent-fix`);
      const data = res.data?.data;
      if (signal?.aborted) return;

      if (data?.queued) {
        const initialDelay = typeof data.poll_interval_ms === "number" ? data.poll_interval_ms : 3000;
        const maxPollMs = typeof data.poll_max_ms === "number" ? data.poll_max_ms : POLL_MAX_MS_DEFAULT;
        await new Promise((resolve) => {
          setTimeout(resolve, initialDelay);
        });
        if (signal?.aborted) return;
        toast.info("Agent fix is running — updating…");
        await pollUntilStructuralReportSettles(signal, baselineBeforeAgent, { maxMs: maxPollMs });
      } else {
        const actions = data?.actions || [];
        const patched = actions.filter((a) => a.action === "patch").length;
        const dismissed = actions.filter((a) => a.action === "dismiss").length;
        const skipped = actions.filter((a) => a.action === "skip").length;
        if (patched + dismissed === 0 && skipped === 0) {
          toast.info(data?.message || "No unresolved issues to fix.");
          try {
            const previewRes = await api.get(`/admin/question-bank/import/${importId}/preview`);
            applyPreviewToReviewState(previewRes.data?.data);
          } catch {
            /* ignore */
          }
          return;
        }
        toast.success(
          `Agent fix complete: ${patched} patched, ${dismissed} dismissed${skipped ? `, ${skipped} skipped` : ""}.`
        );
        try {
          const previewRes = await api.get(`/admin/question-bank/import/${importId}/preview`);
          applyPreviewToReviewState(previewRes.data?.data);
        } catch {
          /* ignore */
        }
        await pollUntilStructuralReportSettles(signal, baselineBeforeAgent, { maxMs: 60000 });
      }

      if (signal?.aborted) return;

      let pd = null;
      try {
        const fresh = await api.get(`/admin/question-bank/import/${importId}/preview`);
        pd = fresh.data?.data;
        if (pd) applyPreviewToReviewState(pd);
      } catch {
        /* ignore */
      }

      const u = pd?.validation_report?.structural_verification?.unresolved_count ?? 0;
      if (u > 0) {
        const svBeforeVerify = pd?.validation_report?.structural_verification;
        toast.info("Re-running verification after agent fix…");
        setValidating(true);
        try {
          await api.post(`/admin/question-bank/import/${importId}/verify`);
          await pollUntilStructuralReportSettles(signal, svBeforeVerify, { maxMs: POLL_MAX_MS_DEFAULT });
        } finally {
          setValidating(false);
        }
      }
    },
    [importId, applyPreviewToReviewState, pollUntilStructuralReportSettles]
  );


  // Rescan — re-runs AI structuring on the raw markdown, then polls for completion and reloads
  const handleFixMarkdown = async () => {
    const q = questions[currentIndex];
    if (!q) return;
    const qNum = q.paper_number ?? (currentIndex + 1);
    setFixingMarkdown(true);
    try {
      // Step 1: inject any missing tables / structured blocks from source markdown
      const res = await api.post(`/admin/question-bank/import/${importId}/questions/${qNum}/fix-markdown`);
      const data = res.data?.data;
      if (data?.changed) {
        setQuestions((prev) => {
          const next = [...prev];
          next[currentIndex] = { ...next[currentIndex], question_text: data.question_text, _healed: "admin_markdown_fix" };
          return next;
        });
        toast.success(`Markdown fixed — ${data.injected_count} block(s) injected. Running agent check…`);
      } else {
        toast.info("No missing blocks detected — running agent check for other issues…");
      }

      // Step 2: run MCP agent fix to catch remaining structural / markdown issues
      const ac = startImportFlow();
      const baselineSv = validationReport?.structural_verification;
      await runAgentFixWithFollowUp(ac.signal, baselineSv);
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error(err.response?.data?.message || "Fix markdown failed. Is the mcp-verification-agent running?");
      }
    } finally {
      setFixingMarkdown(false);
      setValidating(false);
      setAgentFixing(false);
    }
  };

  // Manual one-click: word-wrap the passage draft at a natural reading width and write {L#}
  // markers every 5th line into the editor. Client-side only — admin can then tweak the
  // markers by hand. (Exact PDF-matched numbering is the "AI line numbers" path.)
  const handleInsertLineNumbers = () => {
    const numbered = insertLineNumberMarkers(editDraft?.question_text || "");
    updateDraft("question_text", numbered);
    toast.success("Line numbers inserted. Edit the {L…} markers if needed.");
  };

  const handleClearLineNumbers = () => {
    updateDraft("question_text", stripLineNumberMarkers(editDraft?.question_text || ""));
  };

  const handleInsertManualLineNum = () => {
    const num = parseInt(manualLineNum, 10);
    if (!num || num < 1) return;
    const marker = `{L${num}} `;
    const editor = passageQuillRef.current?.getEditor?.();
    if (editor) {
      // Use the saved selection because clicking the button steals focus from Quill,
      // making getSelection() return null.
      const saved = savedPassageSelectionRef.current;
      if (!saved) {
        toast.info("Click where you want to insert the line number, then click Insert.");
        return;
      }
      editor.insertText(saved.index, marker, "user");
      editor.setSelection(saved.index + marker.length);
      savedPassageSelectionRef.current = { index: saved.index + marker.length, length: 0 };
      editor.focus();
    } else {
      const cur = editDraft?.question_text || "";
      updateDraft("question_text", cur + marker);
    }
    setManualLineNum("");
  };

  // AI (deterministic): backend reads the original printed line numbers from the source
  // markdown and writes exact-match {L#} markers into the passage.
  const handleAiLineNumbers = async () => {
    const q = currentQuestion;
    if (!q) return;
    const qNum = q.paper_number ?? (currentIndex + 1);
    setAddingLineNumbers(true);
    try {
      const res = await api.post(`/admin/question-bank/import/${importId}/questions/${qNum}/add-line-numbers`);
      const data = res.data?.data;
      if (data?.changed && data.question_text) {
        updateDraft("question_text", data.question_text);
        toast.success(`Line numbers added from the PDF — ${data.marker_count} marker(s).`);
      } else {
        toast.info(data?.message || "No printed line numbers found in the source markdown — use Insert line numbers instead.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "AI line numbers failed.");
    } finally {
      setAddingLineNumbers(false);
    }
  };

  const handleRescan = async () => {
    setRescanning(true);
    try {
      await api.post(`/admin/question-bank/import/${importId}/rescan`);
      toast.info("Rescan started — polling for completion…");

      // Poll every 4 s for up to 3 minutes
      let attempts = 0;
      const maxAttempts = 45;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await api.get(`/admin/question-bank/import/${importId}/status`);
          const status = statusRes.data?.data?.status;
          if (status === "preview" || status === "completed" || status === "failed") {
            clearInterval(poll);
            setRescanning(false);
            if (status === "failed") {
              toast.error("Rescan failed on the server.");
            } else {
              await reloadPreview({ refreshBaseline: true });
              toast.success("Rescan complete — questions refreshed.");
            }
          }
        } catch {
          // keep polling
        }
        if (attempts >= maxAttempts) {
          clearInterval(poll);
          setRescanning(false);
          toast.warning("Rescan is taking longer than expected. Reload the page when ready.");
        }
      }, 4000);
    } catch (err) {
      toast.error(err.response?.data?.message || "Rescan failed");
      setRescanning(false);
    }
  };

  // Apply Updates — syncs rescanned parsed_questions to the already-confirmed DB rows,
  // then reloads /preview so the review page shows the updated images and diagram types.
  const handleApplyUpdates = async () => {
    setApplyingUpdates(true);
    setApplyUpdatesResult(null);
    try {
      const res = await api.post(`/admin/question-bank/import/${importId}/apply-updates`);
      const d = res.data?.data || {};
      setApplyUpdatesResult(d);
      const parts = [];
      if ((d.answers_from_markdown ?? 0) > 0) parts.push(`${d.answers_from_markdown} answer(s) filled from paper`);
      if ((d.meta_updated ?? 0) > 0) parts.push(`${d.meta_updated} type(s) set`);
      if ((d.image_fixed ?? 0) > 0) parts.push(`${d.image_fixed} image(s) corrected`);
      if ((d.diagrams_generated ?? 0) > 0) parts.push(`${d.diagrams_generated} SVG(s) generated`);
      if ((d.text_updated ?? 0) > 0) parts.push(`${d.text_updated} text(s) updated`);
      if ((d.answer_data_updated ?? 0) > 0) parts.push(`${d.answer_data_updated} answer(s) updated`);
      if ((d.decimal_fixed ?? 0) > 0) parts.push(`${d.decimal_fixed} decimal(s) normalised`);
      if ((d.options_fixed ?? 0) > 0) parts.push(`${d.options_fixed} option(s) fixed`);
      toast.success(`Apply Updates: ${parts.length ? parts.join(", ") : "all up-to-date"}.`);

      // Reload questions so updated answer / image_url / diagram_type values are visible immediately
      const anyChanges =
        (d.answers_from_markdown ?? 0) > 0 ||
        (d.meta_updated ?? 0) > 0 ||
        (d.image_fixed ?? 0) > 0 ||
        (d.diagrams_generated ?? 0) > 0 ||
        (d.text_updated ?? 0) > 0 ||
        (d.answer_data_updated ?? 0) > 0;
      if (anyChanges) {
        await reloadPreview();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Apply updates failed");
    } finally {
      setApplyingUpdates(false);
    }
  };

  // Flatten category tree for dropdown
  const flattenCategories = useCallback((cats, level = 0) => {
    let result = [];
    (cats || []).forEach((cat) => {
      result.push({ id: cat.id, name: cat.name, level });
      if (cat.all_children?.length > 0) {
        result = result.concat(flattenCategories(cat.all_children, level + 1));
      }
    });
    return result;
  }, []);

  const openCreateExamDialog = useCallback(async () => {
    // Pre-fill exam name, duration, and category from parsed exam header / import
    const rawDuration = examDuration ? parseInt(examDuration.replace(/[^0-9]/g, "")) : "";
    const suggestedName =
      examTitle ||
      (importData?.source_filename || "").replace(/\.pdf$/i, "").replace(/[_-]/g, " ").trim();
    const suggestedCategoryId = importData?.target_category_id ?? "";

    setExamForm((prev) => ({
      ...prev,
      name: suggestedName,
      duration_minutes: rawDuration || "",
      category_id: suggestedCategoryId || prev.category_id,
      original_paper_link: importData?.source_url || prev.original_paper_link || "",
      subject_ids: [],
    }));
    setExamFormErrors({});
    setCreateExamOpen(true);

    const needExamMeta =
      examCategories.length === 0 ||
      examFormats.length === 0 ||
      examSubjects.length === 0 ||
      examSchoolYears.length === 0 ||
      examSchools.length === 0;
    if (needExamMeta) {
      setLoadingExamMeta(true);
      try {
        const [catRes, fmtRes, subjRes, syRes, schoolRes] = await Promise.all([
          api.get("admin/mock-exam/category-tree"),
          api.get("common/data?param=Formats"),
          api.get("common/data?param=Subjects"),
          api.get("common/data?param=SchoolYears"),
          api.get("admin/master-form/schools?per_page=1000"),
        ]);
        setExamCategories(catRes.data?.data || []);
        setExamFormats(fmtRes.data?.data || []);
        setExamSubjects(subjRes.data?.data || []);
        setExamSchoolYears(syRes.data?.data || []);
        setExamSchools(schoolRes.data?.data?.data || []);
      } catch {
        toast.error("Failed to load exam form data");
      } finally {
        setLoadingExamMeta(false);
      }
    }
  }, [examTitle, examDuration, importData?.source_filename, importData?.source_url, importData?.target_category_id, examCategories.length, examFormats.length, examSubjects.length, examSchoolYears.length, examSchools.length]);

  const buildConfirmPayloadFromApproved = useCallback(() => {
    const approvedQuestions = questions.filter((_, i) => approvedSet.has(i));
    return approvedQuestions.map((q) => {
      const parentMarks = normalizeMarksValue(q.default_marks);
      if (q.type === "grouped" && q.sub_questions?.length > 0) {
        return {
          question_text: q.question_text || '',
          type: "grouped",
          difficulty: q.difficulty || "Medium",
          topic: q.topic ?? null,
          topic_ids: Array.isArray(q.topic_ids) ? q.topic_ids.map(Number) : [],
          default_marks: parentMarks ?? 1,
          image_url: q.image_url || null,
          image_urls: q.image_urls || (q.image_url ? [q.image_url] : null),
          image_description: q.image_description || null,
          passage_type: q.passage_type || null,
          paper_number: q.paper_number != null && String(q.paper_number).trim() !== '' ? String(q.paper_number).trim() : null,
          section_title: q.section_title || null,
          section_instructions: q.section_instructions || null,
          question_subtype: q.question_subtype || null,
          explanation: q.explanation || null,
          category_id: importData?.target_category_id || null,
          options: [],
          answer_data: {},
          sub_questions: q.sub_questions.map((sq) => {
            const subMarks = normalizeMarksValue(sq.default_marks);
            return ({
            question_text: sq.question_text,
            type: normalizeQuestionType(sq.type),
            difficulty: sq.difficulty || "Medium",
            topic: sq.topic ?? null,
            default_marks: subMarks ?? 1,
            image_url: sq.image_url || null,
            image_urls: sq.image_urls || (sq.image_url ? [sq.image_url] : null),
            image_description: sq.image_description || null,
            paper_number: sq.paper_number != null && String(sq.paper_number).trim() !== '' ? String(sq.paper_number).trim() : null,
            section_title: sq.section_title || q.section_title || null,
            section_instructions: sq.section_instructions || q.section_instructions || null,
            question_subtype: sq.question_subtype || q.question_subtype || null,
            explanation: sq.explanation || null,
            category_id: importData?.target_category_id || null,
            options: sq.options || [],
            answer_data: sq.answer_data || {},
          })}),
        };
      }
      return {
        question_text: q.question_text,
        type: normalizeQuestionType(q.type),
        difficulty: q.difficulty || "Medium",
        topic: q.topic ?? null,
        topic_ids: Array.isArray(q.topic_ids) ? q.topic_ids.map(Number) : [],
        default_marks: parentMarks ?? 1,
        image_url: q.image_url || null,
        image_urls: q.image_urls || (q.image_url ? [q.image_url] : null),
        image_description: q.image_description || null,
        paper_number: q.paper_number != null && String(q.paper_number).trim() !== '' ? String(q.paper_number).trim() : null,
        section_title: q.section_title || null,
        section_instructions: q.section_instructions || null,
        question_subtype: q.question_subtype || null,
        explanation: q.explanation || null,
        category_id: importData?.target_category_id || null,
        options: q.options || [],
        answer_data: q.answer_data || {},
      };
    });
  }, [questions, approvedSet, importData?.target_category_id]);

  const openCreatePastPaperDialog = useCallback(async () => {
    const needMeta =
      paperCategories.length === 0 ||
      examFormats.length === 0 ||
      examSubjects.length === 0 ||
      examSchoolYears.length === 0 ||
      examSchools.length === 0;

    setPastPaperFormErrors({});
    setCreatePastPaperOpen(true);
    setLoadingPastPaperMeta(needMeta);

    const suggestedName =
      examTitle ||
      (importData?.source_filename || "").replace(/\.pdf$/i, "").replace(/[_-]/g, " ").trim();

    const setDefaultsFromCategories = (categories) => {
      const shelfId =
        findPaperShelfCategoryId(categories || [], importPaperShelf === "practice" ? "practice" : "past") ||
        findPastPapersCategoryId(categories || []);
      setPastPaperForm((prev) => ({
        ...prev,
        name: suggestedName || prev.name,
        category_id: shelfId || prev.category_id,
        original_paper_link: importData?.source_url || prev.original_paper_link || "",
        subject_ids: importData?.subject_id ? [importData.subject_id] : prev.subject_ids,
      }));
    };

    try {
      if (needMeta) {
        const [catRes, fmtRes, subjRes, syRes, schoolRes] = await Promise.all([
          api.get("admin/paper/category-tree"),
          api.get("common/data?param=Formats"),
          api.get("common/data?param=Subjects"),
          api.get("common/data?param=SchoolYears"),
          api.get("admin/master-form/schools?per_page=1000"),
        ]);
        const categories = catRes.data?.data || [];
        setPaperCategories(categories);
        setExamFormats(fmtRes.data?.data || []);
        setExamSubjects(subjRes.data?.data || []);
        setExamSchoolYears(syRes.data?.data || []);
        setExamSchools(schoolRes.data?.data?.data || []);
        setDefaultsFromCategories(categories);
      } else {
        setDefaultsFromCategories(paperCategories);
      }
    } catch {
      toast.error("Failed to load past paper form data");
    } finally {
      setLoadingPastPaperMeta(false);
    }
  }, [
    paperCategories,
    examFormats,
    examSubjects,
    examSchoolYears,
    examSchools,
    examTitle,
    importData?.source_filename,
    importData?.source_url,
    importData?.subject_id,
    importPaperShelf,
  ]);

  const handleAttachPastPaperSubmit = useCallback(async () => {
    await persistDraftAndSyncPreview(questions);
    const errs = {};
    if (!pastPaperForm.name?.trim()) errs.name = "Paper name is required";
    if (!pastPaperForm.category_id) errs.category_id = "Category is required";
    if (!pastPaperForm.format_id) errs.format_id = "Format is required";
    if (!Array.isArray(pastPaperForm.subject_ids) || pastPaperForm.subject_ids.length === 0) {
      errs.subject_ids = "Select at least one subject";
    }
    if (!pastPaperForm.school_year_id) errs.school_year_id = "School year is required";
    if (Object.keys(errs).length > 0) {
      setPastPaperFormErrors(errs);
      return;
    }

    const payload = buildConfirmPayloadFromApproved();
    if (payload.length === 0) {
      toast.error("No questions approved for import");
      return;
    }

    setCreatingPastPaper(true);
    try {
      await api.post(`/admin/question-bank/import/${importId}/past-paper`, {
        paper: {
          name: pastPaperForm.name.trim(),
          description: (() => {
            const base = pastPaperForm.description?.trim() || "";
            const link = pastPaperForm.original_paper_link?.trim() || "";
            if (!link) return base;
            if (base.toLowerCase().includes(link.toLowerCase())) return base;
            return base ? `${base}\n\nOriginal paper link: ${link}` : `Original paper link: ${link}`;
          })(),
          original_paper_link: pastPaperForm.original_paper_link?.trim() || null,
          category_id: Number(pastPaperForm.category_id),
          format_id: Number(pastPaperForm.format_id),
          subject_ids: (pastPaperForm.subject_ids || []).map((id) => Number(id)),
          school_year_id: Number(pastPaperForm.school_year_id),
          target_school_ids: (pastPaperForm.target_school_ids || []).map((id) => Number(id)).filter((n) => !Number.isNaN(n) && n),
          price: pastPaperForm.price !== "" ? pastPaperForm.price : "0",
          currency: pastPaperForm.currency || "£",
          duration_minutes:
            pastPaperForm.duration_minutes !== "" ? Number(pastPaperForm.duration_minutes) : null,
          school_id: (pastPaperForm.target_school_ids || []).length
            ? Number(pastPaperForm.target_school_ids[0])
            : (pastPaperForm.school_id !== "" ? Number(pastPaperForm.school_id) : null),
          is_free_paper: (() => {
            const p = parseFloat(String(pastPaperForm.price ?? "").replace(",", "."));
            return !!pastPaperForm.is_free_paper || (!Number.isNaN(p) && p <= 0);
          })(),
        },
        questions: payload,
      });
      toast.success("Past paper created and linked successfully.");
      setCreatePastPaperOpen(false);
      navigate("/admin/question-bank/import");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create past paper");
    } finally {
      setCreatingPastPaper(false);
    }
  }, [
    pastPaperForm,
    buildConfirmPayloadFromApproved,
    importId,
    navigate,
    questions,
    persistDraftAndSyncPreview,
  ]);

  const handleCreateExamSubmit = useCallback(async () => {
    await persistDraftAndSyncPreview(questions);
    // Validate
    const errs = {};
    if (!examForm.name.trim()) errs.name = "Exam name is required";
    if (!examForm.category_id) errs.category_id = "Category is required.";
    if (!examForm.format_id) errs.format_id = "Format is required";
    if (!Array.isArray(examForm.subject_ids) || examForm.subject_ids.length === 0) {
      errs.subject_ids = "Select at least one subject.";
    }
    if (!examForm.school_year_id) errs.school_year_id = "School year is required.";
    if (approvedCount === 0) {
      toast.error("Please approve at least one question before creating the exam");
      return;
    }
    if (Object.keys(errs).length > 0) {
      setExamFormErrors(errs);
      return;
    }
    setExamFormErrors({});
    setCreatingExam(true);

    try {
      // Step 1: Import approved questions → get IDs
      const payload = buildConfirmPayloadFromApproved();

      const importRes = await api.post(
        `/admin/question-bank/import/${importId}/confirm`,
        { questions: payload }
      );
      let questionIds = importRes.data?.data?.question_ids || [];
      const importedCount = importRes.data?.data?.questions_imported || payload.length;

      // If confirm returned no IDs (e.g. already_imported path stored them differently),
      // fetch the stored imported_question_ids from the status endpoint
      if (questionIds.length === 0) {
        try {
          const statusRes = await api.get(`/admin/question-bank/import/${importId}/status`);
          const storedIds = statusRes.data?.data?.imported_question_ids || [];
          if (storedIds.length > 0) {
            questionIds = storedIds;
          }
        } catch {
          // ignore fallback fetch error
        }
      }

      if (questionIds.length === 0) {
        toast.error("No question IDs were returned from the import. Please try again.");
        setCreatingExam(false);
        return;
      }

      // Step 2: Create the mock exam with question bank IDs
      const formData = new FormData();
      formData.append("name", examForm.name.trim());

      // Guard optional/nullable fields — only send if they have real values
      if (examForm.description && examForm.description.trim() !== "") {
        const link = examForm.original_paper_link?.trim() || "";
        const desc = examForm.description.trim();
        const finalDesc = link && !desc.toLowerCase().includes(link.toLowerCase())
          ? `${desc}\n\nOriginal paper link: ${link}`
          : desc;
        formData.append("description", finalDesc);
      } else if (examForm.original_paper_link && examForm.original_paper_link.trim() !== "") {
        formData.append("description", `Original paper link: ${examForm.original_paper_link.trim()}`);
      }
      if (examForm.original_paper_link && examForm.original_paper_link.trim() !== "") {
        formData.append("original_paper_link", examForm.original_paper_link.trim());
      }
      formData.append("category_id", Number(examForm.category_id));
      formData.append("format_id", Number(examForm.format_id));
      (examForm.subject_ids || []).forEach((id) => {
        if (id !== "" && id != null && !Number.isNaN(Number(id))) {
          formData.append("subject_ids[]", Number(id));
        }
      });
      formData.append("school_year_id", Number(examForm.school_year_id));
      formData.append("price", examForm.price != null && examForm.price !== "" ? examForm.price : "0");

      // currency must be exactly 1 char from allowed set
      if (examForm.currency && ["€", "$", "£"].includes(examForm.currency)) {
        formData.append("currency", examForm.currency);
      }

      // duration_minutes must be a valid positive integer
      if (
        examForm.duration_minutes !== "" &&
        examForm.duration_minutes != null &&
        !isNaN(examForm.duration_minutes) &&
        Number(examForm.duration_minutes) >= 1
      ) {
        formData.append("duration_minutes", Number(examForm.duration_minutes));
      }

      // school_id must be a valid integer
      const examSchoolIds = (examForm.target_school_ids || []).map((id) => Number(id)).filter((n) => !Number.isNaN(n) && n);
      examSchoolIds.forEach((id) => formData.append("target_school_ids[]", id));
      formData.append("school_targets_sync", "1");
      if (examSchoolIds[0]) {
        formData.append("school_id", examSchoolIds[0]);
      } else if (examForm.school_id && examForm.school_id !== "" && !isNaN(examForm.school_id)) {
        formData.append("school_id", Number(examForm.school_id));
      }

      // Advanced settings
      if (
        examForm.max_attempts != null &&
        examForm.max_attempts !== "" &&
        !isNaN(examForm.max_attempts)
      ) {
        formData.append("max_attempts", Math.max(0, Number(examForm.max_attempts)));
      }
      if (
        examForm.marks_per_correct_answer != null &&
        examForm.marks_per_correct_answer !== "" &&
        !isNaN(examForm.marks_per_correct_answer)
      ) {
        formData.append(
          "marks_per_correct_answer",
          Math.max(0, Number(examForm.marks_per_correct_answer))
        );
      }
      if (
        examForm.negative_marks_penalty != null &&
        examForm.negative_marks_penalty !== "" &&
        !isNaN(examForm.negative_marks_penalty)
      ) {
        formData.append(
          "negative_marks_penalty",
          Math.max(0, Number(examForm.negative_marks_penalty))
        );
      }
      if (examForm.instructions !== undefined) {
        formData.append(
          "instructions",
          examForm.instructions != null && String(examForm.instructions).trim() !== ""
            ? examForm.instructions.trim()
            : ""
        );
      }

      questionIds.forEach((id, i) =>
        formData.append(`question_bank_question_ids[${i}]`, id)
      );

      await api.post("admin/mock-exam", formData);

      toast.success(`${importedCount} questions imported and exam created!`);
      setCreateExamOpen(false);
      navigate("/admin/mock-exams");
    } catch (err) {
      console.error("Create exam error:", err.response?.data || err);
      // Show each validation error individually if available
      const validationErrors = err.response?.data?.errors;
      if (validationErrors) {
        Object.entries(validationErrors).forEach(([field, messages]) => {
          const msgs = Array.isArray(messages) ? messages : [messages];
          msgs.forEach((msg) => toast.error(`${field}: ${msg}`));
        });
      } else {
        toast.error(err.response?.data?.message || "Failed to create exam");
      }
    } finally {
      setCreatingExam(false);
    }
  }, [
    examForm,
    approvedCount,
    buildConfirmPayloadFromApproved,
    importId,
    navigate,
    questions,
    persistDraftAndSyncPreview,
  ]);

  const toggleQuestionSelection = useCallback((index) => {
    setSelectedQuestions((prev) => {
      const updated = new Set(prev);
      if (updated.has(index)) updated.delete(index);
      else updated.add(index);
      return updated;
    });
  }, []);

  const selectAllQuestions = useCallback(() => {
    setSelectedQuestions(new Set(questions.map((_, idx) => idx)));
  }, [questions]);

  const deselectAllQuestions = useCallback(() => {
    setSelectedQuestions(new Set());
  }, []);

  const rejectSelected = useCallback(() => {
    if (selectedQuestions.size === 0) return;
    if (window.confirm(`Reject ${selectedQuestions.size} selected question(s)?`)) {
      setApprovedSet((prev) => {
        const updated = new Set(prev);
        selectedQuestions.forEach((idx) => updated.delete(idx));
        return updated;
      });
      deselectAllQuestions();
      toast.success(`${selectedQuestions.size} question(s) rejected`);
    }
  }, [selectedQuestions, deselectAllQuestions]);

  const applyBulkEdit = useCallback(() => {
    if (selectedQuestions.size === 0) return;
    const { difficulty, default_marks } = bulkEditValues;

    const next = questions.map((q, idx) => {
      if (!selectedQuestions.has(idx)) return q;
      const updates = {
        ...(difficulty && { difficulty }),
        ...(default_marks && { default_marks: Number(default_marks) }),
      };
      if (q.type === "grouped" && q.sub_questions?.length > 0) {
        return {
          ...q,
          ...updates,
          sub_questions: q.sub_questions.map((sq) => ({ ...sq, ...updates })),
        };
      }
      return { ...q, ...updates };
    });

    setQuestions(next);
    setModifiedQuestions((prev) => new Set([...prev, ...selectedQuestions]));
    setBulkEditMode(false);
    deselectAllQuestions();
    toast.success(`${selectedQuestions.size} question(s) updated`);
    void persistDraftAndSyncPreview(next);
  }, [selectedQuestions, bulkEditValues, deselectAllQuestions, questions, persistDraftAndSyncPreview]);

  const applyBulkSectionFromForm = useCallback(() => {
    if (selectedQuestions.size === 0) return;
    const st = bulkEditValues.section_title?.trim() ? bulkEditValues.section_title.trim() : null;
    const si = bulkEditValues.section_instructions?.trim() ? bulkEditValues.section_instructions.trim() : null;
    if (!st && !si) {
      toast.error("Enter a section title and/or section instructions.");
      return;
    }
    const next = questions.map((q, idx) =>
      selectedQuestions.has(idx) ? mergeSectionFieldsOntoQuestion(q, st, si) : q
    );
    setQuestions(next);
    setModifiedQuestions((prev) => new Set([...prev, ...selectedQuestions]));
    setBulkEditMode(false);
    deselectAllQuestions();
    toast.success(`${selectedQuestions.size} question(s) updated with section fields.`);
    void persistDraftAndSyncPreview(next);
  }, [selectedQuestions, bulkEditValues, deselectAllQuestions, questions, persistDraftAndSyncPreview]);

  const clearBulkSectionOnSelected = useCallback(() => {
    if (selectedQuestions.size === 0) return;
    if (!window.confirm(`Clear section title and instructions on ${selectedQuestions.size} selected question(s)?`)) return;
    const next = questions.map((q, idx) =>
      selectedQuestions.has(idx) ? mergeSectionFieldsOntoQuestion(q, null, null) : q
    );
    setQuestions(next);
    setModifiedQuestions((prev) => new Set([...prev, ...selectedQuestions]));
    deselectAllQuestions();
    toast.success("Section fields cleared on selected questions.");
    void persistDraftAndSyncPreview(next);
  }, [selectedQuestions, deselectAllQuestions, questions, persistDraftAndSyncPreview]);

  const applyDraftSectionToSelected = useCallback(() => {
    if (!editDraft || selectedQuestions.size === 0) return;
    const st = editDraft.section_title ?? null;
    const si = editDraft.section_instructions ?? null;
    if (!st && !si) {
      toast.error("Set section title or instructions in Paper / section accordion first.");
      return;
    }
    if (!window.confirm(`Apply current section title and instructions to ${selectedQuestions.size} selected question(s)?`)) return;
    const next = questions.map((q, idx) =>
      selectedQuestions.has(idx) ? mergeSectionFieldsOntoQuestion(q, st, si) : q
    );
    setQuestions(next);
    setModifiedQuestions((prev) => new Set([...prev, ...selectedQuestions]));
    toast.success("Section fields applied to selected questions.");
    void persistDraftAndSyncPreview(next);
  }, [editDraft, selectedQuestions, questions, persistDraftAndSyncPreview]);

  // Auto-save interval
  useEffect(() => {
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);
    }
    if (questions.length > 0) {
      autoSaveTimerRef.current = setInterval(() => {
        autoSaveDraft();
      }, 30000);
    }
    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [questions, autoSaveDraft]);

  useEffect(() => {
    if (loading || questions.length === 0) return undefined;
    if (draftDebounceTimerRef.current) {
      clearTimeout(draftDebounceTimerRef.current);
    }
    draftDebounceTimerRef.current = setTimeout(() => {
      persistDraftAndSyncPreview(questions);
    }, 2000);
    return () => {
      if (draftDebounceTimerRef.current) {
        clearTimeout(draftDebounceTimerRef.current);
      }
    };
  }, [questions, loading, persistDraftAndSyncPreview]);

  const mergeGeneratedExplanation = (prev, text) => {
    const formatted = formatExplanationStepwise(text);
    const hasRichMedia = /<img\s/i.test(prev || "");
    if (hasRichMedia && String(prev || "").replace(/<[^>]*>/g, "").trim()) {
      return `${prev}<p><br></p>${formatted}`;
    }
    return formatted;
  };

  const handleGenerateExplanation = async () => {
    if (!editDraft) return;
    setGeneratingExplanation(true);
    try {
      const res = await api.post(
        `/admin/question-bank/import/${importId}/generate-explanation`,
        { question: editDraft }
      );
      const text = res.data?.data?.explanation;
      if (text) {
        updateDraft("explanation", mergeGeneratedExplanation(editDraft.explanation, text));
      }
      toast.success("Explanation generated in step-wise format");
    } catch {
      toast.error("Failed to generate explanation");
    } finally {
      setGeneratingExplanation(false);
    }
  };

  const handleGenerateExplanationForPath = async (path) => {
    const q =
      path.type === "subquestion"
        ? questions[path.parentIndex]?.sub_questions?.[path.subIndex]
        : questions[path.index ?? path.parentIndex];
    if (!q) return;
    setGeneratingExplanation(true);
    try {
      const res = await api.post(
        `/admin/question-bank/import/${importId}/generate-explanation`,
        { question: q }
      );
      const text = res.data?.data?.explanation;
      if (!text) return;
      const merged = mergeGeneratedExplanation(q.explanation, text);
      setQuestions((prev) => {
        const next = structuredClone(prev);
        if (path.type === "subquestion") {
          next[path.parentIndex].sub_questions[path.subIndex].explanation = merged;
        } else {
          next[path.index].explanation = merged;
        }
        return next;
      });
      setModifiedQuestions((prev) =>
        new Set([
          ...prev,
          path.type === "subquestion" ? path.parentIndex : path.index,
        ])
      );
      toast.success("Explanation generated in step-wise format");
    } catch {
      toast.error("Failed to generate explanation");
    } finally {
      setGeneratingExplanation(false);
    }
  };

  const handleGenerateAllExplanations = async () => {
    if (!importId || !questions.length) return;
    const targets = [];
    questions.forEach((q, index) => {
      if (q?.type === "grouped" && Array.isArray(q.sub_questions) && q.sub_questions.length > 0) {
        q.sub_questions.forEach((sq, subIndex) => {
          targets.push({ type: "subquestion", parentIndex: index, subIndex, question: sq });
        });
      } else {
        targets.push({ type: "standalone", index, question: q });
      }
    });

    setGeneratingAllExplanations(true);
    setExplainAllProgress({ done: 0, total: targets.length });
    let ok = 0;
    let failed = 0;
    const nextQuestions = structuredClone(questions);
    const modified = new Set(modifiedQuestions);

    try {
      for (let i = 0; i < targets.length; i++) {
        const t = targets[i];
        setExplainAllProgress({ done: i, total: targets.length });
        try {
          const res = await api.post(
            `/admin/question-bank/import/${importId}/generate-explanation`,
            { question: t.question }
          );
          const text = res.data?.data?.explanation;
          if (!text) {
            failed += 1;
            continue;
          }
          const prevExp =
            t.type === "subquestion"
              ? nextQuestions[t.parentIndex].sub_questions[t.subIndex].explanation
              : nextQuestions[t.index].explanation;
          const merged = mergeGeneratedExplanation(prevExp, text);
          if (t.type === "subquestion") {
            nextQuestions[t.parentIndex].sub_questions[t.subIndex].explanation = merged;
            modified.add(t.parentIndex);
          } else {
            nextQuestions[t.index].explanation = merged;
            modified.add(t.index);
          }
          ok += 1;
        } catch {
          failed += 1;
        }
      }
      setQuestions(nextQuestions);
      setModifiedQuestions(modified);
      setExplainAllProgress({ done: targets.length, total: targets.length });
      if (ok > 0 && failed === 0) {
        toast.success(`Generated explanations for ${ok} question${ok === 1 ? "" : "s"}`);
      } else if (ok > 0) {
        toast.warning(`Generated ${ok}, failed ${failed}`);
      } else {
        toast.error("Failed to generate explanations");
      }
    } finally {
      setGeneratingAllExplanations(false);
    }
  };

  // AI Improve handler
  const handleAiImprove = async (path) => {
    setImproving(true);
    const q =
      path.type === "subquestion"
        ? questions[path.parentIndex].sub_questions[path.subIndex]
        : questions[path.index];
    try {
      const res = await api.post(
        `/admin/question-bank/import/${importId}/ai-improve-question`,
        { question: q }
      );
      setAiOriginal(q);
      setAiSuggestion(res.data?.data?.improved_question);
      setAiImprovePath(path);
      setAiImproveOpen(true);
    } catch {
      toast.error("AI improvement failed. Please try again.");
    } finally {
      setImproving(false);
    }
  };

  const handleAiImproveAll = async () => {
    setImprovingAll(true);
    try {
      const res = await api.post(`/admin/question-bank/import/${importId}/ai-improve-all`);
      const data = res.data?.data;
      if (data?.parsed_questions) {
        setQuestions(normalizeQuestionsForReview(data.parsed_questions));
        toast.success(`AI improved: ${data.improved_count}/${data.total_count} questions`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Bulk AI improvement failed");
    } finally {
      setImprovingAll(false);
    }
  };

  // AI Generate Diagram handler
  const handleGenerateDiagram = async (path) => {
    setGeneratingDiagram(true);
    const q =
      path.type === "subquestion"
        ? questions[path.parentIndex].sub_questions[path.subIndex]
        : path.type === "stem"
          ? questions[path.parentIndex]
          : questions[path.index];
    try {
      const res = await api.post(
        `/admin/question-bank/import/${importId}/ai-generate-diagram`,
        { question: q }
      );
      setGeneratedDiagram(res.data?.data);
      setDiagramTargetPath(path);
      setDiagramDialogOpen(true);
    } catch {
      toast.error("AI diagram generation failed. Please try again.");
    } finally {
      setGeneratingDiagram(false);
    }
  };

  // Apply generated diagram — upload SVG to R2, then store the returned URL (avoids 512-char limit on image_url)
  const applyGeneratedDiagram = async () => {
    if (!generatedDiagram?.svg || !diagramTargetPath) return;

    setImageUploading(true);
    try {
      // Convert SVG string → Blob → File so we can POST it to the upload endpoint
      const svgBlob = new Blob([generatedDiagram.svg], { type: "image/svg+xml" });
      const svgFile = new File([svgBlob], `diagram_${Date.now()}.svg`, { type: "image/svg+xml" });
      const formData = new FormData();
      formData.append("image", svgFile);

      const res = await api.post(
        `/admin/question-bank/import/${importId}/upload-image`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const imageUrl = res.data?.data?.image_url;
      if (!imageUrl) {
        toast.error("Diagram upload failed — no URL returned");
        return;
      }

      // Also persist diagram_type / hub_value when the API returned them (hub-spoke native rendering)
      const diagType  = generatedDiagram?.diagram_type ?? null;
      const hubVal    = generatedDiagram?.hub_value ?? null;

      setQuestions((prev) => {
        const next = structuredClone(prev);
        const applyToQuestion = (q) => {
          q.image_url = imageUrl;
          if (diagType) {
            q.answer_data = {
              ...(q.answer_data || {}),
              diagram_type: diagType,
              hub_value: hubVal,
            };
          }
        };
        if (diagramTargetPath.type === "subquestion") {
          applyToQuestion(next[diagramTargetPath.parentIndex].sub_questions[diagramTargetPath.subIndex]);
        } else if (diagramTargetPath.type === "stem") {
          applyToQuestion(next[diagramTargetPath.parentIndex]);
        } else {
          applyToQuestion(next[diagramTargetPath.index]);
        }
        return next;
      });
      // Keep editDraft in sync so the edit panel shows the new diagram immediately
      setEditDraft((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, image_url: imageUrl };
        if (diagType) {
          updated.answer_data = { ...(prev.answer_data || {}), diagram_type: diagType, hub_value: hubVal };
        }
        return updated;
      });
      setModifiedQuestions((prev) =>
        new Set([
          ...prev,
          diagramTargetPath.type === "stem"
            ? diagramTargetPath.parentIndex
            : diagramTargetPath.index ?? diagramTargetPath.parentIndex,
        ])
      );
      setDiagramDialogOpen(false);
      setGeneratedDiagram(null);
      toast.success("Diagram added to question");
    } catch {
      toast.error("Failed to upload diagram. Please try again.");
    } finally {
      setImageUploading(false);
    }
  };

  const moveQuestion = useCallback(
    (idx, dir) => {
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= questions.length) return;
      const next = [...questions];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      setQuestions(next);
      setApprovedSet((s) => remapSetAfterSwap(s, idx, newIdx));
      setSelectedQuestions((s) => remapSetAfterSwap(s, idx, newIdx));
      setModifiedQuestions((s) => {
        const m = remapSetAfterSwap(s, idx, newIdx);
        m.add(idx);
        m.add(newIdx);
        return m;
      });
      setOriginalQuestions((orig) => {
        const o = [...orig];
        if (o.length === next.length) {
          [o[idx], o[newIdx]] = [o[newIdx], o[idx]];
        }
        return o;
      });
      setCurrentIndex(newIdx);
      void persistDraftAndSyncPreview(next);
    },
    [questions, persistDraftAndSyncPreview]
  );

  const applyAiSuggestion = () => {
    setQuestions((prev) => {
      const next = structuredClone(prev);
      if (aiImprovePath.type === "subquestion") {
        const existing = next[aiImprovePath.parentIndex].sub_questions[aiImprovePath.subIndex];
        next[aiImprovePath.parentIndex].sub_questions[aiImprovePath.subIndex] = ensureQuestionRef(
          { ...aiSuggestion, import_question_ref: existing?.import_question_ref },
          aiImprovePath.parentIndex,
          aiImprovePath.subIndex
        );
      } else {
        const existing = next[aiImprovePath.index];
        next[aiImprovePath.index] = ensureQuestionRef(
          { ...aiSuggestion, import_question_ref: existing?.import_question_ref },
          aiImprovePath.index
        );
      }
      return next;
    });
    setModifiedQuestions((prev) =>
      new Set([...prev, aiImprovePath.index ?? aiImprovePath.parentIndex])
    );
    setAiImproveOpen(false);
    toast.success("AI suggestion applied");
  };

  // Bank question search handler
  const updateDraft = (field, value) => {
    setEditDraft((prev) => ({ ...prev, [field]: value }));
  };


  // ---- Fill-in-the-blank blank helpers ----
  // Reads blanks from either admin format (answer_data.correct_blanks) or legacy (blanks array).
  // Each blank has { text (primary answer), alts (string[] of alternatives), label, order }.
  const getFillBlanks = (draft) => {
    const labels = draft.answer_data?.blank_labels || [];
    const marks = draft.answer_data?.blank_marks || [];
    const positions = draft.answer_data?.blank_label_positions || [];
    if (draft.answer_data?.correct_blanks?.length > 0) {
      return draft.answer_data.correct_blanks.map((b, i) => ({
        text: Array.isArray(b) ? (b[0] ?? "") : String(b ?? ""),
        alts: Array.isArray(b) && b.length > 1 ? b.slice(1) : [],
        label: labels[i] ?? "",
        labelPosition: positions[i] || inferBlankLabelPosition(labels[i] ?? ""),
        marks: marks[i] ?? 1,
        order: i,
      }));
    }
    if (draft.blanks?.length > 0) {
      return draft.blanks.map((b, i) => ({
        text: b.text ?? "",
        alts: [],
        label: labels[i] ?? "",
        labelPosition: positions[i] || inferBlankLabelPosition(labels[i] ?? ""),
        marks: marks[i] ?? 1,
        order: b.order ?? i,
      }));
    }
    return [{ text: "", alts: [], label: "", labelPosition: "before", marks: 1, order: 0 }];
  };

  const updateFillMarksConfig = (mode, fixedTotal) => {
    setEditDraft((prev) => {
      const ad = { ...(prev.answer_data || {}) };
      if (mode === "fixed") {
        const total = parseInt(fixedTotal, 10) || 1;
        ad.marks_mode = "fixed";
        ad.marks_total = total;
        return { ...prev, default_marks: total, answer_data: ad };
      }
      delete ad.marks_mode;
      delete ad.marks_total;
      return { ...prev, answer_data: ad };
    });
  };

  // Save blanks back to answer_data.correct_blanks (canonical admin format).
  // Each element is [primary, ...alts], with empty alts omitted from the tail.
  const saveFillBlanks = (blanks) => {
    setEditDraft((prev) => ({
      ...prev,
      blanks: undefined,
      answer_data: {
        ...(prev.answer_data || {}),
        correct_blanks: blanks.map((b) => {
          const alts = (b.alts || []).filter((a) => a.trim() !== "");
          return alts.length > 0 ? [b.text, ...alts] : [b.text];
        }),
        blank_labels: blanks.map((b) => b.label ?? ""),
        blank_marks: blanks.map((b) => Math.max(1, parseInt(b.marks, 10) || 1)),
        blank_label_positions: blanks.map((b) => b.labelPosition || inferBlankLabelPosition(b.label ?? "")),
      },
    }));
  };

  const updateFillBlankLabelPosition = (idx, position) => {
    const blanks = getFillBlanks(editDraft).map((b, i) => (i === idx ? { ...b, labelPosition: position } : b));
    saveFillBlanks(blanks);
  };

  const addFillBlank = () => {
    const blanks = getFillBlanks(editDraft);
    saveFillBlanks([...blanks, { text: "", alts: [], marks: 1, order: blanks.length }]);
  };

  const updateFillBlankMarks = (idx, value) => {
    const blanks = getFillBlanks(editDraft).map((b, i) =>
      i === idx ? { ...b, marks: Math.max(1, parseInt(value, 10) || 1) } : b
    );
    saveFillBlanks(blanks);
  };

  const removeFillBlank = (idx) => {
    const blanks = getFillBlanks(editDraft).filter((_, i) => i !== idx).map((b, i) => ({ ...b, order: i }));
    saveFillBlanks(blanks);
  };

  const updateFillBlankText = (idx, text) => {
    const blanks = getFillBlanks(editDraft).map((b, i) => (i === idx ? { ...b, text } : b));
    saveFillBlanks(blanks);
  };

  const updateFillBlankAlt = (blankIdx, altIdx, value) => {
    const blanks = getFillBlanks(editDraft).map((b, i) => {
      if (i !== blankIdx) return b;
      const alts = [...(b.alts || [])];
      alts[altIdx] = value;
      return { ...b, alts };
    });
    saveFillBlanks(blanks);
  };

  const addFillBlankAlt = (blankIdx) => {
    const blanks = getFillBlanks(editDraft).map((b, i) =>
      i === blankIdx ? { ...b, alts: [...(b.alts || []), ""] } : b
    );
    saveFillBlanks(blanks);
  };

  const removeFillBlankAlt = (blankIdx, altIdx) => {
    const blanks = getFillBlanks(editDraft).map((b, i) => {
      if (i !== blankIdx) return b;
      const alts = (b.alts || []).filter((_, ai) => ai !== altIdx);
      return { ...b, alts };
    });
    saveFillBlanks(blanks);
  };

  const moveFillBlankUp = (idx) => {
    if (idx <= 0) return;
    const blanks = [...getFillBlanks(editDraft)];
    [blanks[idx - 1], blanks[idx]] = [blanks[idx], blanks[idx - 1]];
    saveFillBlanks(blanks.map((b, i) => ({ ...b, order: i })));
  };

  const moveFillBlankDown = (idx) => {
    const blanks = getFillBlanks(editDraft);
    if (idx >= blanks.length - 1) return;
    const updated = [...blanks];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    saveFillBlanks(updated.map((b, i) => ({ ...b, order: i })));
  };

  const uploadQuestionImageFile = useCallback(async (file) => {
    if (!file) return;
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await api.post(
        `/admin/question-bank/import/${importId}/upload-image`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const imageUrl = res.data?.data?.image_url;
      if (imageUrl) {
        setEditDraft((prev) => {
          const urls = Array.isArray(prev.image_urls) ? [...prev.image_urls] : (prev.image_url ? [prev.image_url] : []);
          if (!urls.includes(imageUrl)) urls.push(imageUrl);
          return { ...prev, image_url: imageUrl, image_urls: urls };
        });
        toast.success("Image uploaded");
      }
    } catch {
      toast.error("Image upload failed");
    } finally {
      setImageUploading(false);
      setImageDropActive(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  }, [importId]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadQuestionImageFile(file);
  };

  const handleEditImageReorder = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setEditDraft((prev) => {
      const imgs = Array.isArray(prev.image_urls) && prev.image_urls.filter(Boolean).length > 0
        ? prev.image_urls.filter(Boolean)
        : prev.image_url ? [prev.image_url] : [];
      const next = [...imgs];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return { ...prev, image_url: next[0] || null, image_urls: next.length > 0 ? next : null };
    });
  };

  const handleQuestionImageDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (!file || !String(file.type || "").startsWith("image/")) {
      setImageDropActive(false);
      toast.error("Drop an image file");
      return;
    }
    await uploadQuestionImageFile(file);
  }, [uploadQuestionImageFile]);


  // Confirm import — only approved questions
  // Generate answers for all questions or selected ones
  const handleGenerateAnswers = async (indices = null) => {
    // Flush unsaved passage/stem text before sending to server, so the
    // generate-answers endpoint works on the latest question_text.
    if (editMode && editTarget?.type === 'stem' && editDraft?.question_text != null) {
      const { parentIndex } = editTarget;
      const flushed = [...questions];
      flushed[parentIndex] = { ...flushed[parentIndex], question_text: editDraft.question_text };
      setQuestions(flushed);
      setEditMode(false);
      setEditDraft(null);
      setEditTarget(null);
      await persistDraftAndSyncPreview(flushed);
    }
    setGeneratingAnswers(true);
    try {
      const payload = indices ? { question_indices: indices } : {};
      const res = await api.post(`/admin/question-bank/import/${importId}/generate-answers`, payload);
      const data = res.data?.data;
      if (data?.parsed_questions) {
        setQuestions(normalizeQuestionsForReview(data.parsed_questions));
        toast.success(`Answers generated: ${data.answers_generated}/${data.total_questions} questions now have answers`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to generate answers");
    } finally {
      setGeneratingAnswers(false);
    }
  };

  // Switch the active answer source (pdf | ai | manual) for a question or sub-question.
  // When source === 'manual' the caller should also open the edit panel.
  const handleSwitchAnswerSource = (qIdx, sqIdx, source) => {
    setQuestions((prev) => {
      const next = [...prev];
      const q = { ...next[qIdx] };

      if (sqIdx != null) {
        const sub = { ...(q.sub_questions?.[sqIdx] || {}) };
        if (source === "pdf" && sub.pdf_answer_data) {
          sub.answer_data = { ...sub.pdf_answer_data };
          // Re-sync options is_correct from pdf_answer_data.correct_options
          if (Array.isArray(sub.options) && Array.isArray(sub.pdf_answer_data.correct_options)) {
            sub.options = sub.options.map((o, i) => ({
              ...o,
              is_correct: sub.pdf_answer_data.correct_options.includes(i),
            }));
          }
        } else if (source === "ai" && sub.ai_answer_data) {
          sub.answer_data = { ...sub.ai_answer_data };
          if (Array.isArray(sub.options) && Array.isArray(sub.ai_answer_data.correct_options)) {
            sub.options = sub.options.map((o, i) => ({
              ...o,
              is_correct: sub.ai_answer_data.correct_options.includes(i),
            }));
          }
        }
        const sqs = [...(q.sub_questions || [])];
        sqs[sqIdx] = sub;
        q.sub_questions = sqs;
      } else {
        if (source === "pdf" && q.pdf_answer_data) {
          q.answer_data = { ...q.pdf_answer_data };
          if (Array.isArray(q.options) && Array.isArray(q.pdf_answer_data.correct_options)) {
            q.options = q.options.map((o, i) => ({
              ...o,
              is_correct: q.pdf_answer_data.correct_options.includes(i),
            }));
          }
        } else if (source === "ai" && q.ai_answer_data) {
          q.answer_data = { ...q.ai_answer_data };
          if (Array.isArray(q.options) && Array.isArray(q.ai_answer_data.correct_options)) {
            q.options = q.options.map((o, i) => ({
              ...o,
              is_correct: q.ai_answer_data.correct_options.includes(i),
            }));
          }
        }
      }

      next[qIdx] = q;
      return next;
    });
    // Draft auto-save will pick up the questions state change automatically
  };

  // Trigger validation of extracted questions
  const handleUploadSupplementary = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSupplementary(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(
        `/admin/question-bank/import/${importId}/upload-supplementary`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const url = res.data?.data?.supplementary_booklet_url;
      setSupplementaryBookletUrl(url);
      setShowSupplementary(true);
      toast.success("Supplementary booklet uploaded");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload supplementary booklet");
    } finally {
      setUploadingSupplementary(false);
      // Reset input so same file can be re-uploaded
      e.target.value = "";
    }
  };

  // Phase A/C: re-run structural verification only (lighter than full validate)
  const handleReverify = async () => {
    const ac = startImportFlow();
    setValidating(true);
    const baselineSv = validationReport?.structural_verification;
    try {
      await api.post(`/admin/question-bank/import/${importId}/verify`);
      toast.info("Verification dispatched. Updating…");
      const result = await pollUntilStructuralReportSettles(ac.signal, baselineSv);
      if (result.reason === "zero_unresolved") {
        toast.success("Verification complete — no unresolved structural issues.");
      } else if (result.ok) {
        toast.success("Verification refreshed");
      } else if (result.reason === "timeout") {
        toast.info("Verification is still running or timed out — try refreshing the page.");
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error(err.response?.data?.message || "Failed to dispatch verification");
      }
    } finally {
      setValidating(false);
    }
  };

  // Agent fix: call MCP verification-agent to auto-resolve unresolved issues, then follow up with verify if needed.
  const handleAgentFix = async () => {
    const ac = startImportFlow();
    setAgentFixing(true);
    try {
      const baselineSv = validationReport?.structural_verification;
      await runAgentFixWithFollowUp(ac.signal, baselineSv);
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error(err.response?.data?.message || "Agent fix failed. Is the mcp-verification-agent running?");
      }
    } finally {
      setAgentFixing(false);
      setValidating(false);
    }
  };

  // Phase A/C: dismiss an issue (false positive). persistent=true silences future runs.
  const handleDismissIssue = async (issueKey, persistent = true) => {
    try {
      await api.post(`/admin/question-bank/import/${importId}/issues/${issueKey}/dismiss`, {
        persistent,
        reason: persistent ? "false_positive" : "needs_human",
      });
      const res = await api.get(`/admin/question-bank/import/${importId}/preview`);
      if (res.data?.data?.validation_report) setValidationReport(res.data.data.validation_report);
      toast.success(persistent ? "Issue dismissed permanently" : "Issue cleared from this run");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to dismiss issue");
    }
  };

  // Phase A/C: lift a persistent dismissal (issue will reappear next verification).
  const handleUndismissIssue = async (issueKey) => {
    try {
      await api.delete(`/admin/question-bank/import/${importId}/issues/${issueKey}/dismiss`);
      const res = await api.get(`/admin/question-bank/import/${importId}/preview`);
      if (res.data?.data?.validation_report) setValidationReport(res.data.data.validation_report);
      toast.success("Dismissal lifted");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to undismiss");
    }
  };

  // Phase A/C: mark an issue as verified by admin (when admin has manually checked it).
  const handleVerifyIssue = async (issueKey) => {
    try {
      await api.post(`/admin/question-bank/import/${importId}/issues/${issueKey}/verify`, {
        verified_by: "admin",
        notes: "Manually verified in review screen",
      });
      const res = await api.get(`/admin/question-bank/import/${importId}/preview`);
      if (res.data?.data?.validation_report) setValidationReport(res.data.data.validation_report);
      toast.success("Issue marked verified");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to verify issue");
    }
  };

  const handleValidate = async () => {
    const ac = startImportFlow();
    setValidating(true);
    const baselineSnap = JSON.stringify({
      vr: validationReport,
      qr: qualityReport,
    });
    try {
      await api.post(`/admin/question-bank/import/${importId}/validate`);
      toast.info("Validation started. Updating…");
      const t0 = Date.now();
      const maxMs = 90000;
      let lastSnap = null;
      let stableAfterChange = 0;
      let sawChange = false;

      while (Date.now() - t0 < maxMs) {
        if (ac.signal.aborted) break;
        await new Promise((resolve) => {
          setTimeout(resolve, 3000);
        });
        if (ac.signal.aborted) break;
        try {
          const res = await api.get(`/admin/question-bank/import/${importId}/preview`);
          const data = res.data?.data;
          if (!data) continue;
          applyPreviewToReviewState(data);
          const snap = JSON.stringify({
            vr: data.validation_report,
            qr: data.quality_report,
          });
          if (snap !== baselineSnap) {
            sawChange = true;
          }
          if (sawChange) {
            if (snap === lastSnap) {
              stableAfterChange += 1;
            } else {
              stableAfterChange = 0;
            }
            lastSnap = snap;
            if (stableAfterChange >= 2) {
              break;
            }
          }
        } catch {
          /* keep polling */
        }
      }
      toast.success("Validation complete");
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error(err.response?.data?.message || "Failed to start validation");
      }
    } finally {
      setValidating(false);
    }
  };

  // ── Extracted Images panel handlers ──────────────────────────────────────
  const fetchExtractedImages = async () => {
    setImagesLoading(true);
    try {
      const res = await api.get(`/admin/question-bank/import/${importId}/images`);
      const d = res.data?.data;
      const images = d?.images || [];
      setExtractedImages(images);
      setImagesPanelQuestions(d?.questions || []);
      setImagesLoaded(true);
      // Change H: Auto-open panel so admin sees orphaned images immediately
      if (images.some((img) => !img.assigned_question_id)) {
        setImagesOpen(true);
      }
    } catch {
      toast.error("Failed to load extracted images");
    } finally {
      setImagesLoading(false);
    }
  };

  const toggleImagesPanel = async () => {
    const willOpen = !imagesOpen;
    setImagesOpen(willOpen);
    if (willOpen && !imagesLoaded) await fetchExtractedImages();
  };

  const openReassignDialog = (img) => {
    setReassignTargetImage(img);
    setReassignSelectedQuestion(String(img.assigned_question_ref ?? img.assigned_question_id ?? ""));
    setReassignDialogOpen(true);
  };

  const handleReassignConfirm = async () => {
    if (!reassignTargetImage) return;
    setReassigningImage(reassignTargetImage.url);
    try {
      const targetQRef = reassignSelectedQuestion === "" ? null : reassignSelectedQuestion;
      // Use has_imported_questions (not status) so that rescanned confirmed imports
      // (status reverted to 'preview') still update DB rows, not parsed_questions.
      const isConfirmed = importData?.has_imported_questions === true;

      if (!isConfirmed) {
        // Optimistic local update for preview imports
        setQuestions((prev) => {
          const next = structuredClone(prev);
          const imgUrl = reassignTargetImage.url;
          next.forEach((q) => {
            if (q.image_url === imgUrl) q.image_url = null;
            if (Array.isArray(q.image_urls)) {
              q.image_urls = q.image_urls.filter((u) => u !== imgUrl);
              if (q.image_urls.length === 0) q.image_urls = null;
            }
            (q.sub_questions || []).forEach((sq) => {
              if (sq.image_url === imgUrl) sq.image_url = null;
              if (Array.isArray(sq.image_urls)) {
                sq.image_urls = sq.image_urls.filter((u) => u !== imgUrl);
                if (sq.image_urls.length === 0) sq.image_urls = null;
              }
            });
          });
          if (targetQRef) {
            const subMatch = String(targetQRef).match(/^(?:preview:)?p_(\d+)_sq_(\d+)$/);
            const parentMatch = String(targetQRef).match(/^(?:preview:)?p_(\d+)$/);
            if (subMatch && next[+subMatch[1]]?.sub_questions?.[+subMatch[2]]) {
              const sq = next[+subMatch[1]].sub_questions[+subMatch[2]];
              sq.image_url = imgUrl;
              sq.image_urls = Array.isArray(sq.image_urls) ? [...sq.image_urls, imgUrl] : [imgUrl];
            } else if (parentMatch && next[+parentMatch[1]]) {
              const q = next[+parentMatch[1]];
              q.image_url = imgUrl;
              q.image_urls = Array.isArray(q.image_urls) ? [...q.image_urls, imgUrl] : [imgUrl];
            }
          }
          return next;
        });
      }

      const imgUrl = reassignTargetImage.url;
      await api.post(`/admin/question-bank/import/${importId}/reassign-image`, {
        image_url: imgUrl,
        question_ref: targetQRef,
      });
      await fetchExtractedImages();

      // Optimistic questions state update for confirmed imports (preview imports already
      // updated above). Keeps the question card in sync without needing a page refresh.
      if (isConfirmed) {
        const panelMatch = targetQRef
          ? imagesPanelQuestions.find((p) => p.question_ref === targetQRef)
          : null;
        setQuestions((prev) =>
          prev.map((q) => {
            // First: clear the image from whichever question currently holds it
            const hadIt =
              (q.image_url && q.image_url === imgUrl) ||
              (Array.isArray(q.image_urls) && q.image_urls.includes(imgUrl));
            const cleared = hadIt
              ? {
                  ...q,
                  image_url: q.image_url === imgUrl ? null : q.image_url,
                  image_urls: Array.isArray(q.image_urls)
                    ? q.image_urls.filter((u) => u !== imgUrl) || null
                    : q.image_urls,
                }
              : q;
            // Then: assign to target (matched by paper_number)
            if (
              panelMatch &&
              cleared.paper_number != null &&
              String(cleared.paper_number) === String(panelMatch.paper_number)
            ) {
              return {
                ...cleared,
                image_url: imgUrl,
                image_urls: [...(cleared.image_urls || []), imgUrl],
              };
            }
            return cleared;
          })
        );
      }

      setReassignDialogOpen(false);
      setReassignTargetImage(null);
      toast.success("Image reassigned");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reassign image");
    } finally {
      setReassigningImage(null);
    }
  };

  const handleUnassignImage = async (img) => {
    setUnassigningImage(img.url);
    try {
      const isConfirmed = importData?.has_imported_questions === true;
      if (!isConfirmed) {
        setQuestions((prev) => {
          const next = structuredClone(prev);
          const imgUrl = img.url;
          next.forEach((q) => {
            if (q.image_url === imgUrl) q.image_url = null;
            if (Array.isArray(q.image_urls)) {
              q.image_urls = q.image_urls.filter((u) => u !== imgUrl);
              if (q.image_urls.length === 0) q.image_urls = null;
            }
            (q.sub_questions || []).forEach((sq) => {
              if (sq.image_url === imgUrl) sq.image_url = null;
              if (Array.isArray(sq.image_urls)) {
                sq.image_urls = sq.image_urls.filter((u) => u !== imgUrl);
                if (sq.image_urls.length === 0) sq.image_urls = null;
              }
            });
          });
          return next;
        });
      }
      await api.post(`/admin/question-bank/import/${importId}/reassign-image`, {
        image_url: img.url,
        question_ref: null,
      });
      await fetchExtractedImages();
      toast.success("Image unassigned");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to unassign image");
    } finally {
      setUnassigningImage(null);
    }
  };

  const handleRemoveExtractedImage = async (img) => {
    setRemovingExtractedImage(img.url);
    try {
      await api.post(`/admin/question-bank/import/${importId}/remove-image`, { image_url: img.url });
      if (!importData?.has_imported_questions) {
        setQuestions((prev) => {
          const next = structuredClone(prev);
          const imgUrl = img.url;
          next.forEach((q) => {
            if (q.image_url === imgUrl) q.image_url = null;
            if (Array.isArray(q.image_urls)) {
              q.image_urls = q.image_urls.filter((u) => u !== imgUrl);
              if (q.image_urls.length === 0) q.image_urls = null;
            }
            (q.sub_questions || []).forEach((sq) => {
              if (sq.image_url === imgUrl) sq.image_url = null;
              if (Array.isArray(sq.image_urls)) {
                sq.image_urls = sq.image_urls.filter((u) => u !== imgUrl);
                if (sq.image_urls.length === 0) sq.image_urls = null;
              }
            });
          });
          return next;
        });
      }
      await fetchExtractedImages();
      toast.success("Image removed");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove image");
    } finally {
      setRemovingExtractedImage(null);
    }
  };

  // Quickly unassign an image that was incorrectly attached to a sub-question (e.g. decorative wreaths)
  const quickRemoveSubQuestionImage = async (imageUrl, parentIdx, subIdx) => {
    try {
      await api.post(`/admin/question-bank/import/${importId}/reassign-image`, {
        image_url: imageUrl,
        question_ref: null, // null = unassign from all questions
      });
      setQuestions((prev) => {
        const next = structuredClone(prev);
        const sq = next[parentIdx]?.sub_questions?.[subIdx];
        if (sq) {
          sq.image_url = null;
          sq.image_urls = null;
        }
        return next;
      });
      await fetchExtractedImages();
      toast.success("Image removed from sub-question");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove image");
    }
  };

  const quickRemovePassageImage = async (imageUrl, questionIdx) => {
    try {
      await api.post(`/admin/question-bank/import/${importId}/reassign-image`, {
        image_url: imageUrl,
        question_ref: null,
      });
      setQuestions((prev) => {
        const next = structuredClone(prev);
        const q = next[questionIdx];
        if (q) {
          if (q.image_url === imageUrl) q.image_url = null;
          if (Array.isArray(q.image_urls)) {
            q.image_urls = q.image_urls.filter((u) => u !== imageUrl);
          }
        }
        return next;
      });
      await fetchExtractedImages();
      toast.success("Image removed from passage");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove image");
    }
  };

  // Change J: One-click assign to the currently-viewed question
  const quickAssignImage = async (imageUrl, questionRef) => {
    try {
      await api.post(`/admin/question-bank/import/${importId}/reassign-image`, {
        image_url: imageUrl,
        question_ref: questionRef,
      });
      toast.success("Image assigned");
      await fetchExtractedImages();
      // Optimistic local update so the question card reflects the new image immediately.
      // For confirmed imports the questionRef is 'db:N', so we match via imagesPanelQuestions;
      // for preview imports the questionRef is 'preview:p_N', matched by import_question_ref.
      const panelMatch = imagesPanelQuestions.find((p) => p.question_ref === questionRef);
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.import_question_ref === questionRef) {
            return { ...q, image_url: imageUrl, image_urls: [...(q.image_urls || []), imageUrl] };
          }
          if (
            panelMatch &&
            q.paper_number != null &&
            String(q.paper_number) === String(panelMatch.paper_number)
          ) {
            return { ...q, image_url: imageUrl, image_urls: [...(q.image_urls || []), imageUrl] };
          }
          return q;
        })
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to assign image");
    }
  };

  const handleConfirmImport = async () => {
    const previewSync = await persistDraftAndSyncPreview(questions);
    const parity = previewSync?.markdown_parity ?? importData?.markdown_parity;
    if (isMarkdownParityFailed(parity)) {
      const ranges = (parity?.missing_ranges || []).join(", ");
      toast.error(
        `Import blocked: source markdown questions missing in parsed import (${ranges || "unknown range"}).`
      );
      return;
    }
    const payload = buildConfirmPayloadFromApproved();
    if (payload.length === 0) {
      toast.error("No questions approved for import");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post(`/admin/question-bank/import/${importId}/confirm`, {
        questions: payload,
      });

      toast.success(
        `${res.data?.data?.questions_imported || payload.length} questions imported!`
      );
      navigate("/admin/question-bank/import");
    } catch (err) {
      toast.error(err.response?.data?.message || "Import failed");
    } finally {
      setSubmitting(false);
    }
  };

  const getTypeLabel = (type) =>
    QUESTION_TYPES.find((t) => t.value === type)?.label || type || "Unknown";

  // Shared edit panel — handles standalone, sub-question, and stem edits
  const renderEditPanel = () => {
    const isStemEdit = editTarget?.type === "stem";
    // Show passage line-number tools for any English grouped stem edit, even when passage text
    // is currently empty (the user is pasting a new passage from scratch).
    const isPassageLineEdit = isStemEdit && (
      isComprehensionPassageForReview(currentQuestion, examSubject) ||
      (isEnglishSubjectLabel(examSubject) && currentQuestion?.type === "grouped")
    );
    const passagePreviewWrapWidth = isPassageLineEdit
      ? computePassageWrapWidth(
          stripTagsKeepLatex(editDraft?.question_text || ""),
          currentQuestion?.sub_questions || []
        )
      : undefined;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {!isStemEdit && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={
                  QUESTION_TYPES.filter((t) => t.value !== "grouped").some(
                    (t) => t.value === editDraft.type
                  )
                    ? editDraft.type
                    : "multiple-choice"
                }
                onChange={(e) => updateDraft("type", e.target.value)}
              >
                {QUESTION_TYPES.filter((t) => t.value !== "grouped").map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }} error={!!validationErrors.difficulty}>
              <Select
                value={editDraft.difficulty}
                onChange={(e) => updateDraft("difficulty", e.target.value)}
              >
                <MenuItem value="Easy">Easy</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Hard">Hard</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              label="Marks"
              value={editDraft.default_marks ?? 1}
              onChange={(e) => {
                const normalized = normalizeMarksValue(e.target.value);
                updateDraft("default_marks", normalized ?? 1);
              }}
              sx={{ width: 80 }}
              inputProps={{ min: 1 }}
              error={!!validationErrors.default_marks}
              helperText={validationErrors.default_marks}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Passage type</InputLabel>
              <Select
                value={editDraft.passage_type || ""}
                label="Passage type"
                onChange={(e) => updateDraft("passage_type", e.target.value || null)}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="comprehension">Comprehension</MenuItem>
                <MenuItem value="context">Context</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ minWidth: 220, flex: 1 }}>
              <TopicTreeSelect
                label="Topics"
                value={(editDraft.topic_ids || []).map((id) => Number(id))}
                onChange={(ids) => updateDraft("topic_ids", ids.map(Number))}
                catalog={importTopicCatalog}
                yearId={importTopicYearId}
                onYearChange={(y) => { setImportTopicYearId(y); updateDraft("topic_ids", []); }}
                schoolYears={examSchoolYears}
                disabled={importTopicCatalog.length === 0}
              />
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
            {isStemEdit ? "Passage / shared text" : "Question text"} — source editor
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
            {isPassageLineEdit && (
              <>
                <Tooltip title="Read the original printed line numbers from the source PDF and insert exact {L…} markers">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={addingLineNumbers ? <CircularProgress size={12} /> : <AutoAwesome sx={{ fontSize: "0.9rem !important" }} />}
                      onClick={handleAiLineNumbers}
                      disabled={addingLineNumbers}
                      sx={{ py: 0.25, px: 1, fontSize: "0.7rem", textTransform: "none" }}
                    >
                      {addingLineNumbers ? "Reading PDF…" : "AI line numbers"}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Word-wrap the passage and number every 5th line — then tweak the {L…} markers by hand">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleInsertLineNumbers}
                    sx={{ py: 0.25, px: 1, fontSize: "0.7rem", textTransform: "none" }}
                  >
                    Insert line numbers
                  </Button>
                </Tooltip>
                <Tooltip title="Place cursor in the passage, type a line number, then click Insert to add {L#} at that position">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={manualLineNum}
                      onChange={(e) => setManualLineNum(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleInsertManualLineNum(); }}
                      placeholder="L#"
                      inputProps={{ min: 1, max: 999, style: { padding: "2px 6px", fontSize: "0.7rem", width: 44, textAlign: "center" } }}
                      sx={{ "& .MuiOutlinedInput-root": { height: 24 } }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleInsertManualLineNum}
                      disabled={!manualLineNum || parseInt(manualLineNum, 10) < 1}
                      sx={{ py: 0.25, px: 0.75, fontSize: "0.7rem", textTransform: "none", minWidth: 0 }}
                    >
                      Insert {manualLineNum ? `{L${manualLineNum}}` : "{L#}"}
                    </Button>
                  </Box>
                </Tooltip>
                {/\{L\d+\}/.test(editDraft?.question_text || "") && (
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    onClick={handleClearLineNumbers}
                    sx={{ py: 0.25, px: 0.75, fontSize: "0.7rem", textTransform: "none" }}
                  >
                    Clear
                  </Button>
                )}
              </>
            )}
            <Button
              size="small"
              variant="text"
              startIcon={editLivePreview ? <VisibilityOffIcon sx={{ fontSize: "1rem !important" }} /> : <VisibilityIcon sx={{ fontSize: "1rem !important" }} />}
              onClick={() => setEditLivePreview((v) => !v)}
              sx={{ textTransform: "none", fontSize: "0.75rem" }}
            >
              {editLivePreview ? "Hide" : "Show"} live preview
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              flex: editLivePreview ? "1 1 45%" : "1 1 100%",
              minWidth: 0,
              "& .ql-toolbar": { borderRadius: "6px 6px 0 0" },
              "& .ql-container": {
                minHeight: isStemEdit ? 280 : 140,
                borderRadius: "0 0 6px 6px",
                fontSize: "0.95rem",
              },
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
              {isStemEdit ? "Passage / shared text (what students see)" : "Question text (what students see)"}
            </Typography>
            <QuillR2Editor
              ref={isStemEdit ? passageQuillRef : undefined}
              value={editDraft.question_text || ""}
              onChange={(val) => updateDraft("question_text", normalizeQuillValue(val))}
              onChangeSelection={isStemEdit ? (range) => { if (range) savedPassageSelectionRef.current = range; } : undefined}
              modules={isStemEdit ? PASSAGE_QUILL_MODULES : QUESTION_TEXT_QUILL_MODULES}
              formats={isStemEdit ? PASSAGE_QUILL_FORMATS : QUESTION_TEXT_QUILL_FORMATS}
              placeholder={isStemEdit ? "Paste the full reading passage here..." : "Write or paste question text. Use $...$ for inline LaTeX."}
              showMathTools
            />
            <Typography variant="caption" color={validationErrors.question_text ? "error.main" : "text.secondary"} sx={{ mt: 0.5, display: "block" }}>
              {validationErrors.question_text || (isStemEdit
                ? `${(editDraft.question_text || "").replace(/<[^>]*>/g, "").length} characters — typical reading passage is 400–1500 characters`
                : "LaTeX is supported using $...$ or $$...$$")}
            </Typography>
          </Box>
          {editLivePreview && (
            <Paper
              variant="outlined"
              sx={{
                flex: "1 1 45%",
                minWidth: { xs: "100%", lg: 280 },
                p: 1.5,
                bgcolor: "grey.50",
                maxHeight: isStemEdit ? 560 : 340,
                overflow: "auto",
                "& mjx-container": { fontSize: "0.95rem" },
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                Live preview (MathJax — same rendering as the paper view)
              </Typography>
              {(editDraft.section_instructions || "").trim() ? (
                <Box sx={{ mb: 1.5, p: 1, bgcolor: "#fffbeb", borderRadius: 1, border: "1px solid #fde047" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#854d0e", display: "block", mb: 0.5 }}>
                    Section instructions (preview)
                  </Typography>
                  <FormatInstructions
                    text={editDraft.section_instructions}
                    typographySx={{ color: "#713f12", fontSize: "0.85rem" }}
                  />
                </Box>
              ) : null}
              {(editDraft.question_text || "").trim() ? (
                isPassageLineEdit ? (
                  <PassageWithLineNumbers
                    text={editDraft.question_text || ""}
                    showAllFallbackLineNumbers
                    wrapWidth={passagePreviewWrapWidth}
                  />
                ) : (
                  <HtmlWithLatex html={stripTagsKeepLatex(editDraft.question_text || "")} />
                )
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Type or paste text to see the formatted preview.
                </Typography>
              )}
            </Paper>
          )}
        </Box>

        {!isStemEdit && (
          <>
            {/* Section fields */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                Section (optional)
              </Typography>
              <TextField
                size="small"
                label="Section title"
                fullWidth
                value={editDraft.section_title ?? ""}
                onChange={(e) => updateDraft("section_title", e.target.value || null)}
                placeholder="e.g. Section A"
              />
              <TextField
                size="small"
                label="Section instructions"
                fullWidth
                multiline
                minRows={2}
                value={editDraft.section_instructions ?? ""}
                onChange={(e) => updateDraft("section_instructions", e.target.value || null)}
                placeholder="Instructions shown at the start of this section (optional)"
              />
            </Box>

            {/* Explanation */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  Explanation (optional)
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={generatingExplanation
                    ? <CircularProgress size={12} />
                    : <AutoAwesome sx={{ fontSize: "0.9rem !important" }} />}
                  onClick={handleGenerateExplanation}
                  disabled={generatingExplanation}
                  sx={{ py: 0.25, px: 1, fontSize: "0.7rem", textTransform: "none" }}
                >
                  {generatingExplanation ? "Generating…" : "AI Generate"}
                </Button>
              </Box>
              <Box
                sx={{
                  "& .ql-toolbar": { borderRadius: "6px 6px 0 0" },
                  "& .ql-container": { minHeight: 120, borderRadius: "0 0 6px 6px", fontSize: "0.9rem" },
                }}
              >
                <QuillR2Editor
                  value={editDraft.explanation ?? ""}
                  onChange={(val) => updateDraft("explanation", normalizeQuillValue(val))}
                  modules={EXPL_QUILL_MODULES}
                  formats={EXPL_QUILL_FORMATS}
                  placeholder="Write step-by-step explanation for students..."
                  style={{ minHeight: 120 }}
                  showMathTools
                />
              </Box>
            </Box>
          </>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>
            {isStemEdit
              ? "Images and diagrams (students see these with the shared stem — Maths, English, NVR/VR)"
              : "Images and diagrams (students see these with the question text)"}
          </Typography>
          <Box>
              {(() => {
                const imgs = Array.isArray(editDraft.image_urls) && editDraft.image_urls.filter(Boolean).length > 0
                  ? editDraft.image_urls.filter(Boolean)
                  : editDraft.image_url
                    ? [editDraft.image_url]
                    : [];
                const removeImg = (urlToRemove) => {
                  setEditDraft((prev) => {
                    const newUrls = imgs.filter((u) => u !== urlToRemove);
                    return {
                      ...prev,
                      image_url: newUrls[0] || null,
                      image_urls: newUrls.length > 0 ? newUrls : null,
                    };
                  });
                };
                return imgs.length > 0 ? (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                    {imgs.map((url, idx) => (
                      <Box
                        key={url}
                        draggable
                        onDragStart={() => { setDragImageIdx(idx); draggedExtractedImageRef.current = null; }}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverImageIdx(idx); }}
                        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleEditImageReorder(dragImageIdx, idx); setDragImageIdx(null); setDragOverImageIdx(null); }}
                        onDragEnd={() => { setDragImageIdx(null); setDragOverImageIdx(null); }}
                        sx={{
                          position: "relative",
                          border: dragOverImageIdx === idx && dragImageIdx !== idx ? "2px dashed #6366f1" : "1px solid #e0e0e0",
                          borderRadius: 1,
                          overflow: "hidden",
                          width: 110,
                          height: 110,
                          opacity: dragImageIdx === idx ? 0.4 : 1,
                          cursor: "grab",
                          transition: "opacity 0.15s, border-color 0.15s",
                        }}
                      >
                        <img
                          src={url}
                          alt={`${idx + 1}`}
                          style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeImg(url)}
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            bgcolor: "rgba(255,255,255,0.85)",
                            color: "#f44336",
                            p: 0.25,
                            "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: "rgba(0,0,0,0.5)",
                            py: 0.25,
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="caption" sx={{ color: "white", fontSize: "0.6rem" }}>
                            {idx === 0 ? "Primary" : `#${idx + 1}`}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : null;
              })()}
              <input
                type="file"
                accept="image/*"
                ref={imageInputRef}
                style={{ display: "none" }}
                onChange={handleImageUpload}
              />
              <Box
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (draggedExtractedImageRef.current) {
                    if (!isDropOverEditImages) setIsDropOverEditImages(true);
                  } else {
                    if (!imageDropActive) setImageDropActive(true);
                  }
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (draggedExtractedImageRef.current) setIsDropOverEditImages(true);
                  else setImageDropActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setImageDropActive(false);
                  setIsDropOverEditImages(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // If an extracted image card was dragged here, assign it
                  if (draggedExtractedImageRef.current && currentQuestionImagesRef) {
                    const imgUrl = draggedExtractedImageRef.current;
                    draggedExtractedImageRef.current = null;
                    setIsDropOverEditImages(false);
                    quickAssignImage(imgUrl, currentQuestionImagesRef);
                    return;
                  }
                  handleQuestionImageDrop(e);
                }}
                onClick={() => !imageUploading && imageInputRef.current?.click()}
                sx={{
                  mb: 1,
                  border: "1px dashed",
                  borderColor: (imageDropActive || isDropOverEditImages) ? "primary.main" : "divider",
                  bgcolor: (imageDropActive || isDropOverEditImages) ? "primary.50" : "grey.50",
                  borderRadius: 1.5,
                  px: 2,
                  py: 2.5,
                  minHeight: 140,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  cursor: imageUploading ? "not-allowed" : "pointer",
                  transition: "all 0.15s",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "center" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                    Drag and drop image here (larger drop zone), or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    You can also drag images from the Extracted Images panel above
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={imageUploading ? <CircularProgress size={16} /> : <CloudUpload />}
                  onClick={() => imageInputRef.current?.click()}
                  disabled={imageUploading || generatingDiagram}
                  sx={{ textTransform: "none" }}
                >
                  {imageUploading ? "Uploading..." : "Add Image"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  startIcon={generatingDiagram ? <CircularProgress size={16} /> : <AutoAwesome />}
                  disabled={imageUploading || generatingDiagram}
                  onClick={() => {
                    const path =
                      editTarget?.type === "subquestion"
                        ? { type: "subquestion", parentIndex: editTarget.parentIndex, subIndex: editTarget.subIndex }
                        : editTarget?.type === "stem"
                          ? { type: "stem", parentIndex: editTarget.parentIndex }
                          : { type: "standalone", index: currentIndex };
                    handleGenerateDiagram(path);
                  }}
                  sx={{ textTransform: "none" }}
                  title="Generate diagram using AI (appends to images)"
                >
                  {generatingDiagram ? "Generating..." : "Generate Diagram"}
                </Button>
              </Box>
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={8}
            size="small"
            label="Image description (optional)"
            value={editDraft.image_description ?? ""}
            onChange={(e) => updateDraft("image_description", e.target.value.trim() ? e.target.value : null)}
            sx={{ mt: 1 }}
            placeholder="Describe diagrams for accessibility and verification tools"
          />
        </Box>

        {!isStemEdit && (
          <>
            {/* Options editor (MCQ types) */}
            {["multiple-choice", "multiple-select"].includes(editDraft.type) && (() => {
              const mcqOpts = (editDraft.options || []).map((o) => ({
                text: o.text || o.option_text || '',
                image_url: o.image_url || null,
              }));
              const mcqCorrectIndex = (editDraft.options || []).findIndex((o) => o.is_correct);
              const mcqCorrectIndices = (editDraft.options || []).reduce(
                (acc, o, i) => (o.is_correct ? [...acc, i] : acc), []
              );
              const setMcqOptions = (newOpts) =>
                setEditDraft((prev) => ({
                  ...prev,
                  options: newOpts.map((o, i) => ({
                    ...(prev.options?.[i] || {}),
                    text: o.text,
                    option_text: o.text,
                    image_url: o.image_url ?? null,
                  })),
                }));
              const setMcqCorrectIndex = (idx) =>
                setEditDraft((prev) => ({
                  ...prev,
                  options: (prev.options || []).map((o, i) => ({ ...o, is_correct: i === idx })),
                }));
              const setMcqCorrectIndices = (idxs) =>
                setEditDraft((prev) => ({
                  ...prev,
                  options: (prev.options || []).map((o, i) => ({ ...o, is_correct: idxs.includes(i) })),
                }));
              return (
                <McqOptions
                  options={mcqOpts}
                  setOptions={setMcqOptions}
                  correctIndex={mcqCorrectIndex >= 0 ? mcqCorrectIndex : 0}
                  setCorrectIndex={setMcqCorrectIndex}
                  correctIndices={mcqCorrectIndices}
                  setCorrectIndices={setMcqCorrectIndices}
                  isMultiSelect={editDraft.type === 'multiple-select'}
                />
              );
            })()}
            {editDraft.type === "true-false" && (() => {
              const mcqOpts = (editDraft.options || []).map((o) => ({
                text: o.text || o.option_text || '',
                image_url: o.image_url || null,
              }));
              const mcqCorrectIndex = (editDraft.options || []).findIndex((o) => o.is_correct);
              const setMcqOptions = (newOpts) =>
                setEditDraft((prev) => ({
                  ...prev,
                  options: newOpts.map((o, i) => ({
                    ...(prev.options?.[i] || {}),
                    text: o.text,
                    option_text: o.text,
                    image_url: o.image_url ?? null,
                  })),
                }));
              const setMcqCorrectIndex = (idx) =>
                setEditDraft((prev) => ({
                  ...prev,
                  options: (prev.options || []).map((o, i) => ({ ...o, is_correct: i === idx })),
                }));
              return (
                <McqOptions
                  options={mcqOpts}
                  setOptions={setMcqOptions}
                  correctIndex={mcqCorrectIndex >= 0 ? mcqCorrectIndex : 0}
                  setCorrectIndex={setMcqCorrectIndex}
                  correctIndices={[]}
                  setCorrectIndices={() => {}}
                  isMultiSelect={false}
                />
              );
            })()}

            {/* Essay */}
            {editDraft.type === "essay" && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Model Answer
                </Typography>
                <EssayAnswer
                  value={editDraft.answer_data?.correct_answer_text || ""}
                  onChange={(value) =>
                    updateDraft("answer_data", { ...editDraft.answer_data, correct_answer_text: value })
                  }
                  showMathTools
                />
              </Box>
            )}

            {/* Fill-in-the-blank */}
            {editDraft.type === "fill-in-the-blank" && (() => {
              const blanks = getFillBlanks(editDraft);
              const fillMarksMode = editDraft.answer_data?.marks_mode === "fixed" ? "fixed" : "sum";
              const fillFixedTotal = editDraft.answer_data?.marks_total ?? editDraft.default_marks ?? 4;
              return (
                <Box>
                  <FillTypeMarksControls
                    marksMode={fillMarksMode}
                    onMarksModeChange={(mode) => updateFillMarksConfig(mode, fillFixedTotal)}
                    fixedTotal={fillFixedTotal}
                    onFixedTotalChange={(val) => updateFillMarksConfig("fixed", val)}
                    blankCount={blanks.length}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Blanks
                  </Typography>
                  {blanks.map((blank, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography variant="body2"><strong>Blank {idx + 1}</strong></Typography>
                        <IconButton size="small" onClick={() => moveFillBlankUp(idx)} disabled={idx === 0}>↑</IconButton>
                        <IconButton size="small" onClick={() => moveFillBlankDown(idx)} disabled={idx === blanks.length - 1}>↓</IconButton>
                        <TextField
                          type="number"
                          size="small"
                          label="Marks"
                          value={blank.marks ?? 1}
                          onChange={(e) => updateFillBlankMarks(idx, e.target.value)}
                          inputProps={{ min: 1 }}
                          sx={{ width: 84, ml: "auto", display: fillMarksMode === "fixed" ? "none" : undefined }}
                        />
                        {blanks.length > 1 && (
                          <IconButton size="small" color="error" onClick={() => removeFillBlank(idx)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.4 }}>
                        Blank label (e.g. (a), Part i, equation)
                      </Typography>
                      <Box sx={{ mb: 1 }}>
                        <BlankAnswerEditor
                          value={blank.label ?? ""}
                          onChange={(val) => {
                            const updated = blanks.map((b, j) => j === idx ? { ...b, label: val } : b);
                            saveFillBlanks(updated);
                          }}
                          placeholder="Optional label shown with this answer slot"
                        />
                      </Box>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={blank.labelPosition || inferBlankLabelPosition(blank.label)}
                        onChange={(_, val) => { if (val) updateFillBlankLabelPosition(idx, val); }}
                        sx={{ mb: 1 }}
                      >
                        <ToggleButton value="before" sx={{ px: 1.25, fontSize: "0.72rem", textTransform: "none" }}>Before blank</ToggleButton>
                        <ToggleButton value="after" sx={{ px: 1.25, fontSize: "0.72rem", textTransform: "none" }}>After blank</ToggleButton>
                      </ToggleButtonGroup>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.4 }}>
                        Primary answer
                      </Typography>
                      <BlankAnswerEditor
                        value={blank.text}
                        onChange={(val) => updateFillBlankText(idx, val)}
                        placeholder="Primary answer"
                      />
                      {(blank.alts || []).map((alt, ai) => (
                        <Box key={ai} sx={{ mt: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: "block", mb: 0.4 }}>
                                Alternative answer {ai + 1}
                              </Typography>
                              <BlankAnswerEditor
                                value={alt}
                                onChange={(val) => updateFillBlankAlt(idx, ai, val)}
                                placeholder={`Alternative ${ai + 1}`}
                              />
                            </Box>
                            <IconButton size="small" color="error" onClick={() => removeFillBlankAlt(idx, ai)} sx={{ mt: 3 }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                      <Button size="small" startIcon={<Add />} onClick={() => addFillBlankAlt(idx)} sx={{ textTransform: "none", mt: 1 }}>
                        Add Alternative
                      </Button>
                    </Paper>
                  ))}
                  <Button size="small" startIcon={<Add />} onClick={addFillBlank} sx={{ textTransform: "none" }}>
                    Add Blank
                  </Button>
                </Box>
              );
            })()}

            {/* Multi-fill */}
            {editDraft.type === "multi-fill" && (() => {
              const blanks = getFillBlanks(editDraft);
              const fillMarksMode = editDraft.answer_data?.marks_mode === "fixed" ? "fixed" : "sum";
              const fillFixedTotal = editDraft.answer_data?.marks_total ?? editDraft.default_marks ?? 4;
              return (
                <Box>
                  <FillTypeMarksControls
                    marksMode={fillMarksMode}
                    onMarksModeChange={(mode) => updateFillMarksConfig(mode, fillFixedTotal)}
                    fixedTotal={fillFixedTotal}
                    onFixedTotalChange={(val) => updateFillMarksConfig("fixed", val)}
                    blankCount={blanks.length}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Blanks (Multi-fill)
                  </Typography>
                  {blanks.map((blank, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography variant="body2"><strong>Blank {idx + 1}</strong></Typography>
                        <IconButton size="small" onClick={() => moveFillBlankUp(idx)} disabled={idx === 0}>↑</IconButton>
                        <IconButton size="small" onClick={() => moveFillBlankDown(idx)} disabled={idx === blanks.length - 1}>↓</IconButton>
                        <TextField
                          type="number"
                          size="small"
                          label="Marks"
                          value={blank.marks ?? 1}
                          onChange={(e) => updateFillBlankMarks(idx, e.target.value)}
                          inputProps={{ min: 1 }}
                          sx={{ width: 84, ml: "auto", display: fillMarksMode === "fixed" ? "none" : undefined }}
                        />
                        {blanks.length > 1 && (
                          <IconButton size="small" color="error" onClick={() => removeFillBlank(idx)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.4 }}>
                        Blank label (e.g. (a), Part i, equation)
                      </Typography>
                      <Box sx={{ mb: 1 }}>
                        <BlankAnswerEditor
                          value={blank.label ?? ""}
                          onChange={(val) => {
                            const updated = blanks.map((b, j) => j === idx ? { ...b, label: val } : b);
                            saveFillBlanks(updated);
                          }}
                          placeholder="Optional label shown with this answer slot"
                        />
                      </Box>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={blank.labelPosition || inferBlankLabelPosition(blank.label)}
                        onChange={(_, val) => { if (val) updateFillBlankLabelPosition(idx, val); }}
                        sx={{ mb: 1 }}
                      >
                        <ToggleButton value="before" sx={{ px: 1.25, fontSize: "0.72rem", textTransform: "none" }}>Before blank</ToggleButton>
                        <ToggleButton value="after" sx={{ px: 1.25, fontSize: "0.72rem", textTransform: "none" }}>After blank</ToggleButton>
                      </ToggleButtonGroup>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.4 }}>
                        Primary answer
                      </Typography>
                      <BlankAnswerEditor
                        value={blank.text}
                        onChange={(val) => updateFillBlankText(idx, val)}
                        placeholder="Primary answer"
                      />
                      {(blank.alts || []).map((alt, ai) => (
                        <Box key={ai} sx={{ mt: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: "block", mb: 0.4 }}>
                                Alternative answer {ai + 1}
                              </Typography>
                              <BlankAnswerEditor
                                value={alt}
                                onChange={(val) => updateFillBlankAlt(idx, ai, val)}
                                placeholder={`Alternative ${ai + 1}`}
                              />
                            </Box>
                            <IconButton size="small" color="error" onClick={() => removeFillBlankAlt(idx, ai)} sx={{ mt: 3 }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                      <Button size="small" startIcon={<Add />} onClick={() => addFillBlankAlt(idx)} sx={{ textTransform: "none", mt: 1 }}>
                        Add Alternative
                      </Button>
                    </Paper>
                  ))}
                  <Button size="small" startIcon={<Add />} onClick={addFillBlank} sx={{ textTransform: "none" }}>
                    Add Blank
                  </Button>
                </Box>
              );
            })()}

            {editDraft.type === "table" && (
              <Box>
                <FillTypeMarksControls
                  marksMode={editDraft.answer_data?.marks_mode === "fixed" ? "fixed" : "sum"}
                  onMarksModeChange={(mode) =>
                    updateFillMarksConfig(mode, editDraft.answer_data?.marks_total ?? editDraft.default_marks ?? 4)
                  }
                  fixedTotal={editDraft.answer_data?.marks_total ?? editDraft.default_marks ?? 4}
                  onFixedTotalChange={(val) => updateFillMarksConfig("fixed", val)}
                  blankCount={(editDraft.answer_data?.correct_blanks || []).length}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Table (fill-in cells)
                </Typography>
                <TableFillEditor
                  blanks={
                    Array.isArray(editDraft.answer_data?.correct_blanks)
                      ? editDraft.answer_data.correct_blanks.map((b) => (Array.isArray(b) ? b : [b]))
                      : Array.from({ length: 9 }, () => [""])
                  }
                  setBlanks={(correct_blanks) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      answer_data: { ...(prev.answer_data || {}), correct_blanks },
                    }))
                  }
                  blankMarks={editDraft.answer_data?.blank_marks || []}
                  setBlankMarks={(blank_marks) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      answer_data: { ...(prev.answer_data || {}), blank_marks },
                    }))
                  }
                  blankLabels={editDraft.answer_data?.blank_labels || []}
                  setBlankLabels={(blank_labels) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      answer_data: { ...(prev.answer_data || {}), blank_labels },
                    }))
                  }
                  tableLayout={editDraft.answer_data?.table_layout?.grid ? editDraft.answer_data.table_layout : DEFAULT_GRID()}
                  setTableLayout={(table_layout) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      answer_data: { ...(prev.answer_data || {}), table_layout },
                    }))
                  }
                  showMathTools
                />
              </Box>
            )}

            {editDraft.type === "image-fill" && (
              <Box>
                <FillTypeMarksControls
                  marksMode={editDraft.answer_data?.marks_mode === "fixed" ? "fixed" : "sum"}
                  onMarksModeChange={(mode) =>
                    updateFillMarksConfig(mode, editDraft.answer_data?.marks_total ?? editDraft.default_marks ?? 4)
                  }
                  fixedTotal={editDraft.answer_data?.marks_total ?? editDraft.default_marks ?? 4}
                  onFixedTotalChange={(val) => updateFillMarksConfig("fixed", val)}
                  blankCount={(editDraft.answer_data?.correct_blanks || []).length}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Image fill (drag answer boxes)
                </Typography>
                <ImageOverlayFillEditor
                  blanks={
                    Array.isArray(editDraft.answer_data?.correct_blanks)
                      ? editDraft.answer_data.correct_blanks.map((b) => (Array.isArray(b) ? b : [b]))
                      : [[""]]
                  }
                  setBlanks={(correct_blanks) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      answer_data: { ...(prev.answer_data || {}), correct_blanks },
                    }))
                  }
                  blankMarks={editDraft.answer_data?.blank_marks || []}
                  setBlankMarks={(blank_marks) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      answer_data: { ...(prev.answer_data || {}), blank_marks },
                    }))
                  }
                  blankLabels={editDraft.answer_data?.blank_labels || []}
                  setBlankLabels={(blank_labels) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      answer_data: { ...(prev.answer_data || {}), blank_labels },
                    }))
                  }
                  overlayLayout={
                    editDraft.answer_data?.overlay_layout?.image_url
                    || editDraft.answer_data?.overlay_layout?.mode
                    || (editDraft.answer_data?.overlay_layout?.blanks?.length > 0)
                      ? editDraft.answer_data.overlay_layout
                      : { image_url: "", blanks: [] }
                  }
                  setOverlayLayout={(overlay_layout) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      answer_data: { ...(prev.answer_data || {}), overlay_layout },
                    }))
                  }
                />
                {validationErrors.answer_data && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {validationErrors.answer_data}
                  </FormHelperText>
                )}
              </Box>
            )}

            {/* Short-answer */}
            {editDraft.type === "short-answer" && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Expected Answer
                </Typography>
                <ShortAnswerEditor
                  value={editDraft.answer_data?.correct_answer_text || ""}
                  onChange={(value) =>
                    updateDraft("answer_data", { ...editDraft.answer_data, correct_answer_text: value })
                  }
                  showMathTools
                />
              </Box>
            )}

            {/* Matching */}
            {editDraft.type === "matching" && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Matching Pairs
                </Typography>
                <MatchingEditor
                  options={editDraft.options || [{ text: "", match_text: "" }, { text: "", match_text: "" }]}
                  setOptions={(options) => updateDraft("options", options)}
                />
              </Box>
            )}

            {/* Ordering */}
            {editDraft.type === "ordering" && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Items (in correct order)
                </Typography>
                <OrderingEditor
                  options={editDraft.options || [{ text: "" }, { text: "" }]}
                  setOptions={(options) => updateDraft("options", options)}
                />
              </Box>
            )}
            {editDraft.type === "drawing" && (() => {
              const spec = editDraft.answer_data?.solution_geometry?.interactive || null;
              const mode = spec?.kind || "free";
              const setSpec = (s) =>
                updateDraft("answer_data", {
                  ...(editDraft.answer_data || {}),
                  solution_geometry: s ? { interactive: s } : null,
                });
              const drawImage = (editDraft.image_urls && editDraft.image_urls[0]) || editDraft.image_url || "";
              return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Answer mode</Typography>
                  <FormControl size="small" sx={{ maxWidth: 320 }}>
                    <InputLabel>Answer mode</InputLabel>
                    <Select
                      label="Answer mode"
                      value={mode}
                      onChange={(e) => setSpec(e.target.value === "free" ? null : { kind: e.target.value })}
                    >
                      <MenuItem value="free">Free draw (AI vision)</MenuItem>
                      <MenuItem value="shade">Shade regions (tap to fill)</MenuItem>
                      <MenuItem value="points">Place points / reflect (tap grid)</MenuItem>
                      <MenuItem value="rect">Draw rectangle (area match)</MenuItem>
                      <MenuItem value="lines">Draw lines (symmetry / mirror)</MenuItem>
                      <MenuItem value="polygon">Draw shape by area (triangle / square)</MenuItem>
                      <MenuItem value="glyph">Place / reflect / rotate letters</MenuItem>
                      <MenuItem value="segments">Join points (segments)</MenuItem>
                      <MenuItem value="pictogram">Pictogram (fill icons)</MenuItem>
                      <MenuItem value="drag-shape">Drag-copy shape (translation)</MenuItem>
                      <MenuItem value="reflect">Reflect shape (auto-computed)</MenuItem>
                      <MenuItem value="rotate">Rotate shape (auto-computed)</MenuItem>
                      <MenuItem value="clock">Clock hands (set the time)</MenuItem>
                      <MenuItem value="marker">Arrow / bar marker (value on axis)</MenuItem>
                    </Select>
                  </FormControl>
                  {mode === "free" ? (
                    <Typography variant="caption" color="text.secondary">
                      Free draw: the student draws on the pad; grading uses AI vision. Pick an interactive
                      mode above to grade instantly with no AI.
                    </Typography>
                  ) : (
                    <InteractiveDiagramEditor
                      kind={mode}
                      spec={spec}
                      onChange={setSpec}
                      imageUrl={drawImage}
                    />
                  )}
                </Box>
              );
            })()}
          </>
        )}

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button variant="outlined" size="small" startIcon={<Close />} onClick={cancelEdit} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          {!isStemEdit && (
            <>
              <Button variant="outlined" size="small" onClick={undoEdit} sx={{ textTransform: "none" }}>
                Undo
              </Button>
              <Button
                variant="outlined" size="small" color="warning"
                onClick={resetQuestion}
                disabled={!modifiedQuestions.has(currentIndex)}
                sx={{ textTransform: "none" }}
              >
                Reset to Original
              </Button>
            </>
          )}
          <Button variant="contained" size="small" startIcon={<Save />} onClick={saveEdit} sx={{ textTransform: "none" }}>
            Save
          </Button>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <CircularProgress size={24} />
        <Typography>Loading structured questions...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: "auto" }}>
        <Alert severity="error">
          <AlertTitle>Unable to load job</AlertTitle>
          {error}
        </Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBack />} onClick={() => navigate("/admin/question-bank/import")}>
          Back to imports
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Review structured questions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Inspect the structured payload for job #{importId} and approve questions before continuing.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/admin/question-bank/import")}
          sx={{ textTransform: "none" }}
        >
          Back to imports
        </Button>
      </Box>

      {/* Enhanced Toolbar */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          {qualityReport && <QualityBadge qualityReport={qualityReport} />}
          <Chip
            label={`${approvedSet.size}/${questions.length} approved`}
            color={approvedSet.size === questions.length ? "success" : "default"}
            size="small"
            variant="outlined"
          />
          {accuracyMetrics && (
            <Chip
              label={`${accuracyMetrics.questions_with_answers || 0}/${accuracyMetrics.questions_extracted || 0} with answers`}
              size="small"
              variant="outlined"
              color="info"
            />
          )}
          {lastSaved && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
              {autoSaving ? "Saving..." : `Auto-saved ${formatDistanceToNow(new Date(lastSaved), { addSuffix: true })}`}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 0.5, ml: lastSaved ? 0 : "auto" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleGenerateAllExplanations}
              disabled={generatingAllExplanations || generatingExplanation || questions.length === 0}
              startIcon={generatingAllExplanations ? <CircularProgress size={14} /> : <AutoAwesome />}
              sx={{ textTransform: "none", fontSize: "0.75rem" }}
            >
              {generatingAllExplanations
                ? `Explain ${explainAllProgress.done}/${explainAllProgress.total}`
                : "Explain All"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleAiImproveAll}
              disabled={improvingAll || improving}
              startIcon={improvingAll ? <CircularProgress size={14} /> : <AutoAwesome />}
              sx={{ textTransform: "none", fontSize: "0.75rem" }}
            >
              {improvingAll ? "Improving..." : "Improve All"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleGenerateAnswers()}
              disabled={generatingAnswers}
              startIcon={generatingAnswers ? <CircularProgress size={14} /> : <AutoFixHigh />}
              sx={{ textTransform: "none", fontSize: "0.75rem" }}
            >
              {generatingAnswers ? "Generating..." : "Gen Answers"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleValidate}
              disabled={validating}
              startIcon={validating ? <CircularProgress size={14} /> : <CheckCircle />}
              sx={{ textTransform: "none", fontSize: "0.75rem" }}
            >
              {validating ? "Validating..." : "Validate"}
            </Button>
            <PdfToggleButton showPdf={showPdf} onToggle={() => { setShowPdf(!showPdf); if (!showPdf) setShowPaperView(false); }} />
            <PaperViewToggleButton show={showPaperView} onToggle={() => { setShowPaperView(!showPaperView); if (!showPaperView) setShowPdf(false); }} />
            {/* Supplementary Booklet toggle — shown when auto-detected or manually uploaded */}
            {supplementaryBookletUrl && (
              <Tooltip title="View supplementary booklet / sources">
                <Button
                  variant={showSupplementary ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setShowSupplementary((s) => !s)}
                  startIcon={<MenuBookIcon />}
                  sx={{ textTransform: "none", fontSize: "0.75rem" }}
                >
                  Supplementary
                </Button>
              </Tooltip>
            )}
            {/* Upload Supplementary Booklet — admin override / manual upload */}
            <Tooltip title="Upload supplementary booklet PDF (override auto-detection)">
              <Button
                component="label"
                size="small"
                variant="outlined"
                disabled={uploadingSupplementary}
                startIcon={uploadingSupplementary ? <CircularProgress size={14} /> : <UploadFileIcon />}
                sx={{ textTransform: "none", fontSize: "0.75rem" }}
              >
                {uploadingSupplementary ? "Uploading..." : "Upload Booklet"}
                <input type="file" hidden accept=".pdf" onChange={handleUploadSupplementary} />
              </Button>
            </Tooltip>
            {/* Rescan — available for all reviewable imports */}
            <Button
              variant="outlined"
              size="small"
              onClick={handleRescan}
              disabled={rescanning}
              startIcon={rescanning ? <CircularProgress size={14} /> : <RefreshIcon />}
              sx={{ textTransform: "none", fontSize: "0.75rem" }}
            >
              {rescanning ? "Rescanning..." : "Rescan"}
            </Button>
            {/* AI auto-tag — only available after confirmation */}
            {importData?.has_imported_questions && (
              <Button
                variant="outlined"
                size="small"
                color="info"
                onClick={async () => {
                  try {
                    const res = await api.get(`/admin/question-bank/import/${importId}/status`);
                    const ids = res.data?.data?.imported_question_ids || [];
                    if (ids.length === 0) {
                      toast.info("No imported questions found yet.");
                      return;
                    }
                    await api.post("/admin/question-bank/auto-tag-bulk", { ids, skip_if_already_tagged: true });
                    toast.success(`Auto-tag job queued for ${ids.length} questions`);
                  } catch (err) {
                    toast.error(err.response?.data?.message || "Failed to queue auto-tag");
                  }
                }}
                sx={{ textTransform: "none", fontSize: "0.75rem" }}
                title="Re-run AI topic tagging across all questions imported from this paper"
              >
                AI auto-tag this import
              </Button>
            )}
            {/* Apply Updates — only available when import has already-confirmed DB rows */}
            {importData?.has_imported_questions && (
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                onClick={handleApplyUpdates}
                disabled={applyingUpdates}
                startIcon={applyingUpdates ? <CircularProgress size={14} /> : <SyncIcon />}
                sx={{ textTransform: "none", fontSize: "0.75rem" }}
                title="Sync rescanned question text/images/answers into the confirmed question bank records"
              >
                {applyingUpdates
                  ? "Applying..."
                  : applyUpdatesResult
                    ? (() => {
                        const r = applyUpdatesResult;
                        const items = [
                          (r.answers_from_markdown ?? 0) > 0 && { n: r.answers_from_markdown, label: "paper ans" },
                          (r.meta_updated ?? 0) > 0 && { n: r.meta_updated, label: "type" },
                          (r.image_fixed ?? 0) > 0 && { n: r.image_fixed, label: "img" },
                          (r.diagrams_generated ?? 0) > 0 && { n: r.diagrams_generated, label: "svg" },
                          (r.text_updated ?? 0) > 0 && { n: r.text_updated, label: "txt" },
                          (r.answer_data_updated ?? 0) > 0 && { n: r.answer_data_updated, label: "ans" },
                          (r.decimal_fixed ?? 0) > 0 && { n: r.decimal_fixed, label: "dec" },
                          (r.options_fixed ?? 0) > 0 && { n: r.options_fixed, label: "opts" },
                        ].filter(Boolean);
                        if (items.length === 0) return "✓ up-to-date";
                        return (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                            <Typography component="span" sx={{ fontSize: "0.7rem", fontWeight: 700, color: "success.main", mr: 0.25 }}>✓</Typography>
                            {items.map(({ n, label }, i) => (
                              <Box key={i} sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                                <Box component="span" sx={{ bgcolor: "#1a1a1a", color: "#fff", fontWeight: 700, fontSize: "0.65rem", fontFamily: "monospace", px: 0.6, py: 0.1, borderRadius: 0.4, letterSpacing: "0.03em" }}>{n}</Box>
                                <Typography component="span" sx={{ fontSize: "0.65rem", color: "text.secondary" }}>{label}</Typography>
                                {i < items.length - 1 && <Typography component="span" sx={{ fontSize: "0.65rem", color: "text.disabled", mx: 0.15 }}>·</Typography>}
                              </Box>
                            ))}
                          </Box>
                        );
                      })()
                    : "Apply Updates"}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* ===== EXTRACTED IMAGES PANEL ===== */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
        {/* Collapsible header row */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer", userSelect: "none" }}
          onClick={toggleImagesPanel}
        >
          <PhotoLibraryIcon sx={{ color: "text.secondary", fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
            Extracted Images
          </Typography>
          {currentQuestion && (
            <Chip
              size="small"
              label={`Viewing Q${currentIndex + 1}${currentQuestion.paper_number ? ` · Paper Q${currentQuestion.paper_number}` : ""}`}
              color="primary"
              variant="outlined"
              sx={{ fontSize: "0.65rem" }}
            />
          )}
          {imagesLoaded && (
            <Chip
              size="small"
              label={`${extractedImages.length} image${extractedImages.length !== 1 ? "s" : ""}${
                extractedImages.filter((i) => !i.assigned_question_id).length > 0
                  ? ` · ${extractedImages.filter((i) => !i.assigned_question_id).length} unassigned`
                  : ""
              }`}
              color={extractedImages.filter((i) => !i.assigned_question_id).length > 0 ? "warning" : "success"}
              variant="outlined"
            />
          )}
          {imagesLoading ? (
            <CircularProgress size={18} />
          ) : imagesOpen ? (
            <ExpandLess sx={{ color: "text.secondary" }} />
          ) : (
            <ExpandMore sx={{ color: "text.secondary" }} />
          )}
        </Box>

        {/* Expanded content */}
        {imagesOpen && (
          <Box sx={{ mt: 2 }}>
            {imagesLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading images...</Typography>
              </Box>
            ) : extractedImages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No extracted images found for this import.
              </Typography>
            ) : (
              <>
                {editMode && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Drag any image card to the image drop zone in the edit panel to assign it to the current question.
                  </Typography>
                )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                  gap: 1.5,
                }}
              >
                {extractedImages.map((img, idx) => {
                  const isReassigning = reassigningImage === img.url;
                  const isUnassigning = unassigningImage === img.url;
                  const isRemoving = removingExtractedImage === img.url;
                  const isBusy = isReassigning || isUnassigning || isRemoving;
                  const _imgRef   = img.assigned_question_ref;
                  const _imgQId   = img.assigned_question_id;
                  const _cqRef    = currentQuestionImagesRef;
                  const isCurrentQuestion =
                    _cqRef != null &&
                    (_imgRef != null || _imgQId != null) &&
                    (
                      // new-format ref matches (e.g. 'db:45' === 'db:45' or 'preview:p_0' === 'preview:p_0')
                      (_imgRef != null && String(_imgRef) === String(_cqRef)) ||
                      // legacy numeric id matches a 'db:N' ref (e.g. assigned_question_id=45 vs 'db:45')
                      (_imgQId != null && String(_cqRef) === `db:${_imgQId}`) ||
                      // legacy preview id matches 'preview:p_N' (e.g. assigned_question_id='p_0' vs 'preview:p_0')
                      (_imgQId != null && String(_cqRef) === `preview:${_imgQId}`)
                    );
                  // Find assigned question's label from the label map
                  const assignedQNumber = (img.assigned_question_ref ?? img.assigned_question_id) != null
                    ? (qLabelMap[String(img.assigned_question_ref ?? img.assigned_question_id)] ?? null)
                    : null;
                  return (
                    <Box
                      key={idx}
                      draggable={!isBusy}
                      onDragStart={() => { draggedExtractedImageRef.current = img.url; setDragImageIdx(null); }}
                      onDragEnd={() => { draggedExtractedImageRef.current = null; setIsDropOverEditImages(false); }}
                      sx={{
                        border: "1px solid",
                        borderColor: isCurrentQuestion
                          ? "primary.light"
                          : img.assigned_question_id
                          ? "success.light"
                          : "warning.light",
                        borderRadius: 2,
                        p: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                        bgcolor: isCurrentQuestion
                          ? "rgba(99,102,241,0.06)"
                          : img.assigned_question_id
                          ? "rgba(76,175,80,0.04)"
                          : "rgba(255,152,0,0.06)",
                        cursor: isBusy ? "default" : "grab",
                      }}
                    >
                      {/* Thumbnail */}
                      <Box
                        sx={{
                          width: "100%",
                          height: 90,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          borderRadius: 1,
                          bgcolor: "#f5f5f5",
                        }}
                      >
                        <img
                          src={img.url}
                          alt={img.filename}
                          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      </Box>

                      {/* Filename */}
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.65rem",
                          color: "text.secondary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={img.filename}
                      >
                        {img.filename}
                      </Typography>

                      {/* Assignment badge */}
                      <Chip
                        size="small"
                        label={
                          isCurrentQuestion
                            ? `This Q · Q${currentIndex + 1}`
                            : img.assigned_question_id
                            ? assignedQNumber != null
                              ? assignedQNumber
                              : String(img.assigned_question_id)
                            : "Unassigned"
                        }
                        color={isCurrentQuestion ? "primary" : img.assigned_question_id ? "success" : "warning"}
                        variant="filled"
                        sx={{ fontSize: "0.6rem", height: 18, fontWeight: 700 }}
                      />

                      {/* Reassign button */}
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={isBusy}
                        onClick={() => openReassignDialog(img)}
                        sx={{ textTransform: "none", fontSize: "0.7rem", py: 0.25 }}
                        startIcon={isReassigning ? <CircularProgress size={12} /> : null}
                      >
                        {isReassigning ? "Reassigning…" : "Reassign"}
                      </Button>

                      {/* Unassign button — only shown when image is assigned */}
                      {img.assigned_question_id && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          disabled={isBusy}
                          onClick={() => handleUnassignImage(img)}
                          sx={{ textTransform: "none", fontSize: "0.7rem", py: 0.25 }}
                          startIcon={isUnassigning ? <CircularProgress size={12} color="inherit" /> : null}
                        >
                          {isUnassigning ? "Unassigning…" : "Unassign"}
                        </Button>
                      )}

                      {/* Quick-assign/move to currently viewed question */}
                      {!isCurrentQuestion && currentQuestionImagesRef && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          disabled={isBusy}
                          onClick={() => quickAssignImage(img.url, currentQuestionImagesRef)}
                          startIcon={img.assigned_question_id ? <SyncIcon sx={{ fontSize: "0.75rem !important" }} /> : null}
                          sx={{
                            textTransform: "none",
                            fontSize: "0.65rem",
                            py: 0.25,
                            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                            "&:hover": { background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)" },
                          }}
                        >
                          {img.assigned_question_id ? `Move to Q${currentIndex + 1}` : `Assign to Q${currentIndex + 1}`}
                        </Button>
                      )}

                      {/* Remove button — deletes the image from the import */}
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={isBusy}
                        onClick={() => handleRemoveExtractedImage(img)}
                        sx={{ textTransform: "none", fontSize: "0.7rem", py: 0.25 }}
                        startIcon={isRemoving ? <CircularProgress size={12} color="inherit" /> : <Delete sx={{ fontSize: "0.85rem" }} />}
                      >
                        {isRemoving ? "Removing…" : "Remove"}
                      </Button>

                    </Box>
                  );
                })}
              </Box>
              </>
            )}
          </Box>
        )}
      </Paper>

      {(markdownParityBlocking || (markdownParity && (markdownParity.markdown_count > 0 || markdownParity.parsed_count > 0))) && (
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, borderColor: markdownParityBlocking ? "error.light" : "divider" }}>
          <Box
            onClick={() => setParityPanelExpanded((e) => !e)}
            sx={{
              px: 2,
              py: 1.25,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              bgcolor: markdownParityBlocking ? "error.50" : "grey.50",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Source markdown vs import{markdownParityBlocking ? " — action required" : ""}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {markdownParityBlocking && (
                <Chip size="small" color="error" label={`Missing ${markdownParity?.missing_count ?? 0}`} sx={{ fontWeight: 700 }} />
              )}
              <Typography variant="caption" color="text.secondary">
                {parityPanelExpanded ? "Hide" : "Show"}
              </Typography>
              {parityPanelExpanded ? <ExpandLess /> : <ExpandMore />}
            </Box>
          </Box>
          <Collapse in={parityPanelExpanded}>
            <Box sx={{ px: 2, pb: 2, pt: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Compares question numbers detected in DataLab markdown with paper numbers in this draft. Does not run per-question; open source markdown when cross-checking the PDF.
              </Typography>
              {markdownParityBlocking && (
                <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
                  Missing from import: <strong>{markdownParityRanges || "unknown range"}</strong>. Add or fix paper numbers, or insert missing questions before confirming.
                </Alert>
              )}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1.5 }}>
                <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                    In markdown ({markdownParity?.markdown_count ?? 0} detected)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem", wordBreak: "break-all" }}>
                    {(markdownParity?.markdown_question_numbers || []).join(", ") || "—"}
                    {(markdownParity?.markdown_count ?? 0) > 200 ? " …" : ""}
                  </Typography>
                </Box>
                <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                    In this import ({markdownParity?.parsed_count ?? 0})
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem", wordBreak: "break-all" }}>
                    {(markdownParity?.parsed_question_numbers || []).join(", ") || "—"}
                    {(markdownParity?.parsed_count ?? 0) > 200 ? " …" : ""}
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="info"
                startIcon={<DescriptionIcon sx={{ fontSize: "1rem !important" }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  setMarkdownDialogOpen(true);
                }}
                sx={{ textTransform: "none" }}
              >
                Open source markdown
              </Button>
            </Box>
          </Collapse>
        </Paper>
      )}
      {(() => {
        const raw = importData?.datalab_section_instructions;
        const dlabList = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? [raw] : [];
        if (!dlabList.length) return null;
        return (
          <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Suggested sections (DataLab /extract)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              Verbatim hints from the conversion pipeline — use with bulk “Section fields” or the edit accordion.
            </Typography>
            {dlabList.map((entry, i) => {
              const text = typeof entry === "string" ? entry : JSON.stringify(entry, null, 2);
              return (
                <Box key={i} sx={{ mb: 1.5, p: 1.5, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                  <Typography variant="body2" component="pre" sx={{ fontFamily: "monospace", fontSize: "0.75rem", whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>
                    {text}
                  </Typography>
                  <Button
                    size="small"
                    sx={{ mt: 1, textTransform: "none" }}
                    onClick={() => {
                      navigator.clipboard.writeText(text).then(
                        () => toast.success("Copied to clipboard"),
                        () => toast.error("Copy failed")
                      );
                    }}
                  >
                    Copy
                  </Button>
                </Box>
              );
            })}
          </Paper>
        );
      })()}
      {validationReport?.structural_verification && (() => {
        const sv = validationReport.structural_verification;
        const statusConfig = {
          passed:      { severity: "success", icon: "✓", label: "Verified", color: "#2e7d32" },
          healed:      { severity: "info",    icon: "⚕", label: "Auto-Healed", color: "#0288d1" },
          issues_found:{ severity: "warning", icon: "⚠", label: "Issues Found", color: "#ed6c02" },
        };
        const cfg = statusConfig[sv.status] || statusConfig.issues_found;
        return (
          <Alert severity={cfg.severity} sx={{ mb: 2 }}>
            <AlertTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>{cfg.icon}</span> Extraction Verification: {cfg.label}
              {sv.expected_question_count > 0 && (
                <Chip
                  size="small"
                  label={`${sv.extracted_question_count}/${sv.expected_question_count} Qs`}
                  color={sv.extracted_question_count >= sv.expected_question_count ? "success" : "warning"}
                  sx={{ ml: 1, height: 22 }}
                />
              )}
            </AlertTitle>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
              {Object.entries(sv.checks || {})
                .filter(([key]) => key !== "markdown_parity")
                .map(([key, check]) => (
                  <Chip
                    key={key}
                    size="small"
                    label={key.replace(/_/g, " ")}
                    color={check.passed ? "success" : "warning"}
                    variant={check.passed ? "outlined" : "filled"}
                    sx={{ textTransform: "capitalize", height: 22 }}
                  />
                ))}
            </Box>
            {sv.healed_count > 0 && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {sv.healed_count} issue{sv.healed_count > 1 ? "s" : ""} auto-healed.
              </Typography>
            )}
            {sv.unresolved_count > 0 && (
              <Box sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="warning.dark" sx={{ mb: 0.5 }}>
                  {sv.unresolved_count} issue{sv.unresolved_count > 1 ? "s" : ""} need manual review:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(sv.issues || [])
                    .filter(
                      (i) =>
                        !i.healed &&
                        i.status !== "verified_by_agent" &&
                        i.status !== "dismissed" &&
                        (i.type || "") !== "markdown_question_missing_in_import"
                    )
                    .map((i, idx) => {
                      const qPrefix = i.question_number != null ? `Q${i.question_number}: ` : "";
                      const friendlyType = VR_NVR_ISSUE_LABELS[i.type] || i.type?.replace(/_/g, " ");
                      const label = `${qPrefix}${friendlyType}`;
                      const issueKey = i.issue_key;
                      const isNvrVrCritical = i.type && (i.type.startsWith("nvr_") || i.type.startsWith("vr_"));
                      return (
                        <Chip
                          key={issueKey || idx}
                          size="small"
                          label={
                            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                              {label}
                              {issueKey && (
                                <>
                                  <Box
                                    component="span"
                                    onClick={(e) => { e.stopPropagation(); handleVerifyIssue(issueKey); }}
                                    sx={{ cursor: "pointer", color: "success.main", fontWeight: "bold", "&:hover": { textDecoration: "underline" } }}
                                    title="Mark verified"
                                  >✓</Box>
                                  <Box
                                    component="span"
                                    onClick={(e) => { e.stopPropagation(); handleDismissIssue(issueKey, true); }}
                                    sx={{ cursor: "pointer", color: "text.secondary", fontWeight: "bold", "&:hover": { textDecoration: "underline" } }}
                                    title="Dismiss permanently (false positive)"
                                  >✕</Box>
                                </>
                              )}
                            </Box>
                          }
                          title={i.message || ""}
                          color={isNvrVrCritical ? "error" : "warning"}
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      );
                    })}
                </Box>
              </Box>
            )}
            {(sv.issues || []).some(i => i.status === "verified_by_agent") && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="success.dark">
                  Verified & fixed by agent:
                  {(sv.issues || []).filter(i => i.status === "verified_by_agent").map((i, idx) => (
                    <Chip
                      key={i.issue_key || idx}
                      size="small"
                      label={`${i.question_number != null ? `Q${i.question_number}: ` : ""}${(i.type || "").replace(/_/g, " ")}`}
                      title={i.verification_notes || `Verified by ${i.verified_by || 'agent'}`}
                      color="success"
                      variant="outlined"
                      sx={{ ml: 0.5, height: 18, fontSize: "0.65rem" }}
                    />
                  ))}
                </Typography>
              </Box>
            )}
            {(sv.issues || []).some(i => i.status === "dismissed") && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Dismissed:
                  {(sv.issues || []).filter(i => i.status === "dismissed").map((i, idx) => (
                    <Chip
                      key={i.issue_key || idx}
                      size="small"
                      label={`${i.question_number != null ? `Q${i.question_number}: ` : ""}${(i.type || "").replace(/_/g, " ")}`}
                      onClick={() => i.issue_key && handleUndismissIssue(i.issue_key)}
                      title="Click to undismiss"
                      sx={{ ml: 0.5, height: 18, fontSize: "0.65rem", opacity: 0.6, cursor: "pointer" }}
                    />
                  ))}
                </Typography>
              </Box>
            )}
            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleReverify}
                disabled={validating || agentFixing}
                sx={{ textTransform: "none", fontSize: "0.75rem" }}
              >
                {validating ? "Re-verifying…" : "Re-run verification"}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={handleAgentFix}
                disabled={agentFixing || validating}
                startIcon={agentFixing ? <CircularProgress size={12} color="inherit" /> : <AutoFixHigh fontSize="small" />}
                sx={{ textTransform: "none", fontSize: "0.75rem" }}
              >
                {agentFixing ? "Agent fixing…" : "Fix with Agent"}
              </Button>
            </Box>
            {sv.ai_verification_calls > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                AI verification: {sv.ai_verification_calls} call{sv.ai_verification_calls > 1 ? "s" : ""} (${sv.ai_verification_cost_usd?.toFixed(3)})
              </Typography>
            )}
          </Alert>
        );
      })()}

      {/* Validation Report Alert */}
      {validationReport?.total_issues > 0 && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setValidationReport(null)}>
          <AlertTitle>Validation Report</AlertTitle>
          {validationReport.total_issues} issue{validationReport.total_issues > 1 ? 's' : ''} found.
          {validationReport.corrections_applied > 0 && ` ${validationReport.corrections_applied} auto-corrected.`}
          {validationReport.answer_verification && (
            <> | Answer sheet: {validationReport.answer_verification.answers_verified} verified, {validationReport.answer_verification.corrections_applied} corrected.</>
          )}
        </Alert>
      )}

      {questions.length > 0 && duplicatePaperNumbers.size > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Duplicate paper numbers</AlertTitle>
          The same paper number appears more than once within the same section:{" "}
          {duplicatePaperNumbersSorted.map((n) => (
            <Chip key={n} size="small" label={`Q${n}`} sx={{ mx: 0.25, height: 22 }} />
          ))}
          . Use the toolbar Paper # field (or the accordion) to align numbers with the PDF.
        </Alert>
      )}

      {questions.length === 0 ? (
        <Alert severity="warning">
          <AlertTitle>No questions found</AlertTitle>
          No structured questions are available to review for this job.
        </Alert>
      ) : (
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* PDF Comparison View (optional) */}
          {showPdf && (
            <Box sx={{ width: "40%", minWidth: 350, height: "80vh", position: "sticky", top: 16 }}>
              <PdfComparisonView importId={importId} onClose={() => setShowPdf(false)} />
            </Box>
          )}
          {/* Past-paper page view — groups questions by page with thumbnails */}
          {showPaperView && (
            <Box sx={{ width: "300px", flexShrink: 0, height: "80vh", position: "sticky", top: 16 }}>
              <PaperPageView
                importId={importId}
                questions={questions}
                onClose={() => setShowPaperView(false)}
                onQuestionClick={(paperNum) => {
                  const idx = questions.findIndex(
                    (q) => String(q.paper_number) === String(paperNum)
                  );
                  if (idx >= 0) setCurrentIndex(idx);
                }}
              />
            </Box>
          )}
          {/* Supplementary Booklet Panel */}
          {showSupplementary && supplementaryBookletUrl && (
            <Box sx={{ width: "40%", minWidth: 350, height: "80vh", position: "sticky", top: 16, display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1, py: 0.75, bgcolor: "#f3e8ff", borderBottom: "1px solid #c4b5fd" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <MenuBookIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
                  <Typography variant="caption" fontWeight={700} color="#5b21b6">
                    Supplementary Booklet / Sources
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setShowSupplementary(false)}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <iframe
                  src={supplementaryBookletUrl}
                  width="100%"
                  height="100%"
                  style={{ border: "none", display: "block" }}
                  title="Supplementary Booklet"
                />
              </Box>
            </Box>
          )}

        <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 3 }}>
          {/* Left Column — Main Content */}
          <Box>
            {/* Professional Exam Paper Header */}
            {(examTitle || examInstructions || schoolName || examSubject) && (
              <Paper
                sx={{
                  mb: 2,
                  border: '2px solid #333',
                  borderRadius: 0,
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                {/* Header Section - Dark Background */}
                <Box sx={{ bgcolor: '#1a1a1a', color: '#fff', p: 2.5, textAlign: 'center' }}>
                  {schoolLogo && (
                    <Box sx={{ mb: 1.5 }}>
                      <img 
                        src={schoolLogo} 
                        alt="School Logo" 
                        style={{ maxHeight: 80, maxWidth: 120 }} 
                      />
                    </Box>
                  )}
                  {schoolName && (
                    <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 1, mb: 0.5, textTransform: 'uppercase' }}>
                      {schoolName}
                    </Typography>
                  )}
                  {examTitle && (
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                      {examTitle}
                    </Typography>
                  )}
                  {examSubject && (
                    <Typography variant="h5" sx={{ fontWeight: 700, textTransform: 'uppercase', mb: 0.5, color: '#fff' }}>
                      {examSubject}
                    </Typography>
                  )}
                  {examSection && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {examSection}
                    </Typography>
                  )}
                </Box>

                {/* Metadata Bar */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: 4, 
                  py: 1.5, 
                  bgcolor: '#f5f5f5',
                  borderBottom: '1px solid #ddd',
                  flexWrap: 'wrap'
                }}>
                  {examDuration && (
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                      Time Allowed: {examDuration}
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                    Questions: {totalCount}
                  </Typography>
                  {importData?.paper_format && (
                    <Chip
                      label={importData.paper_format.replace(/_/g, ' ').toUpperCase()}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: '0.7rem', borderColor: '#6366f1', color: '#6366f1' }}
                    />
                  )}
                  {importData?.extraction_confidence_score && (
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      DataLab: {Number(importData.extraction_confidence_score).toFixed(1)}/5.0
                    </Typography>
                  )}
                </Box>

                {/* Instructions Section */}
                {examInstructions && (
                  <Box sx={{ p: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, textTransform: 'uppercase', color: '#333' }}>
                      Instructions:
                      <Typography component="span" variant="caption" sx={{ color: '#888', ml: 1, fontWeight: 400, textTransform: 'none' }}>
                        ({examInstructions.split(/\s+/).filter(Boolean).length} words)
                      </Typography>
                    </Typography>
                    <Box sx={{
                      pl: 1,
                      '& ol': { pl: 2, m: 0 },
                      '& li': { mb: 0.75, fontSize: '0.875rem', color: '#555', lineHeight: 1.6 }
                    }}>
                      <FormatInstructions text={examInstructions} typographySx={{ color: '#555' }} />
                    </Box>
                  </Box>
                )}

                {/* Edit Button */}
                <Box sx={{ p: 1, bgcolor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #ddd' }}>
                  {examSubject ? (
                    <SubjectChip subject={examSubject} size="small" />
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No subject detected — set via Edit Header
                    </Typography>
                  )}
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={openEditExamMeta}
                    sx={{ textTransform: 'none' }}
                  >
                    Edit Header
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Exam header card */}
            <Paper sx={{ p: 3, mb: 2, borderRadius: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {importData?.source_filename || "Imported Questions"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {autoSaving && (
                    <Chip
                      size="small"
                      label="Saving..."
                      color="primary"
                      variant="outlined"
                      icon={<CircularProgress size={12} />}
                    />
                  )}
                  {!autoSaving && lastSaved && (
                    <Chip
                      size="small"
                      label={`Saved ${formatDistanceToNow(lastSaved, { addSuffix: true })}`}
                      color="success"
                      variant="outlined"
                    />
                  )}
                  <Button
                    variant="outlined" size="small"
                    startIcon={<CheckCircle />}
                    onClick={approveAll}
                    disabled={questions.length === 0}
                    sx={{ textTransform: "none" }}
                  >
                    Approve all
                  </Button>
                  <Button
                    variant="outlined" size="small" color="error"
                    startIcon={<Cancel />}
                    onClick={() => {
                      if (window.confirm("Reject all questions? They will not be imported.")) {
                        setApprovedSet(new Set());
                        toast.success("All questions rejected");
                      }
                    }}
                    disabled={questions.length === 0}
                    sx={{ textTransform: "none" }}
                  >
                    Reject all
                  </Button>
                  <Button
                    variant="outlined" size="small" color="warning"
                    onClick={resetAllToOriginal}
                    disabled={questions.length === 0}
                    sx={{ textTransform: "none" }}
                  >
                    Reset all to original
                  </Button>
                  <Chip
                    label={`${approvedCount}/${totalCount} approved`}
                    variant={approvedCount === totalCount ? "filled" : "outlined"}
                    size="small"
                  />
                </Box>
              </Box>
            </Paper>

            <Divider sx={{ mb: 2 }} />

            {/* Bulk operations toolbar */}
            {selectedQuestions.size > 0 && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: "primary.50", borderRadius: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedQuestions.size} question(s) selected
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button variant="outlined" size="small" onClick={() => setBulkEditMode(!bulkEditMode)} sx={{ textTransform: "none" }}>
                      {bulkEditMode ? "Cancel Edit" : "Bulk Edit"}
                    </Button>
                    <Button variant="outlined" size="small" color="error" onClick={rejectSelected} sx={{ textTransform: "none" }}>
                      Reject Selected
                    </Button>
                    <Button variant="outlined" size="small" onClick={deselectAllQuestions} sx={{ textTransform: "none" }}>
                      Deselect All
                    </Button>
                  </Box>
                </Box>
                {bulkEditMode && (
                  <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Difficulty</InputLabel>
                      <Select
                        value={bulkEditValues.difficulty}
                        onChange={(e) => setBulkEditValues((prev) => ({ ...prev, difficulty: e.target.value }))}
                        label="Difficulty"
                      >
                        <MenuItem value="">No Change</MenuItem>
                        <MenuItem value="Easy">Easy</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="Hard">Hard</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small" label="Marks" type="number"
                      value={bulkEditValues.default_marks}
                      onChange={(e) => setBulkEditValues((prev) => ({ ...prev, default_marks: e.target.value }))}
                      sx={{ width: 100 }}
                      inputProps={{ min: 1 }}
                    />
                    <Button
                      variant="contained" size="small"
                      onClick={applyBulkEdit}
                      disabled={!bulkEditValues.difficulty && !bulkEditValues.default_marks}
                      sx={{ textTransform: "none" }}
                    >
                      Apply Changes
                    </Button>
                  </Box>
                )}
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                    Section fields (selected questions)
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: 560 }}>
                    <TextField
                      size="small"
                      label="Section title"
                      value={bulkEditValues.section_title}
                      onChange={(e) => setBulkEditValues((prev) => ({ ...prev, section_title: e.target.value }))}
                      fullWidth
                      placeholder="e.g. Section B"
                    />
                    <TextField
                      size="small"
                      label="Section instructions"
                      value={bulkEditValues.section_instructions}
                      onChange={(e) => setBulkEditValues((prev) => ({ ...prev, section_instructions: e.target.value }))}
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={8}
                      placeholder="Instructions for this section (optional)"
                    />
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={applyBulkSectionFromForm}
                        disabled={!bulkEditValues.section_title?.trim() && !bulkEditValues.section_instructions?.trim()}
                        sx={{ textTransform: "none" }}
                      >
                        Apply section to selected
                      </Button>
                      <Button variant="outlined" size="small" color="warning" onClick={clearBulkSectionOnSelected} sx={{ textTransform: "none" }}>
                        Clear section on selected
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Current question */}
            {currentQuestion && (
              <Box>
                {(currentQuestion.section_title || currentQuestion.section_instructions) &&
                  (() => {
                    const prevQ = currentIndex > 0 ? questions[currentIndex - 1] : null;
                    const sk = sectionBlockKey(currentQuestion);
                    const sameAsPrev = prevQ && sk === sectionBlockKey(prevQ) && sk !== "\u0000";
                    if (sameAsPrev) {
                      return (
                        <Box sx={{ mb: 1.25, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Chip size="small" label="Same section as above" variant="outlined" sx={{ fontWeight: 600 }} />
                          {currentQuestion.section_title ? (
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {currentQuestion.section_title}
                            </Typography>
                          ) : null}
                          <Tooltip title="Remove section title and instructions from this question only">
                            <IconButton size="small" color="warning" onClick={clearSectionOnCurrent} sx={{ p: 0.25 }}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    }
                    return (
                      <Paper
                        variant="outlined"
                        sx={{
                          mb: 1.5,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          borderColor: "divider",
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            {currentQuestion.section_title && (
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  color: "text.primary",
                                  mb: currentQuestion.section_instructions ? 1.25 : 0,
                                }}
                              >
                                {currentQuestion.section_title}
                              </Typography>
                            )}
                            {currentQuestion.section_instructions && (
                              <Box
                                sx={{
                                  p: 1.5,
                                  bgcolor: "#fef9c3",
                                  border: "1px solid #fde047",
                                  borderRadius: 1,
                                  borderLeft: "3px solid #ca8a04",
                                  "& ol": { pl: 2, m: 0 },
                                  "& li": { mb: 0.5, fontSize: "0.875rem", color: "#713f12", lineHeight: 1.6 },
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#854d0e",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                    display: "block",
                                    mb: 0.5,
                                  }}
                                >
                                  Section instructions
                                </Typography>
                                <FormatInstructions
                                  text={currentQuestion.section_instructions}
                                  typographySx={{ color: "#713f12" }}
                                />
                              </Box>
                            )}
                          </Box>
                          <Tooltip title="Remove section title and instructions from this question only">
                            <IconButton size="small" color="warning" onClick={clearSectionOnCurrent} sx={{ mt: -0.5, mr: -0.5 }}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.25, borderTop: "1px solid", borderColor: "divider", pt: 1 }}>
                          <Tooltip title="Renumber all questions in this section from 1 upward">
                            <Button size="small" variant="outlined" onClick={renumberQuestionsInSection} sx={{ textTransform: "none", fontSize: "0.72rem" }}>
                              Renumber this section
                            </Button>
                          </Tooltip>
                          {editDraft && (
                            <Tooltip title="Copy current draft section title + instructions onto all questions from here to the end of the list">
                              <Button size="small" variant="outlined" color="secondary" onClick={applyCurrentSectionToFollowing} sx={{ textTransform: "none", fontSize: "0.72rem" }}>
                                Apply section → following
                              </Button>
                            </Tooltip>
                          )}
                          {selectedQuestions.size > 0 && editDraft && (
                            <Tooltip title="Apply current draft section title + instructions to all selected questions">
                              <Button size="small" variant="outlined" color="secondary" onClick={applyDraftSectionToSelected} sx={{ textTransform: "none", fontSize: "0.72rem" }}>
                                Apply to {selectedQuestions.size} selected
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </Paper>
                    );
                  })()}

                {/* Question header bar */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Tooltip title="List order in this import — use arrows to move this block in the question list">
                      <Chip label={`Question ${currentIndex + 1}`} size="small" color="default" sx={{ cursor: "default" }} />
                    </Tooltip>
                    <IconButton
                      size="small"
                      disabled={currentIndex === 0}
                      onClick={() => moveQuestion(currentIndex, -1)}
                      title="Move this question up in the list"
                      sx={{ p: 0.35 }}
                    >
                      <KeyboardArrowUp sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={currentIndex >= questions.length - 1}
                      onClick={() => moveQuestion(currentIndex, 1)}
                      title="Move this question down in the list"
                      sx={{ p: 0.35 }}
                    >
                      <KeyboardArrowDown sx={{ fontSize: 20 }} />
                    </IconButton>
                    {(!editMode ||
                      editTarget?.type === "stem" ||
                      editTarget?.type === "standalone" ||
                      editTarget?.type === "subquestion") && (
                      <Tooltip
                        title={
                          editTarget?.type === "subquestion"
                            ? "Paper number for this sub-question (part on the PDF). Change it when two parts share the same number or the agent duplicated Q labels."
                            : "Paper question number as on the PDF — editable here or in the accordion; used for markdown parity and ordering."
                        }
                      >
                        <TextField
                          size="small"
                          label="Paper #"
                          value={headerPaperDraft}
                          onChange={(e) => setHeaderPaperDraft(e.target.value)}
                          onBlur={commitHeaderPaperNumber}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                          }}
                          error={
                            duplicatePaperNumbers.size > 0 &&
                            (() => {
                              const pn = String(headerPaperDraft).trim();
                              if (!pn) return false;
                              const key = `${currentQuestion?.section_title ?? ''}|${pn}`;
                              return duplicatePaperNumbers.has(key);
                            })()
                          }
                          sx={{ width: 118, "& .MuiInputBase-input": { fontFamily: "monospace", fontWeight: 600 } }}
                          placeholder="e.g. 12"
                        />
                      </Tooltip>
                    )}
                    {!currentQuestion?.section_title && (
                      <Tooltip title="Renumber all questions 1…N (simple papers without section-local numbering)">
                        <Button size="small" variant="text" onClick={renumberAllQuestions} sx={{ textTransform: "none", fontSize: "0.7rem", color: "text.secondary" }}>
                          Renumber all
                        </Button>
                      </Tooltip>
                    )}
                    {currentQuestion?.question_subtype && NVR_SUBTYPE_LABELS[currentQuestion.question_subtype] && (
                      <Chip
                        label={NVR_SUBTYPE_LABELS[currentQuestion.question_subtype].label}
                        size="small"
                        sx={{
                          bgcolor: NVR_SUBTYPE_LABELS[currentQuestion.question_subtype].bg,
                          color: NVR_SUBTYPE_LABELS[currentQuestion.question_subtype].color,
                          fontWeight: 600,
                          fontSize: "0.65rem",
                        }}
                      />
                    )}
                    {/* Structural verification issue/healed badge — per question */}
                    {(() => {
                      const pn = currentQuestion?.paper_number != null ? String(currentQuestion.paper_number).replace(/^Q\.?\s*/i, '') : null;
                      const sv = validationReport?.structural_verification;
                      if (!pn || !sv) return null;
                      const issues = (sv.issues || []).filter(i => String(i.question_number) === pn);
                      const allAgentVerified = issues.length > 0 && issues.every(i => i.status === 'verified_by_agent' || i.status === 'dismissed');
                      const isAgentVerified = currentQuestion?._verified_by === 'mcp_agent' || allAgentVerified;
                      if (issues.length === 0 && !currentQuestion?._healed && !isAgentVerified) return null;
                      if (isAgentVerified) {
                        const notes = currentQuestion?._verification_notes || (issues[0]?.verification_notes);
                        return (
                          <Tooltip title={`Verified & fixed by agent${notes ? `: ${notes}` : ''}`}>
                            <Chip label="Fixed by agent ✓" size="small" color="success" variant="filled"
                              sx={{ fontWeight: 700, cursor: 'default', height: 22 }} />
                          </Tooltip>
                        );
                      }
                      if (currentQuestion?._paper_number_collision) {
                        return (
                          <Tooltip title="Two chunks both emitted this paper_number with different text. One may carry a neighbouring question's body — verify carefully.">
                            <Chip label="Number collision ⚠" size="small" color="error" variant="filled"
                              sx={{ fontWeight: 700, cursor: 'default', height: 22 }} />
                          </Tooltip>
                        );
                      }
                      if (currentQuestion?._healed) {
                        return (
                          <Tooltip title={`Auto-healed: ${currentQuestion._healed.replace(/_/g, ' ')}`}>
                            <Chip label="Healed" size="small" color="info" variant="filled"
                              sx={{ fontWeight: 700, cursor: 'default', height: 22 }} />
                          </Tooltip>
                        );
                      }
                      // Suppress stale duplicate_number / sequence_gap issues when the live
                      // questions array no longer has the same problem. The validation_report
                      // is written by VerifyExtractionJob and is not refreshed by saveDraft,
                      // so it can lag behind edits the user has already made.
                      const activeIssues = issues.filter(i => {
                        if (i.type === 'duplicate_number') {
                          return questionRowHasDuplicateKey(currentQuestion, duplicatePaperNumbers);
                        }
                        return true;
                      });
                      const unresolvedIssues = activeIssues.filter(i => !i.healed && i.status !== 'verified_by_agent' && i.status !== 'dismissed');
                      if (unresolvedIssues.length === 0 && activeIssues.length > 0) {
                        const issue = activeIssues[0];
                        return (
                          <Tooltip title={issue.message || issue.type?.replace(/_/g, ' ')}>
                            <Chip label="Healed" size="small" color="info" variant="filled"
                              sx={{ fontWeight: 700, cursor: 'default', height: 22 }} />
                          </Tooltip>
                        );
                      }
                      if (unresolvedIssues.length === 0) return null;
                      const issue = unresolvedIssues[0] || activeIssues[0];
                      const isNvrVr = issue.type && (issue.type.startsWith("nvr_") || issue.type.startsWith("vr_"));
                      const friendlyLabel = VR_NVR_ISSUE_LABELS[issue.type] || issue.type?.replace(/_/g, ' ');
                      return (
                        <Tooltip title={issue.message || friendlyLabel}>
                          <Chip
                            label={issue.healed ? 'Healed' : friendlyLabel}
                            size="small"
                            color={issue.healed ? 'info' : isNvrVr ? 'error' : 'warning'}
                            variant="filled"
                            sx={{ fontWeight: 700, cursor: 'default', height: 22 }}
                          />
                        </Tooltip>
                      );
                    })()}
                    {/* Vision verification mismatch badge — detail header */}
                    {(() => {
                      const pn = currentQuestion?.paper_number != null ? String(currentQuestion.paper_number) : null;
                      const visionCheck = validationReport?.vision_check;
                      if (!pn || !Array.isArray(visionCheck)) return null;
                      const entry = visionCheck.find(v => String(v.paper_number) === pn && v.status !== 'pass' && v.status !== 'unverified');
                      if (!entry) return null;
                      return (
                        <Tooltip title={`Vision check flagged: ${entry.status}${entry.detail ? ' — ' + entry.detail : ''}`}>
                          <Chip
                            label={`Vision ⚠ ${entry.status}`}
                            size="small"
                            color="warning"
                            variant="filled"
                            sx={{ fontWeight: 700, cursor: 'default' }}
                          />
                        </Tooltip>
                      );
                    })()}
                  </Box>
                  <Tooltip title="Inject any missing tables/blocks from source markdown, then run the MCP agent to detect and heal remaining markdown issues">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={fixingMarkdown ? <CircularProgress size={14} /> : <AutoFixHigh fontSize="small" />}
                        onClick={handleFixMarkdown}
                        disabled={fixingMarkdown || agentFixing || validating}
                        sx={{ textTransform: "none", borderColor: "warning.main", color: "warning.dark", "&:hover": { bgcolor: "warning.50" } }}
                      >
                        {fixingMarkdown ? "Fixing…" : "Fix markdown"}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="DataLab markdown: preview, raw text, images, JSON (same as import list View)">
                    <Button
                      size="small"
                      variant="outlined"
                      color="info"
                      startIcon={<DescriptionIcon sx={{ fontSize: "1rem !important" }} />}
                      onClick={() => setMarkdownDialogOpen(true)}
                      sx={{ textTransform: "none" }}
                    >
                      Source markdown
                    </Button>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => insertManualQuestionBefore(currentIndex)}
                    sx={{ textTransform: "none" }}
                  >
                    Add before
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => insertManualQuestionAfter(currentIndex)}
                    sx={{ textTransform: "none" }}
                  >
                    Add after
                  </Button>
                  <Tooltip title="Add a sub-question to this question (converts standalone to grouped if needed)">
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<Add />}
                      onClick={addSubQuestionToCurrentQuestion}
                      disabled={questions.length === 0}
                      sx={{ textTransform: "none" }}
                    >
                      Add sub-question
                    </Button>
                  </Tooltip>
                  {isEnglishSubjectLabel(examSubject) && (
                    <Tooltip title="Insert a new comprehension passage (type=grouped, passage_type=comprehension) and open the passage editor immediately">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<MenuBookIcon sx={{ fontSize: "1rem !important" }} />}
                        onClick={() => insertComprehensionPassageAfter(currentIndex)}
                        sx={{ textTransform: "none" }}
                      >
                        Add passage
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title="Remove this question from the draft (parity and approvals update after save)">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => deleteRootQuestionAt(currentIndex)}
                        disabled={questions.length <= 1}
                        sx={{ textTransform: "none" }}
                      >
                        Delete question
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Reset this question back to what the AI extracted (discards all manual edits)">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={resetQuestion}
                        disabled={!modifiedQuestions.has(currentIndex)}
                        sx={{ textTransform: "none" }}
                      >
                        Reset to original
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    size="small"
                    variant={approvedSet.has(currentIndex) ? "contained" : "outlined"}
                    startIcon={<CheckCircle />}
                    onClick={() => toggleApproval(currentIndex)}
                    sx={{ textTransform: "none" }}
                    color={approvedSet.has(currentIndex) ? "primary" : "inherit"}
                  >
                    {approvedSet.has(currentIndex) ? "Approved" : "Mark approved"}
                  </Button>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!currentQuestion?.is_sample_question}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const next = questions.map((q, i) =>
                            i === currentIndex ? { ...q, is_sample_question: checked } : q
                          );
                          setQuestions(next);
                          setModifiedQuestions((prev) => new Set([...prev, currentIndex]));
                          void persistDraftAndSyncPreview(next);
                        }}
                      />
                    }
                    label="Sample question"
                    sx={{ ml: 0, mr: 0, "& .MuiFormControlLabel-label": { fontSize: "0.8125rem" } }}
                  />
                </Box>

                {/* Orphaned passage warning — shown when the AI extracted a comprehension
                    passage as a standalone question instead of grouping it with its Q&A. */}
                {isOrphanedPassage(currentQuestion, examSubject) && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 1.5, borderRadius: 2 }}
                    action={
                      <Button
                        size="small"
                        color="warning"
                        variant="outlined"
                        onClick={handleRescan}
                        disabled={rescanning}
                        sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                      >
                        {rescanning ? "Rescanning…" : "Rescan Paper"}
                      </Button>
                    }
                  >
                    <AlertTitle sx={{ fontWeight: 700 }}>Passage Extraction Issue</AlertTitle>
                    This looks like a <strong>reading passage</strong> that was extracted as a standalone
                    question. It should be grouped together with the comprehension Q&amp;A questions that
                    follow it. Click <strong>Rescan Paper</strong> to re-run the AI extraction and fix
                    the grouping automatically.
                  </Alert>
                )}

                {/* Question content card */}
                <Paper variant="outlined" sx={{ p: 3, borderStyle: "dashed", borderRadius: 2, mb: 2 }}>
                  {editMode && editDraft ? (
                    renderEditPanel()
                  ) : currentQuestion.type === "grouped" ? (
                    /* =========== GROUPED QUESTION VIEW =========== */
                    <Box>
                      {/* Stem */}
                      <Box
                        sx={{
                          mb: 2, p: 2,
                          bgcolor: "grey.50",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "grey.300",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}
                          >
                          {isComprehensionPassageForReview(currentQuestion, examSubject)
                            ? "Comprehension Passage"
                            : (isEnglishSubjectLabel(examSubject) ? "Shared Stem / Passage" : "Shared Stem")}
                          </Typography>
                          {isComprehensionPassageForReview(currentQuestion, examSubject) && (
                            <Chip label="Comprehension" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                          )}
                          {isComprehensionPassageForReview(currentQuestion, examSubject) && (currentQuestion.sub_questions || []).length > 0 && (
                            <Typography variant="caption" color="text.disabled" sx={{ ml: "auto", fontStyle: "italic" }}>
                              Hover a question to highlight its passage lines
                            </Typography>
                          )}
                        </Box>
                        {(() => {
                          const isComprehension = isComprehensionPassageForReview(currentQuestion, examSubject);
                          const stemText = normalizeExtractedDisplayText(
                            stripSubQsFromStem(
                              stripPassageTail(
                                isComprehension
                                  ? convertMarkdownLineNumbersToMarkers(getGroupedStemText(currentQuestion))
                                  : getGroupedStemText(currentQuestion)
                              ),
                              currentQuestion.sub_questions || []
                            )
                          );
                          const passageHighlightLines = (() => {
                            if (!isComprehension) return [];
                            const subQs = currentQuestion.sub_questions || [];
                            // When hovering a sub-question, highlight only its referenced lines
                            const sqs = activeSubQIdx != null && activeSubQIdx < subQs.length
                              ? [subQs[activeSubQIdx]]
                              : subQs;
                            const nums = new Set();
                            for (const sq of sqs) {
                              const sqText = normalizeExtractedDisplayText(
                                String(sq?.question_text || "")
                              );
                              for (const n of parseLineRefs(sqText)) nums.add(n);
                            }
                            return [...nums];
                          })();
                          const isLong = stemText && stemText.length > 500;
                          const showTruncated = isLong && !passageExpanded;
                          const displayText = showTruncated ? stemText.slice(0, 500) + "…" : stemText;
                          const passageMissing = !stemText || stemText.trim().length === 0;
                          // Data tables, LaTeX, HTML tags, and markdown bold must use HtmlWithLatex —
                          // PassageWithLineNumbers only handles plain text with line markers.
                          const pipeLineCount = (stemText || "").split('\n').filter(l => /^\s*\|/.test(l)).length;
                          const stemHasTable = (/\|[^\n]+\|/.test(stemText || "") && /\|[\s\-:]+\|/.test(stemText || "")) || pipeLineCount >= 2;
                          const stemHasLatex = /\$[^$]+\$/.test(stemText || "") || /\\(?:frac|sqrt|text|circ|times|div|dots|ldots|cdots|dotso|dotsc|dotsb|dotsi)\b/.test(stemText || "");
                          const stemHasHtml = /<[a-zA-Z]/.test(stemText || "");
                          const stemHasBold = (stemText || "").includes("**");
                          const useHtmlRenderer = stemHasTable || stemHasLatex || stemHasHtml || stemHasBold || !isComprehension;
                          return (
                            <>
                              {passageMissing ? (
                                isEnglishSubjectLabel(examSubject) ? (
                                  <Box sx={{ py: 1 }}>
                                    <Alert
                                      severity="warning"
                                      sx={{ mb: 1.5, alignItems: "flex-start" }}
                                      action={
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="warning"
                                          startIcon={<Add />}
                                          onClick={() => startEditStem(currentIndex)}
                                          sx={{ textTransform: "none", whiteSpace: "nowrap", mt: 0.5 }}
                                        >
                                          Add Passage
                                        </Button>
                                      }
                                    >
                                      <strong>Passage not found in PDF.</strong> This paper appears to be a
                                      questions-only booklet — the reading passage is printed in a separate
                                      reading booklet. Paste the passage text here so students can read it
                                      alongside the questions.
                                    </Alert>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 0.5 }}>
                                    No shared stem — see sub-questions below.
                                  </Typography>
                                )
                              ) : useHtmlRenderer ? (
                                <Box sx={{ lineHeight: 1.6, color: "#333" }}>
                                  <HtmlWithLatex html={displayText} />
                                </Box>
                              ) : (
                                <PassageWithLineNumbers
                                  text={displayText}
                                  highlightLines={passageHighlightLines}
                                  showAllFallbackLineNumbers={false}
                                  wrapWidth={computePassageWrapWidth(
                                    stemText,
                                    currentQuestion.sub_questions || []
                                  )}
                                />
                              )}
                              {isLong && (
                                <Button
                                  size="small"
                                  onClick={() => setPassageExpanded((e) => !e)}
                                  sx={{ mt: 1, textTransform: "none", fontSize: "0.75rem" }}
                                >
                                  {passageExpanded ? "Collapse ▲" : "Show full passage ▼"}
                                </Button>
                              )}
                            </>
                          );
                        })()}
                        {/* Show parent question's images with remove button (decorative header images can be dismissed) */}
                        {(() => {
                          const allImgUrls = Array.isArray(currentQuestion.image_urls)
                            ? currentQuestion.image_urls.filter(Boolean)
                            : [];
                          if (currentQuestion.image_url && !allImgUrls.includes(currentQuestion.image_url)) {
                            allImgUrls.unshift(currentQuestion.image_url);
                          }
                          if (!allImgUrls.length) return null;
                          return (
                            <Box sx={{ mt: 1.5 }}>
                              {allImgUrls.map((url, i) => (
                                <Box
                                  key={i}
                                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mb: 1 }}
                                >
                                  <img
                                    src={url}
                                    alt={`Passage diagram ${i + 1}`}
                                    style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 6, border: '1px solid #e0e0e0', objectFit: 'contain' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<Delete />}
                                    onClick={() => quickRemovePassageImage(url, currentIndex)}
                                    sx={{ textTransform: 'none', fontSize: '0.72rem' }}
                                  >
                                    Remove Image
                                  </Button>
                                </Box>
                              ))}
                            </Box>
                          );
                        })()}
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => startEditStem(currentIndex)}
                        sx={{ textTransform: "none", mb: 2 }}
                      >
                        Edit Stem
                      </Button>

                      {/* Sub-questions (paper-style) */}
                      {(currentQuestion.sub_questions || []).map((sq, sqIdx) => (
                        <Paper
                          key={sqIdx}
                          variant="outlined"
                          onMouseEnter={() => setActiveSubQIdx(sqIdx)}
                          onMouseLeave={() => setActiveSubQIdx(null)}
                          sx={{ p: 2.5, mb: 2, borderRadius: 2, borderColor: activeSubQIdx === sqIdx ? "primary.main" : "primary.200", transition: "border-color 0.15s" }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.75, mb: 1 }}>
                            <Chip label={getTypeLabel(sq.type)} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                            {(() => {
                              const parentAd = currentQuestion.answer_data || {};
                              const subCount = (currentQuestion.sub_questions || []).length;
                              if (parentAd.marks_mode === "fixed") {
                                const total = parentAd.marks_total ?? currentQuestion.default_marks ?? 1;
                                return <Chip label={`${total}/${subCount}`} size="small" color="primary" variant="outlined" title={`${total} marks total ÷ ${subCount} parts`} />;
                              }
                              // Auto-detect: parent marks < sub-marks sum → fractional
                              const subMarks = (currentQuestion.sub_questions || []).map((s) => parseInt(s.default_marks, 10) || 0);
                              const subSum = subMarks.reduce((a, b) => a + b, 0);
                              const parentMarks = parseInt(currentQuestion.default_marks, 10) || 0;
                              const allEqual = subMarks.length > 1 && subMarks.every((m) => m === subMarks[0]);
                              if (parentMarks > 0 && subSum > parentMarks && allEqual) {
                                return <Chip label={`${parentMarks}/${subCount}`} size="small" color="warning" variant="outlined" title={`Auto-detected: ${parentMarks} marks total across ${subCount} parts`} />;
                              }
                              return <Chip label={`${sq.default_marks ?? 1}m`} size="small" color="primary" variant="outlined" />;
                            })()}
                            {sq.topic && (
                              <Chip
                                label={sq.topic}
                                size="small"
                                variant="outlined"
                                color="secondary"
                                sx={{ mr: "auto" }}
                              />
                            )}
                            {!sq.topic && <Box sx={{ mr: "auto" }} />}
                            <Tooltip title="Reset this sub-question back to what the AI extracted (discards all manual edits)">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => resetQuestion({ type: "subquestion", parentIndex: currentIndex, subIndex: sqIdx })}
                                  disabled={!modifiedQuestions.has(currentIndex)}
                                  sx={{ textTransform: "none" }}
                                >
                                  Reset
                                </Button>
                              </span>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="outlined"
                              color="info"
                              startIcon={generatingDiagram ? <CircularProgress size={13} /> : <AutoAwesome />}
                              disabled={generatingDiagram}
                              onClick={() => handleGenerateDiagram({ type: "subquestion", parentIndex: currentIndex, subIndex: sqIdx })}
                              sx={{ textTransform: "none" }}
                              title={sq.image_url ? "Regenerate diagram using AI (replaces current image)" : "Generate diagram using AI"}
                            >
                              {sq.image_url ? "Regenerate Diagram" : "Generate Diagram"}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={improving ? <CircularProgress size={13} /> : <AutoFixHigh />}
                              disabled={improving}
                              onClick={() => handleAiImprove({ type: "subquestion", parentIndex: currentIndex, subIndex: sqIdx })}
                              sx={{ textTransform: "none" }}
                            >
                              AI Improve
                            </Button>
                            {sq.image_url && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<Delete />}
                                onClick={() => quickRemoveSubQuestionImage(sq.image_url, currentIndex, sqIdx)}
                                sx={{ textTransform: "none" }}
                                title="Remove attached image (may be a decorative element from the PDF)"
                              >
                                Remove Image
                              </Button>
                            )}
                            <Tooltip title="Generate AI answer for this sub-question">
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={generatingAnswers ? <CircularProgress size={13} /> : <AutoFixHigh />}
                                disabled={generatingAnswers}
                                onClick={() => handleGenerateAnswers([currentIndex])}
                                sx={{ textTransform: "none" }}
                              >
                                AI Answer
                              </Button>
                            </Tooltip>
                            <Tooltip title="Generate AI explanation for this sub-question">
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={generatingExplanation ? <CircularProgress size={13} /> : <AutoAwesome />}
                                disabled={generatingExplanation}
                                onClick={() => handleGenerateExplanationForPath({ type: "subquestion", parentIndex: currentIndex, subIndex: sqIdx })}
                                sx={{ textTransform: "none" }}
                              >
                                AI Explanation
                              </Button>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => startEditSubQuestion(currentIndex, sqIdx)}
                              sx={{ textTransform: "none" }}
                            >
                              Edit
                            </Button>
                            <Tooltip title="Remove this sub-question">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteSubQuestion(currentIndex, sqIdx)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {!sq.question_text?.trim() && !sq.image_url && (
                            <Alert severity="error" sx={{ mb: 1, borderRadius: 1 }}>
                              <strong>Question text is missing.</strong> Click <strong>Edit</strong> to add the question text.
                            </Alert>
                          )}
                          {/* For comprehension sub-questions that embed a short passage excerpt
                              in the PDF (typically ≤6 lines — close-reading analysis questions),
                              extract and display those lines as a block quote. Long "Re-read"
                              ranges are navigation instructions only and don't embed inline text. */}
                          {(() => {
                            if (!isComprehensionPassageForReview(currentQuestion, examSubject)) return null;
                            const range = parseReReadLineRange(sq.question_text || "");
                            if (!range) return null;
                            // Only embed the excerpt for short citation ranges (≤6 lines).
                            // Longer ranges (e.g. "lines 1-9", "lines 29-41") are navigation
                            // references — the exam paper does not quote them inline.
                            if (range.to - range.from > 5) return null;
                            const passageForExcerpt = convertMarkdownLineNumbersToMarkers(
                              currentQuestion.question_text || ""
                            );
                            const wrapWidth = computePassageWrapWidth(
                              passageForExcerpt,
                              currentQuestion.sub_questions || []
                            );
                            const excerptLines = extractPassageLines(
                              passageForExcerpt,
                              range.from,
                              range.to,
                              wrapWidth
                            );
                            if (!excerptLines || excerptLines.length === 0) return null;
                            return (
                              <Box
                                sx={{
                                  my: 1,
                                  p: 1.5,
                                  bgcolor: "#f8f9fa",
                                  borderLeft: "3px solid #90a4ae",
                                  borderRadius: "0 4px 4px 0",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    color: "text.secondary",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.4,
                                    display: "block",
                                    mb: 0.75,
                                  }}
                                >
                                  Lines {range.from}–{range.to} from passage
                                </Typography>
                                {excerptLines.map((line, li) => (
                                  <Typography
                                    key={li}
                                    variant="body2"
                                    sx={{
                                      fontFamily: "Georgia, serif",
                                      lineHeight: 1.9,
                                      color: "#2a2a2a",
                                    }}
                                  >
                                    {line}
                                  </Typography>
                                ))}
                              </Box>
                            );
                          })()}
                          <QuestionPaperLayout
                            question={sq}
                            questionNumber={currentIndex + 1}
                            showAnswers={
                              isErrorFindingStandalone(sq) ||
                              isErrorFindingContext(sq) ||
                              isClozeWordChoice(sq) ||
                              ["multiple-choice", "multiple-select", "true-false"].includes(sq?.type)
                            }
                            subLabel={(() => {
                              // Prefer the printed paper number (e.g. "a", "b", "i", "1", "2").
                              // When sub-questions have no distinct paper_number (part-labels
                              // stripped by the AI), fall back to lettered labels a), b), c)…
                              // — never to "1", "2" which collide with real numeric sub-Qs.
                              const pn = sq?.paper_number;
                              if (pn !== null && pn !== undefined && String(pn).trim() !== "") {
                                return String(pn);
                              }
                              return `${String.fromCharCode(97 + sqIdx)})`;
                            })()}
                            showAdminHints={true}
                          />
                          {!(isErrorFindingStandalone(sq) || isErrorFindingContext(sq) || isClozeWordChoice(sq)) && (
                            questionHasAnswer(sq) ? (
                              <Box
                                sx={{
                                  mt: 2,
                                  p: 1.5,
                                  bgcolor: '#f0fdf4',
                                  border: '1px solid',
                                  borderColor: 'success.light',
                                  borderRadius: 2,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      letterSpacing: 0.5,
                                      color: 'success.dark',
                                    }}
                                  >
                                    Answer
                                  </Typography>
                                  <AnswerSourceBadge answerData={sq.answer_data} />
                                </Box>
                                <AnswerSourceSelector
                                  question={sq}
                                  onSwitch={(src) => handleSwitchAnswerSource(currentIndex, sqIdx, src)}
                                  onEditManual={() => startEditSubQuestion(currentIndex, sqIdx)}
                                />
                                {!(["multiple-choice", "multiple-select", "true-false"].includes(sq?.type) && (sq?.options?.length ?? 0) > 0) && (
                                  <QuestionAnswerDisplay question={sq} />
                                )}
                              </Box>
                            ) : (
                              <Box sx={{ mt: 1.5 }}>
                                <Chip
                                  label="No answer"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              </Box>
                            )
                          )}
                        </Paper>
                      ))}
                      <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<Add />}
                          onClick={() => addSubQuestionAt(currentIndex)}
                          sx={{ textTransform: "none" }}
                        >
                          Add sub-question
                        </Button>
                      </Box>
                    </Box>
                  ) : isOrphanedPassage(currentQuestion, examSubject) ? (
                    /* =========== ORPHANED COMPREHENSION PASSAGE VIEW =========== */
                    <Box>
                      <Box sx={{ mb: 1, p: 1.5, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "grey.300" }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}
                        >
                          Comprehension Passage (Orphaned — needs Rescan to group with Q&amp;A)
                        </Typography>
                        {(() => {
                          const text = normalizeExtractedDisplayText(currentQuestion.question_text || "");
                          const isLong = text.length > 500;
                          const displayText = isLong && !passageExpanded ? text.slice(0, 500) + "…" : text;
                          return (
                            <>
                              <PassageWithLineNumbers text={displayText} showAllFallbackLineNumbers />
                              {isLong && (
                                <Button
                                  size="small"
                                  onClick={() => setPassageExpanded((e) => !e)}
                                  sx={{ mt: 1, textTransform: "none", fontSize: "0.75rem" }}
                                >
                                  {passageExpanded ? "Collapse ▲" : "Show full passage ▼"}
                                </Button>
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    </Box>
                  ) : (
                    /* =========== STANDALONE QUESTION VIEW (paper-style) =========== */
                    <Box>
                      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.75, mb: 1 }}>
                        <Chip
                          label={getTypeLabel(currentQuestion.type)}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5 }}
                        />
                        {currentQuestion.topic && (
                          <Chip
                            label={currentQuestion.topic}
                            size="small"
                            variant="outlined"
                            color="secondary"
                            sx={{ mr: "auto" }}
                          />
                        )}
                        {!currentQuestion.topic && <Box sx={{ mr: "auto" }} />}
                        <Tooltip title="Reset this question back to what the AI extracted (discards all manual edits)">
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              color="warning"
                              onClick={() => resetQuestion({ type: "standalone", index: currentIndex })}
                              disabled={!modifiedQuestions.has(currentIndex)}
                              sx={{ textTransform: "none" }}
                            >
                              Reset
                            </Button>
                          </span>
                        </Tooltip>
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          startIcon={generatingDiagram ? <CircularProgress size={13} /> : <AutoAwesome />}
                          disabled={generatingDiagram}
                          onClick={() => handleGenerateDiagram({ type: "standalone", index: currentIndex })}
                          sx={{ textTransform: "none" }}
                          title={currentQuestion.image_url ? "Regenerate diagram using AI (replaces current image)" : "Generate diagram using AI"}
                        >
                          {currentQuestion.image_url ? "Regenerate Diagram" : "Generate Diagram"}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={improving ? <CircularProgress size={13} /> : <AutoFixHigh />}
                          disabled={improving}
                          onClick={() => handleAiImprove({ type: "standalone", index: currentIndex })}
                          sx={{ textTransform: "none" }}
                        >
                          AI Improve
                        </Button>
                        <Tooltip title="Generate AI answer for this question">
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={generatingAnswers ? <CircularProgress size={13} /> : <AutoFixHigh />}
                            disabled={generatingAnswers}
                            onClick={() => handleGenerateAnswers([currentIndex])}
                            sx={{ textTransform: "none" }}
                          >
                            AI Answer
                          </Button>
                        </Tooltip>
                        <Tooltip title="Generate AI explanation for this question">
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={generatingExplanation ? <CircularProgress size={13} /> : <AutoAwesome />}
                            disabled={generatingExplanation}
                            onClick={() => handleGenerateExplanationForPath({ type: "standalone", index: currentIndex })}
                            sx={{ textTransform: "none" }}
                          >
                            AI Explanation
                          </Button>
                        </Tooltip>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={startEdit}
                          sx={{ textTransform: "none", minWidth: 60 }}
                        >
                          Edit
                        </Button>
                      </Box>
                      <QuestionPaperLayout
                        question={currentQuestion}
                        questionNumber={currentIndex + 1}
                        showAnswers={
                          isErrorFindingStandalone(currentQuestion) ||
                          isErrorFindingContext(currentQuestion) ||
                          isClozeWordChoice(currentQuestion) ||
                          ["multiple-choice", "multiple-select", "true-false"].includes(currentQuestion?.type)
                        }
                        showAdminHints={true}
                      />
                      {!(isErrorFindingStandalone(currentQuestion) || isErrorFindingContext(currentQuestion) || isClozeWordChoice(currentQuestion)) && (
                        questionHasAnswer(currentQuestion) ? (
                          <Box
                            sx={{
                              mt: 2,
                              p: 1.5,
                              bgcolor: '#f0fdf4',
                              border: '1px solid',
                              borderColor: 'success.light',
                              borderRadius: 2,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  color: 'success.dark',
                                }}
                              >
                                Answer
                              </Typography>
                              <AnswerSourceBadge answerData={currentQuestion.answer_data} />
                            </Box>
                            <AnswerSourceSelector
                              question={currentQuestion}
                              onSwitch={(src) => handleSwitchAnswerSource(currentIndex, null, src)}
                              onEditManual={startEdit}
                            />
                            {/* For MCQ with options the correct answer is already highlighted
                                inline above — only show QuestionAnswerDisplay for other types */}
                            {!(["multiple-choice", "multiple-select", "true-false"].includes(currentQuestion?.type) && (currentQuestion?.options?.length ?? 0) > 0) && (
                              <QuestionAnswerDisplay question={currentQuestion} />
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ mt: 1.5 }}>
                            <Chip
                              label="No answer"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          </Box>
                        )
                      )}
                    </Box>
                  )}

                  {/* Explanation display */}
                  {currentQuestion?.explanation && (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "info.50", borderRadius: 1, border: "1px solid", borderColor: "info.200" }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "info.dark", display: "block", mb: 0.5 }}>
                        Explanation
                      </Typography>
                      <Box sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                        <HtmlWithLatex html={currentQuestion.explanation} />
                      </Box>
                    </Box>
                  )}
                </Paper>

                {/* Plagiarism: this question already exists in the question bank */}
                {currentSimilarityMatches.map((m) => (
                  <Alert
                    key={`${m.index}-${m.sub_index ?? ""}`}
                    severity="warning"
                    sx={{ mb: 2, borderRadius: 2 }}
                    action={
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Button
                          color="inherit"
                          size="small"
                          sx={{ textTransform: "none" }}
                          onClick={() => window.open(`/admin/question-bank/${m.parent_question_id || m.question_id}/edit`, "_blank")}
                        >
                          Open #{m.question_id}
                        </Button>
                        <Button color="inherit" size="small" sx={{ textTransform: "none" }} onClick={() => setSimilarityDialogOpen(true)}>
                          Report
                        </Button>
                      </Box>
                    }
                  >
                    <AlertTitle sx={{ fontWeight: 600, mb: 0.25 }}>
                      Possible duplicate{m.sub_index != null ? ` (part ${m.sub_index + 1})` : ""}: {Number(m.percent).toFixed(0)}% match with question #{m.question_id}
                      {m.source_type ? ` (${m.source_type})` : ""}
                    </AlertTitle>
                    {m.snippet && (
                      <Typography variant="caption" color="text.secondary">{m.snippet}</Typography>
                    )}
                  </Alert>
                ))}

                {/* Navigation */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    sx={{ textTransform: "none" }}
                  >
                    Previous
                  </Button>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                      Question {currentIndex + 1} of {questions.length}
                    </Typography>
                    {currentQuestion?.paper_number && (
                      <Typography variant="caption" color="text.disabled">
                        Paper Q{/^Q/i.test(String(currentQuestion.paper_number)) ? currentQuestion.paper_number.replace(/^Q/i, "") : currentQuestion.paper_number}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    endIcon={<ArrowForward />}
                    onClick={handleNext}
                    disabled={currentIndex >= questions.length - 1}
                    sx={{ textTransform: "none" }}
                  >
                    Next
                  </Button>
                </Box>

                {/* Footer actions */}
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                      Approved questions can be pushed into your existing flows.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexShrink: 0 }}>
                      {!isPastPaperCategory && (
                        <Button
                          variant="outlined"
                          startIcon={<QuizIcon />}
                          onClick={openCreateExamDialog}
                          disabled={approvedCount === 0 || markdownParityBlocking}
                          sx={{ textTransform: "none" }}
                        >
                          Create Exam ({approvedCount})
                        </Button>
                      )}
                      {isPastPaperCategory && (
                        <Button
                          variant="outlined"
                          startIcon={<LibraryBooksIcon />}
                          onClick={openCreatePastPaperDialog}
                          disabled={approvedCount === 0 || submitting}
                          sx={{ textTransform: "none" }}
                        >
                          Create Paper ({approvedCount})
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<Article />}
                        onClick={() => setGenerateOpen(true)}
                        sx={{ textTransform: "none" }}
                      >
                        Generate Similar Paper
                      </Button>
                      <Button
                        variant="contained"
                        disabled={submitting || approvedCount === 0 || markdownParityBlocking}
                        onClick={handleConfirmImport}
                        startIcon={
                          submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircle />
                        }
                        sx={{
                          textTransform: "none",
                          background: "linear-gradient(to right, #3f2b96, #a71d31)",
                          "&:hover": { background: "linear-gradient(to right, #3f2b96, #8b1a28)" },
                        }}
                      >
                        {submitting ? "Importing..." : `Import to Question Bank`}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            )}
          </Box>

          {/* Right Column — Question List Sidebar */}
          <Box>
            <Paper
              sx={{
                borderRadius: 2,
                maxHeight: "80vh",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ p: 2, pb: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Question list
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Button size="small" onClick={selectAllQuestions} sx={{ textTransform: "none", minWidth: 40, px: 0.5 }}>
                      All
                    </Button>
                    <Button size="small" onClick={deselectAllQuestions} sx={{ textTransform: "none", minWidth: 40, px: 0.5 }}>
                      None
                    </Button>
                    <Tooltip title="Remove section title and instructions from every question">
                      <Button size="small" color="warning" onClick={clearAllSections} sx={{ textTransform: "none", px: 0.5, fontSize: "0.7rem" }}>
                        Clear sections
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Drag to reorder. Click Q-chip to edit paper number. Click section header to edit.
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  px: 2,
                  pb: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {questions.map((q, idx) => (
                  <Fragment key={idx}>
                  {(idx === 0 || sectionBlockKey(q) !== sectionBlockKey(questions[idx - 1])) &&
                    sectionBlockKey(q) !== "\u0000" && (
                    listEditSection?.key === sectionBlockKey(q) ? (
                      <Box sx={{ px: 1.5, py: 1, mt: idx > 0 ? 1 : 0, bgcolor: '#f3e5f5', borderRadius: 1, borderLeft: '3px solid #9c27b0', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <TextField
                          size="small"
                          autoFocus
                          label="Section title"
                          value={listEditSection.title}
                          onChange={(e) => setListEditSection((s) => ({ ...s, title: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitListSectionEdit(listEditSection.key, listEditSection.title, listEditSection.instructions);
                            if (e.key === 'Escape') setListEditSection(null);
                          }}
                          sx={{ bgcolor: 'white' }}
                          inputProps={{ style: { fontSize: 12 } }}
                        />
                        <TextField
                          size="small"
                          label="Instructions"
                          value={listEditSection.instructions}
                          onChange={(e) => setListEditSection((s) => ({ ...s, instructions: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setListEditSection(null);
                          }}
                          multiline
                          rows={2}
                          sx={{ bgcolor: 'white' }}
                          inputProps={{ style: { fontSize: 11 } }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Button size="small" sx={{ textTransform: 'none', fontSize: 11, py: 0.25 }} onClick={() => setListEditSection(null)}>Cancel</Button>
                          <Button size="small" variant="contained" sx={{ textTransform: 'none', fontSize: 11, py: 0.25 }} onClick={() => commitListSectionEdit(listEditSection.key, listEditSection.title, listEditSection.instructions)}>Save</Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        onClick={() => setListEditSection({ key: sectionBlockKey(q), title: q.section_title || '', instructions: q.section_instructions || '' })}
                        sx={{ px: 1.5, py: 0.5, mt: idx > 0 ? 1 : 0, bgcolor: '#f3e5f5', borderRadius: 1, borderLeft: '3px solid #9c27b0', cursor: 'pointer', '&:hover': { bgcolor: '#ede7f6' } }}
                        title="Click to edit section"
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#7b1fa2', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {q.section_title || "Section"} <Edit sx={{ fontSize: 10, ml: 0.25, verticalAlign: 'middle', opacity: 0.5 }} />
                        </Typography>
                        {q.section_instructions && (
                          <Box sx={{ mt: 0.5, px: 1, py: 0.5, bgcolor: '#fef9c3', borderLeft: '3px solid #ca8a04', borderRadius: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{ color: '#713f12', fontStyle: 'italic', lineHeight: 1.4, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={q.section_instructions}
                            >
                              {q.section_instructions}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )
                  )}
                  <Box
                    draggable
                    onDragStart={(e) => handleListDragStart(e, idx)}
                    onDragOver={(e) => handleListDragOver(e, idx)}
                    onDrop={(e) => handleListDrop(e, idx)}
                    onDragEnd={handleListDragEnd}
                    sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedQuestions.has(idx)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleQuestionSelection(idx);
                      }}
                      sx={{ p: 0.5, mt: 0.5 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.8, cursor: 'grab', color: 'text.disabled', '&:active': { cursor: 'grabbing' } }}>
                      <DragIndicator sx={{ fontSize: 16 }} />
                    </Box>
                    <Box
                      onClick={() => {
                        setEditMode(false);
                        setEditTarget(null);
                        setCurrentIndex(idx);
                      }}
                      sx={{
                        flex: 1,
                        p: 1.5,
                        border: "1px solid",
                        borderColor: dragOverIdx === idx ? "secondary.main" : idx === currentIndex ? "primary.main" : "#e0e0e0",
                        borderRadius: 2,
                        cursor: "pointer",
                        transition: "all 0.15s",
                        bgcolor: dragOverIdx === idx ? "secondary.50" : idx === currentIndex ? "primary.50" : "transparent",
                        "&:hover": {
                          bgcolor: dragOverIdx === idx ? "secondary.50" : idx === currentIndex ? "primary.50" : "#f5f5f5",
                        },
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Question {idx + 1}
                          </Typography>
                          {listEditPaperNum === idx ? (
                            <TextField
                              size="small"
                              autoFocus
                              defaultValue={q.paper_number ?? ""}
                              onBlur={(e) => commitListPaperNum(idx, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitListPaperNum(idx, e.target.value);
                                if (e.key === 'Escape') setListEditPaperNum(null);
                                e.stopPropagation();
                              }}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ width: 60 }}
                              inputProps={{ style: { fontSize: 11, padding: '2px 6px', fontFamily: 'monospace' } }}
                            />
                          ) : (
                            <Chip
                              label={q.paper_number != null ? (/^Q/i.test(String(q.paper_number)) ? q.paper_number : `Q${q.paper_number}`) : "Q?"}
                              size="small"
                              onClick={(e) => { e.stopPropagation(); setListEditPaperNum(idx); }}
                              title="Click to edit paper number"
                              sx={{ height: 18, fontSize: 10, bgcolor: q.paper_number != null ? '#e3f2fd' : '#fff3e0', color: q.paper_number != null ? '#1565c0' : '#e65100', fontWeight: 700, fontFamily: 'monospace', cursor: 'pointer' }}
                            />
                          )}
                          {questionRowHasDuplicateKey(q, duplicatePaperNumbers) && (
                            <Tooltip title="This row shares a paper number with another question or sub-question — edit Paper # in the toolbar when editing that item">
                              <Chip label="Dup #" size="small" color="warning" variant="filled" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                            </Tooltip>
                          )}
                          {q.type === "grouped" && (
                            <Chip
                              label="Grouped"
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ height: 18, fontSize: 10 }}
                            />
                          )}
                          {isOrphanedPassage(q, examSubject) && (
                            <Tooltip title="Passage extracted without Q&A grouping — Rescan to fix">
                              <Chip
                                label="Passage ⚠"
                                size="small"
                                color="warning"
                                variant="filled"
                                sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                              />
                            </Tooltip>
                          )}
                          {modifiedQuestions.has(idx) && (
                            <Chip
                              label="Modified"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ height: 18, fontSize: 10 }}
                            />
                          )}
                          {q._quality_score !== undefined && (
                            <QualityIndicator
                              score={q._quality_score}
                              issues={qualityReport?.issues?.find(i => i.question_index === idx)?.issues || []}
                              size="small"
                            />
                          )}
                          {/* Change I: warn when question has image_description but no image_url */}
                          {q.image_description && !q.image_url && (
                            <Tooltip title="Has image description but no image assigned — use Extracted Images panel">
                              <WarningAmberIcon fontSize="inherit" sx={{ color: "warning.main", ml: 0.5, verticalAlign: "middle" }} />
                            </Tooltip>
                          )}
                          {/* Structural verification issue/healed badge — sidebar */}
                          {(() => {
                            const pn = q.paper_number != null ? String(q.paper_number).replace(/^Q\.?\s*/i, '') : null;
                            const sv = validationReport?.structural_verification;
                            if (!pn || !sv) return null;
                            const qIssues = (sv.issues || []).filter(i => String(i.question_number) === pn);
                            const allAgentVerified = qIssues.length > 0 && qIssues.every(i => i.status === 'verified_by_agent' || i.status === 'dismissed');
                            const isAgentVerified = q._verified_by === 'mcp_agent' || allAgentVerified;
                            if (qIssues.length === 0 && !q._healed && !isAgentVerified) return null;
                            const unresolvedIssues = qIssues.filter(i => !i.healed && i.status !== 'verified_by_agent' && i.status !== 'dismissed');
                            const isHealed = !isAgentVerified && (q._healed || (qIssues.length > 0 && unresolvedIssues.length === 0));
                            const tooltip = isAgentVerified
                              ? `Verified & fixed by agent${q._verification_notes ? `: ${q._verification_notes}` : (qIssues[0]?.verification_notes ? `: ${qIssues[0].verification_notes}` : '')}`
                              : isHealed
                                ? `Healed: ${(q._healed || qIssues[0]?.type || '').replace(/_/g, ' ')}`
                                : (unresolvedIssues[0]?.message || unresolvedIssues[0]?.type?.replace(/_/g, ' ') || '');
                            return (
                              <Tooltip title={tooltip}>
                                <Chip
                                  label={isAgentVerified ? "Fixed by agent ✓" : isHealed ? "Healed" : "Issue"}
                                  size="small"
                                  sx={{
                                    height: 18, fontSize: 10, fontWeight: 700, ml: 0.5, cursor: 'default',
                                    bgcolor: isAgentVerified ? 'success.light' : isHealed ? 'info.light' : 'warning.light',
                                    color: isAgentVerified ? 'success.dark' : isHealed ? 'info.dark' : 'warning.dark',
                                  }}
                                />
                              </Tooltip>
                            );
                          })()}
                          {/* Vision verification mismatch badge */}
                          {(() => {
                            const pn = q.paper_number != null ? String(q.paper_number) : null;
                            const visionCheck = validationReport?.vision_check;
                            if (!pn || !Array.isArray(visionCheck)) return null;
                            const entry = visionCheck.find(v => String(v.paper_number) === pn && v.status !== 'pass' && v.status !== 'unverified');
                            if (!entry) return null;
                            return (
                              <Tooltip title={`Vision check: ${entry.status}${entry.detail ? ' — ' + entry.detail : ''}`}>
                                <Chip
                                  label="Vision ⚠"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    bgcolor: 'warning.light',
                                    color: 'warning.dark',
                                    fontWeight: 700,
                                    ml: 0.5,
                                    cursor: 'default',
                                  }}
                                />
                              </Tooltip>
                            );
                          })()}
                        </Box>
                        <Box
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            fontSize: "0.75rem",
                            color: "text.secondary",
                            lineHeight: 1.4,
                            wordBreak: "break-word",
                            "& mjx-container": { verticalAlign: "middle" },
                          }}
                        >
                          <HtmlWithLatex html={stripTagsKeepLatex(q.question_text)} />
                        </Box>
                        {q.topic && (
                          <Typography variant="caption" color="secondary.main" sx={{ display: "block", fontStyle: "italic" }}>
                            {q.topic}
                          </Typography>
                        )}
                        {q.type === "grouped" && q.sub_questions?.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {q.sub_questions.length} sub-question{q.sub_questions.length !== 1 ? "s" : ""}
                          </Typography>
                        )}
                      </Box>
                      {approvedSet.has(idx) ? (
                        <CheckCircle sx={{ color: "success.main", fontSize: 18, ml: 1, flexShrink: 0 }} />
                      ) : (
                        <Cancel sx={{ color: "#bdbdbd", fontSize: 18, ml: 1, flexShrink: 0 }} />
                      )}
                    </Box>
                  </Box>
                  </Fragment>
                ))}
              </Box>
            </Paper>
          </Box>
        </Box>
        </Box>
      )}

      {/* ===== AI IMPROVE DIALOG ===== */}
      <Dialog open={aiImproveOpen} onClose={() => setAiImproveOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>AI Suggestion — Compare &amp; Accept</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            {/* Original */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                Original
              </Typography>
              {aiOriginal && (
                <QuestionPaperLayout
                  question={aiOriginal}
                  questionNumber={1}
                  showAnswers={true}
                  showAdminHints={false}
                />
              )}
            </Paper>
            {/* AI Suggestion */}
            <Paper
              variant="outlined"
              sx={{ p: 2, border: "2px solid", borderColor: "secondary.main" }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, color: "secondary.main" }}>
                AI Suggestion
              </Typography>
              {aiSuggestion && (
                <QuestionPaperLayout
                  question={aiSuggestion}
                  questionNumber={1}
                  showAnswers={true}
                  showAdminHints={false}
                />
              )}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiImproveOpen(false)}>Dismiss</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={applyAiSuggestion}
            disabled={!aiSuggestion}
          >
            Accept AI Suggestion
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== AI GENERATE DIAGRAM DIALOG ===== */}
      <Dialog open={diagramDialogOpen} onClose={() => setDiagramDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>AI Generated Diagram</DialogTitle>
        <DialogContent dividers>
          {generatedDiagram?.svg ? (
            <Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  width: "100%",
                  minHeight: 280,
                  overflow: "auto",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{ width: "100%", maxWidth: 520 }}
                  dangerouslySetInnerHTML={{
                    __html: generatedDiagram.svg.replace(
                      /<svg /,
                      '<svg style="width:100%;height:auto;display:block;" '
                    ),
                  }}
                />
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block", textAlign: "center" }}>
                Diagram shows blank spaces for students to fill in. Answers are shown separately below the question.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Generating diagram...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiagramDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="info"
            onClick={applyGeneratedDiagram}
            disabled={!generatedDiagram?.svg || imageUploading}
            startIcon={imageUploading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
          >
            {imageUploading ? "Uploading..." : "Add Diagram to Question"}
          </Button>
        </DialogActions>
      </Dialog>

      <SimilarityReportDialog
        open={similarityDialogOpen}
        importId={importId}
        filename={importData?.source_filename}
        onClose={() => setSimilarityDialogOpen(false)}
      />
      <GenerateSimilarPaperDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        importId={importId}
        title={importData?.exam_title || importData?.source_filename}
        questionCount={questions.length}
        defaultCategoryId={importData?.target_category_id || ""}
      />

      {/* ===== EDIT EXAM METADATA DIALOG ===== */}
      <Dialog
        open={editExamMetaOpen}
        onClose={() => setEditExamMetaOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Exam Header Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* School logo */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                School Logo
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                {editExamMetaValues.school_logo ? (
                  <Box sx={{ position: "relative" }}>
                    <img
                      src={editExamMetaValues.school_logo}
                      alt="School logo"
                      style={{ height: 64, maxWidth: 200, objectFit: "contain", border: "1px solid #e0e0e0", borderRadius: 4 }}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setEditExamMetaValues(prev => ({ ...prev, school_logo: null }))}
                      sx={{ position: "absolute", top: -8, right: -8, bgcolor: "white", border: "1px solid #e0e0e0", p: 0.25 }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ width: 64, height: 64, border: "1px dashed #bdbdbd", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography variant="caption" color="text.disabled" sx={{ textAlign: "center", px: 0.5 }}>No logo</Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <input
                    type="file"
                    accept="image/*"
                    ref={logoInputRef}
                    style={{ display: "none" }}
                    onChange={handleLogoUpload}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={logoUploading ? <CircularProgress size={14} /> : <CloudUpload />}
                    disabled={logoUploading}
                    onClick={() => logoInputRef.current?.click()}
                    sx={{ textTransform: "none" }}
                  >
                    {logoUploading ? "Uploading…" : "Upload logo image"}
                  </Button>
                  <TextField
                    size="small"
                    label="Or paste logo URL"
                    value={editExamMetaValues.school_logo ?? ""}
                    onChange={(e) => setEditExamMetaValues(prev => ({ ...prev, school_logo: e.target.value || null }))}
                    placeholder="https://…"
                    sx={{ minWidth: 260 }}
                  />
                </Box>
              </Box>
            </Box>
            <TextField
              label="School Name"
              value={editExamMetaValues.school_name}
              onChange={(e) => setEditExamMetaValues(prev => ({ ...prev, school_name: e.target.value }))}
              fullWidth
              placeholder="e.g., ST PAUL'S GIRLS' SCHOOL"
            />
            <TextField
              label="Exam Title"
              value={editExamMetaValues.exam_title}
              onChange={(e) => setEditExamMetaValues(prev => ({ ...prev, exam_title: e.target.value }))}
              fullWidth
              placeholder="e.g., FIRST YEAR ENTRANCE EXAMINATION"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select
                  label="Subject"
                  value={editExamMetaValues.exam_subject}
                  onChange={(e) => setEditExamMetaValues(prev => ({ ...prev, exam_subject: e.target.value }))}
                >
                  <MenuItem value=""><em>— Not set —</em></MenuItem>
                  {availableSubjects.map((s) => {
                    const keyToLegacy = { maths: "MATHEMATICS", english: "ENGLISH", "verbal-reasoning": "VERBAL REASONING", "non-verbal-reasoning": "NON-VERBAL REASONING" };
                    const legacy = keyToLegacy[s.key] ?? s.name.toUpperCase();
                    return <MenuItem key={s.id} value={legacy}>{s.name}</MenuItem>;
                  })}
                </Select>
              </FormControl>
              <TextField
                label="Duration"
                value={editExamMetaValues.exam_duration}
                onChange={(e) => setEditExamMetaValues(prev => ({ ...prev, exam_duration: e.target.value }))}
                fullWidth
                placeholder="e.g., 25 minutes"
              />
            </Box>
            <TextField
              label="Section"
              value={editExamMetaValues.exam_section}
              onChange={(e) => setEditExamMetaValues(prev => ({ ...prev, exam_section: e.target.value }))}
              fullWidth
              placeholder="e.g., Section A"
            />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Instructions
              </Typography>
              <Box sx={{
                '& .ql-container': { minHeight: 120, fontSize: '0.875rem' },
                '& .ql-toolbar': { borderRadius: '4px 4px 0 0' },
                '& .ql-container.ql-snow': { borderRadius: '0 0 4px 4px' },
              }}>
                <ReactQuill
                  theme="snow"
                  value={editExamMetaValues.exam_instructions || ""}
                  onChange={(val) => setEditExamMetaValues(prev => ({ ...prev, exam_instructions: val }))}
                  modules={INSTRUCTIONS_QUILL_MODULES}
                  formats={INSTRUCTIONS_QUILL_FORMATS}
                  placeholder="Enter exam instructions..."
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                Numbering options
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!editExamMetaValues.auto_number_questions}
                    onChange={(e) => setEditExamMetaValues(prev => ({ ...prev, auto_number_questions: e.target.checked }))}
                  />
                }
                label={<Typography variant="body2">Auto-number questions in paper layout</Typography>}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditExamMetaOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={saveExamMeta}
            disabled={savingExamMeta}
            startIcon={savingExamMeta ? <CircularProgress size={16} /> : <Save />}
          >
            {savingExamMeta ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== REASSIGN IMAGE DIALOG ===== */}
      <Dialog
        open={reassignDialogOpen}
        onClose={() => !reassigningImage && setReassignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Reassign Image</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {/* Image preview */}
            {reassignTargetImage && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  p: 1.5,
                  bgcolor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <img
                  src={reassignTargetImage.url}
                  alt="preview of selected file"
                  style={{ maxWidth: "100%", maxHeight: 200, objectFit: "contain" }}
                />
              </Box>
            )}
            <Typography variant="body2" color="text.secondary">
              Select which question this image belongs to, or leave blank to remove it from all questions.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Assign to question</InputLabel>
              <Select
                value={reassignSelectedQuestion}
                label="Assign to question"
                onChange={(e) => setReassignSelectedQuestion(e.target.value)}
              >
                <MenuItem value="">
                  <em>Unassigned — remove from all questions</em>
                </MenuItem>
                {imagesPanelQuestions.map((q, qIdx) => (
                  <MenuItem key={String(q.question_ref || q.id)} value={String(q.question_ref || q.id)}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, maxWidth: "100%", overflow: "hidden" }}>
                      <Chip
                        label={qLabelMap[String(q.id)] ?? `Q${qIdx + 1}`}
                        size="small"
                        sx={{ fontSize: "0.65rem", height: 18, flexShrink: 0, fontWeight: 700, bgcolor: "#1a1a1a", color: "#fff" }}
                      />
                      <Chip
                        label={q.type}
                        size="small"
                        sx={{ fontSize: "0.65rem", height: 18, flexShrink: 0 }}
                        variant="outlined"
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "0.82rem",
                        }}
                      >
                        {q.label || "(no text)"}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReassignDialogOpen(false)}
            disabled={!!reassigningImage}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReassignConfirm}
            disabled={!!reassigningImage}
            startIcon={reassigningImage ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: "none" }}
          >
            {reassigningImage ? "Saving…" : "Confirm Reassign"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== CREATE EXAM FROM IMPORT DIALOG ===== */}
      <Dialog
        open={createExamOpen}
        onClose={() => !creatingExam && setCreateExamOpen(false)}
        maxWidth="md"
        fullWidth
        keepMounted={false}
        aria-modal="true"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #3f2b96 0%, #a71d31 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <QuizIcon sx={{ color: "white", fontSize: 22 }} />
            </Box>
            Create Exam from Import
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            {approvedCount} approved question{approvedCount !== 1 ? "s" : ""} will be imported and linked to the new exam.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {loadingExamMeta ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
              {/* Exam Name */}
              <TextField
                label="Exam Name"
                value={examForm.name}
                onChange={(e) => {
                  setExamForm((p) => ({ ...p, name: e.target.value }));
                  if (examFormErrors.name) setExamFormErrors((p) => ({ ...p, name: "" }));
                }}
                fullWidth
                required
                error={!!examFormErrors.name}
                helperText={examFormErrors.name}
                placeholder="e.g., St Paul's 11+ Maths 2024"
              />

              {/* Description */}
              <TextField
                label="Description (optional)"
                value={examForm.description}
                onChange={(e) => setExamForm((p) => ({ ...p, description: e.target.value }))}
                fullWidth
                multiline
                rows={2}
                placeholder="Brief description of this exam..."
              />

              {/* Category + Format */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth required error={!!examFormErrors.category_id}>
                  <InputLabel id="create-exam-category-label">Category *</InputLabel>
                  <Select
                    labelId="create-exam-category-label"
                    value={examForm.category_id}
                    label="Category *"
                    onChange={(e) => {
                      setExamForm((p) => ({ ...p, category_id: e.target.value }));
                      if (examFormErrors.category_id)
                        setExamFormErrors((p) => ({ ...p, category_id: "" }));
                    }}
                  >
                    <MenuItem value="">
                      <em>Select category...</em>
                    </MenuItem>
                    {flattenCategories(examCategories).map((cat) => (
                      <MenuItem
                        key={cat.id}
                        value={cat.id}
                        sx={{
                          pl: 2 + cat.level * 2,
                          fontWeight: cat.level === 0 ? 700 : 400,
                          fontSize: cat.level === 0 ? "1rem" : "0.9rem",
                          color: cat.level === 0 ? "#1565c0" : "#444",
                        }}
                      >
                        {cat.level > 0 ? "└─ " : ""}{cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {examFormErrors.category_id && (
                    <FormHelperText>{examFormErrors.category_id}</FormHelperText>
                  )}
                </FormControl>

                <FormControl fullWidth required error={!!examFormErrors.format_id}>
                  <InputLabel>Format</InputLabel>
                  <Select
                    value={examForm.format_id}
                    label="Format"
                    onChange={(e) => {
                      setExamForm((p) => ({ ...p, format_id: e.target.value }));
                      if (examFormErrors.format_id)
                        setExamFormErrors((p) => ({ ...p, format_id: "" }));
                    }}
                  >
                    {examFormats.map((f) => (
                      <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                    ))}
                  </Select>
                  {examFormErrors.format_id && (
                    <FormHelperText>{examFormErrors.format_id}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              {/* Subject + School year */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <FormControl fullWidth required sx={{ minWidth: 240 }} error={!!examFormErrors.subject_ids}>
                  <InputLabel>Subjects *</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(examForm.subject_ids) ? examForm.subject_ids : []}
                    label="Subjects *"
                    onChange={(e) => {
                      const v = e.target.value;
                      setExamForm((p) => ({
                        ...p,
                        subject_ids: typeof v === "string" ? v.split(",").map(Number) : v,
                      }));
                      if (examFormErrors.subject_ids)
                        setExamFormErrors((er) => ({ ...er, subject_ids: "" }));
                    }}
                    input={<OutlinedInput label="Subjects *" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(selected || []).map((id) => {
                          const s = examSubjects.find((x) => Number(x.id) === Number(id));
                          return <Chip key={id} label={s?.name ?? id} size="small" />;
                        })}
                      </Box>
                    )}
                  >
                    {examSubjects.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {examFormErrors.subject_ids && (
                    <FormHelperText>{examFormErrors.subject_ids}</FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth required sx={{ minWidth: 200 }} error={!!examFormErrors.school_year_id}>
                  <InputLabel>School year *</InputLabel>
                  <Select
                    value={examForm.school_year_id}
                    label="School year *"
                    onChange={(e) => {
                      setExamForm((p) => ({ ...p, school_year_id: e.target.value }));
                      if (examFormErrors.school_year_id)
                        setExamFormErrors((er) => ({ ...er, school_year_id: "" }));
                    }}
                  >
                    <MenuItem value="">
                      <em>Select school year…</em>
                    </MenuItem>
                    {examSchoolYears.map((y) => (
                      <MenuItem key={y.id} value={y.id}>
                        {y.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {examFormErrors.school_year_id && (
                    <FormHelperText>{examFormErrors.school_year_id}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              {/* Price + Currency + Duration */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Price"
                  type="number"
                  value={examForm.price}
                  onChange={(e) => setExamForm((p) => ({ ...p, price: e.target.value }))}
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={{ flex: 1 }}
                />
                <FormControl sx={{ minWidth: 100 }}>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={examForm.currency}
                    label="Currency"
                    onChange={(e) => setExamForm((p) => ({ ...p, currency: e.target.value }))}
                  >
                    <MenuItem value="£">£ GBP</MenuItem>
                    <MenuItem value="€">€ EUR</MenuItem>
                    <MenuItem value="$">$ USD</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Duration (minutes)"
                  type="number"
                  value={examForm.duration_minutes}
                  onChange={(e) => setExamForm((p) => ({ ...p, duration_minutes: e.target.value }))}
                  inputProps={{ min: 1 }}
                  sx={{ flex: 1 }}
                />
              </Box>

              {/* Target schools */}
              {examSchools.length > 0 && (
                <Autocomplete
                  multiple
                  options={examSchools}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                  value={examSchools.filter((s) =>
                    (examForm.target_school_ids || []).map(Number).includes(Number(s.id))
                  )}
                  onChange={(e, value) =>
                    setExamForm((p) => ({
                      ...p,
                      target_school_ids: (value || []).map((s) => s.id),
                      school_id: value?.[0]?.id || "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Target schools (optional)" placeholder="Search schools" />
                  )}
                />
              )}

              {/* Advanced Settings (aligned with MockExam create form) */}
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "rgba(0,0,0,0.08)",
                  bgcolor: "rgba(0,0,0,0.02)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <SettingsIcon sx={{ color: "#666" }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#263238" }}>
                    Advanced Settings
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <TextField
                      label="Retake Attempts"
                      type="number"
                      value={examForm.max_attempts}
                      onChange={(e) =>
                        setExamForm((p) => ({ ...p, max_attempts: e.target.value }))
                      }
                      inputProps={{ min: 0 }}
                      sx={{ minWidth: 140 }}
                    />
                    <TextField
                      label="Marks per Correct Answer"
                      type="number"
                      value={examForm.marks_per_correct_answer}
                      onChange={(e) =>
                        setExamForm((p) => ({ ...p, marks_per_correct_answer: e.target.value }))
                      }
                      inputProps={{ min: 0, step: "0.01" }}
                      helperText="Points awarded for each correct answer."
                      FormHelperTextProps={{ sx: { color: "text.secondary" } }}
                      sx={{ minWidth: 180 }}
                    />
                    <TextField
                      label="Negative Marks Penalty"
                      type="number"
                      value={examForm.negative_marks_penalty}
                      onChange={(e) =>
                        setExamForm((p) => ({ ...p, negative_marks_penalty: e.target.value }))
                      }
                      inputProps={{ min: 0, step: "0.01" }}
                      helperText="Penalty for each incorrect answer."
                      FormHelperTextProps={{ sx: { color: "text.secondary" } }}
                      sx={{ minWidth: 180 }}
                    />
                  </Box>
                  <TextField
                    label="Exam Instructions"
                    value={examForm.instructions}
                    onChange={(e) =>
                      setExamForm((p) => ({ ...p, instructions: e.target.value }))
                    }
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Instructions shown to students before they start the exam."
                    FormHelperTextProps={{ sx: { color: "text.secondary" } }}
                    helperText="Shown to students before they start the exam."
                  />
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <TextField
                      label="Original Paper Link (optional)"
                      type="url"
                      value={examForm.original_paper_link}
                      onChange={(e) =>
                        setExamForm((p) => ({ ...p, original_paper_link: e.target.value }))
                      }
                      fullWidth
                      placeholder="https://..."
                      helperText="Store the source paper URL used to create this exam."
                      FormHelperTextProps={{ sx: { color: "text.secondary" } }}
                    />
                    <Tooltip title="Find source link online with Gemini">
                      <IconButton
                        sx={{ mt: 1 }}
                        onClick={(e) => handleFindImportSourceLinks("exam", e.currentTarget)}
                        disabled={importFindingLinks && importLinkTarget === "exam"}
                      >
                        {importFindingLinks && importLinkTarget === "exam"
                          ? <CircularProgress size={20} />
                          : <TravelExploreIcon sx={{ color: "#00bcd4" }} />
                        }
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>

              {/* Approved questions preview */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Questions to include ({approvedCount})
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Grouped items (e.g. 1a, 1b) are one exam question with sub-parts; the same stem is kept when creating the exam.
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ maxHeight: 220, overflow: "auto", borderRadius: 2 }}
                >
                  {questions
                    .filter((_, i) => approvedSet.has(i))
                    .map((q, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          px: 2,
                          py: 1.25,
                          borderBottom: "1px solid #f0f0f0",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          "&:last-child": { borderBottom: "none" },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            bgcolor: "primary.50",
                            color: "primary.main",
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            fontWeight: 700,
                            flexShrink: 0,
                            fontSize: "0.7rem",
                          }}
                        >
                          Q{idx + 1}
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            fontSize: "0.8rem",
                            color: "#444",
                            lineHeight: 1.35,
                            wordBreak: "break-word",
                            "& mjx-container": { verticalAlign: "middle" },
                          }}
                        >
                          <HtmlWithLatex html={stripTagsKeepLatex(q.question_text)} />
                        </Box>
                        {q.type === "grouped" && q.sub_questions?.length > 0 && (
                          <Chip
                            label={`${q.sub_questions.length} sub-q`}
                            size="small"
                            color="info"
                            sx={{ ml: 1, height: 18, fontSize: "0.65rem", flexShrink: 0 }}
                          />
                        )}
                        <Chip
                          label={q.difficulty || "Medium"}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.65rem", flexShrink: 0 }}
                        />
                      </Box>
                    ))}
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setCreateExamOpen(false)}
            disabled={creatingExam}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={creatingExam || loadingExamMeta || approvedCount === 0}
            onClick={handleCreateExamSubmit}
            startIcon={
              creatingExam ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <QuizIcon />
              )
            }
            sx={{
              textTransform: "none",
              background: "linear-gradient(to right, #3f2b96, #a71d31)",
              "&:hover": { background: "linear-gradient(to right, #3f2b96, #8b1a28)" },
              minWidth: 180,
            }}
          >
            {creatingExam
              ? "Importing & Creating…"
              : `Create Exam with ${approvedCount} Question${approvedCount !== 1 ? "s" : ""}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Past Paper — full form popup (paper-style fields) */}
      <Dialog
        open={createPastPaperOpen}
        onClose={() => !creatingPastPaper && setCreatePastPaperOpen(false)}
        maxWidth="md"
        fullWidth
        keepMounted={false}
        aria-modal="true"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #1565c0 0%, #6a1b9a 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LibraryBooksIcon sx={{ color: "white", fontSize: 22 }} />
            </Box>
            Create New Paper
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            Fill all details to create a new paper and link {approvedCount} approved question{approvedCount !== 1 ? "s" : ""}.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {loadingPastPaperMeta ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25, pt: 0.75 }}>
              <TextField
                label="Paper Name *"
                fullWidth
                required
                value={pastPaperForm.name}
                onChange={(e) => {
                  setPastPaperForm((p) => ({ ...p, name: e.target.value }));
                  if (pastPaperFormErrors.name) setPastPaperFormErrors((p) => ({ ...p, name: "" }));
                }}
                error={!!pastPaperFormErrors.name}
                helperText={pastPaperFormErrors.name}
              />
              <TextField
                label="Description (optional)"
                multiline
                rows={2}
                fullWidth
                value={pastPaperForm.description}
                onChange={(e) => setPastPaperForm((p) => ({ ...p, description: e.target.value }))}
              />
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <TextField
                  label="Original Paper Link (optional)"
                  type="url"
                  fullWidth
                  value={pastPaperForm.original_paper_link}
                  onChange={(e) =>
                    setPastPaperForm((p) => ({ ...p, original_paper_link: e.target.value }))
                  }
                  placeholder="https://..."
                />
                <Tooltip title="Find source link online with Gemini">
                  <IconButton
                    sx={{ mt: 1 }}
                    onClick={(e) => handleFindImportSourceLinks("pastpaper", e.currentTarget)}
                    disabled={importFindingLinks && importLinkTarget === "pastpaper"}
                  >
                    {importFindingLinks && importLinkTarget === "pastpaper"
                      ? <CircularProgress size={20} />
                      : <TravelExploreIcon sx={{ color: "#00bcd4" }} />
                    }
                  </IconButton>
                </Tooltip>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>
                  Shelf on the website
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={pastPaperShelf}
                  onChange={(_, shelf) => {
                    if (!shelf) return;
                    const id = findPaperShelfCategoryId(paperCategories, shelf);
                    if (!id) {
                      toast.error(`No "${shelf === "practice" ? "Practice" : "Past"} Papers" category exists yet.`);
                      return;
                    }
                    setPastPaperForm((p) => ({ ...p, category_id: id }));
                    if (pastPaperFormErrors.category_id) setPastPaperFormErrors((p) => ({ ...p, category_id: "" }));
                  }}
                  sx={{ "& .MuiToggleButton-root": { textTransform: "none", alignItems: "flex-start", flexDirection: "column", textAlign: "left", py: 1.25 } }}
                >
                  <ToggleButton value="practice">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Practice Papers</Typography>
                    <Typography variant="caption" color="text.secondary">Our original papers. The PDF and answers are sold together.</Typography>
                  </ToggleButton>
                  <ToggleButton value="past">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Past Papers</Typography>
                    <Typography variant="caption" color="text.secondary">Real school papers. The PDF is free for everyone; the price unlocks the answers.</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth required error={!!pastPaperFormErrors.category_id}>
                  <InputLabel>Category *</InputLabel>
                  <Select
                    value={pastPaperForm.category_id}
                    label="Category *"
                    onChange={(e) => {
                      setPastPaperForm((p) => ({ ...p, category_id: e.target.value }));
                      if (pastPaperFormErrors.category_id) setPastPaperFormErrors((p) => ({ ...p, category_id: "" }));
                    }}
                  >
                    {flattenCategories(paperCategories)
                      .filter((cat) => !cat.level)
                      .map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{pastPaperFormErrors.category_id}</FormHelperText>
                </FormControl>

                <FormControl fullWidth required error={!!pastPaperFormErrors.format_id}>
                  <InputLabel>Format *</InputLabel>
                  <Select
                    value={pastPaperForm.format_id}
                    label="Format *"
                    onChange={(e) => {
                      setPastPaperForm((p) => ({ ...p, format_id: e.target.value }));
                      if (pastPaperFormErrors.format_id) setPastPaperFormErrors((p) => ({ ...p, format_id: "" }));
                    }}
                  >
                    {examFormats.map((f) => (
                      <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{pastPaperFormErrors.format_id}</FormHelperText>
                </FormControl>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth required error={!!pastPaperFormErrors.school_year_id}>
                  <InputLabel>School year *</InputLabel>
                  <Select
                    value={pastPaperForm.school_year_id}
                    label="School year *"
                    onChange={(e) => {
                      setPastPaperForm((p) => ({ ...p, school_year_id: e.target.value }));
                      if (pastPaperFormErrors.school_year_id) setPastPaperFormErrors((p) => ({ ...p, school_year_id: "" }));
                    }}
                  >
                    <MenuItem value="">
                      <em>Select school year...</em>
                    </MenuItem>
                    {examSchoolYears.map((sy) => (
                      <MenuItem key={sy.id} value={sy.id}>{sy.name}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{pastPaperFormErrors.school_year_id}</FormHelperText>
                </FormControl>

                <FormControl fullWidth required error={!!pastPaperFormErrors.subject_ids}>
                  <InputLabel>Subjects *</InputLabel>
                  <Select
                    multiple
                    value={pastPaperForm.subject_ids}
                    onChange={(e) => {
                      setPastPaperForm((p) => ({ ...p, subject_ids: e.target.value }));
                      if (pastPaperFormErrors.subject_ids) setPastPaperFormErrors((p) => ({ ...p, subject_ids: "" }));
                    }}
                    input={<OutlinedInput label="Subjects *" />}
                    renderValue={(selected) =>
                      examSubjects
                        .filter((s) => selected.includes(s.id))
                        .map((s) => s.name)
                        .join(", ")
                    }
                  >
                    {examSubjects.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{pastPaperFormErrors.subject_ids}</FormHelperText>
                </FormControl>
              </Box>

              <Autocomplete
                multiple
                options={examSchools}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                value={examSchools.filter((s) =>
                  (pastPaperForm.target_school_ids || []).map(Number).includes(Number(s.id))
                )}
                onChange={(e, value) =>
                  setPastPaperForm((p) => ({
                    ...p,
                    target_school_ids: (value || []).map((s) => s.id),
                    school_id: value?.[0]?.id || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Target schools (optional)" placeholder="Search schools" />
                )}
              />

              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Price"
                  type="number"
                  fullWidth
                  value={pastPaperForm.price}
                  onChange={(e) => {
                    const v = e.target.value;
                    const num = parseFloat(String(v).replace(",", "."));
                    setPastPaperForm((p) => {
                      const next = { ...p, price: v };
                      if (!Number.isNaN(num)) {
                        if (num <= 0) next.is_free_paper = true;
                        if (num > 0) next.is_free_paper = false;
                      }
                      return next;
                    });
                  }}
                />
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={pastPaperForm.currency}
                    label="Currency"
                    onChange={(e) => setPastPaperForm((p) => ({ ...p, currency: e.target.value }))}
                  >
                    <MenuItem value="£">£ Pound</MenuItem>
                    <MenuItem value="$">$ Dollar</MenuItem>
                    <MenuItem value="€">€ Euro</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Duration (minutes)"
                  type="number"
                  fullWidth
                  value={pastPaperForm.duration_minutes}
                  onChange={(e) => setPastPaperForm((p) => ({ ...p, duration_minutes: e.target.value }))}
                />
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!pastPaperForm.is_free_paper}
                    onChange={(e) => setPastPaperForm((p) => ({ ...p, is_free_paper: e.target.checked }))}
                  />
                }
                label="Free paper (show as FREE on the website)"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCreatePastPaperOpen(false)} disabled={creatingPastPaper}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              creatingPastPaper || loadingPastPaperMeta || approvedCount === 0
            }
            onClick={handleAttachPastPaperSubmit}
            startIcon={
              creatingPastPaper ? <CircularProgress size={16} color="inherit" /> : <LibraryBooksIcon />
            }
            sx={{
              textTransform: "none",
              background: "linear-gradient(to right, #1565c0, #6a1b9a)",
              "&:hover": { background: "linear-gradient(to right, #145a9e, #5c1690)" },
              minWidth: 200,
            }}
          >
            {creatingPastPaper ? "Saving…" : `Create ${pastPaperShelf === "practice" ? "Practice" : "Past"} Paper with ${approvedCount} Questions`}
          </Button>
        </DialogActions>
      </Dialog>

      <ImportMarkdownViewerDialog
        open={markdownDialogOpen}
        importId={importId ? Number(importId) : null}
        filename={importData?.source_filename || ""}
        onClose={() => setMarkdownDialogOpen(false)}
      />

      {/* ── Source-link candidates dropdown (shared for both dialogs) ──── */}
      <Menu
        anchorEl={importLinkAnchorEl}
        open={Boolean(importLinkAnchorEl)}
        onClose={() => setImportLinkAnchorEl(null)}
        PaperProps={{ sx: { maxWidth: 500, maxHeight: 380, borderRadius: 2 } }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {importFindingLinks && (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Searching with Gemini…
          </MenuItem>
        )}
        {!importFindingLinks && importLinkCandidates.length === 0 && (
          <MenuItem disabled>No source links found for this paper.</MenuItem>
        )}
        {!importFindingLinks && importLinkCandidates.map((c, i) => {
          const url   = typeof c === "string" ? c : c.url;
          const title = typeof c === "string" ? c : (c.title || c.url);
          return (
            <MenuItem
              key={i}
              onClick={() => handleSelectImportLink(url)}
              sx={{ flexDirection: "column", alignItems: "flex-start", py: 1 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>
              {title !== url && (
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all", maxWidth: 460 }}>
                  {url}
                </Typography>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default QuestionBankImportReview;
